import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import Select from 'react-select';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import { getInitialsAvatar, getDefaultAvatar } from '../../utils/mediaUtils';
import { getImageURL } from '../../utils/api';

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

function formatScheduledAt(isoStr) {
  if (!isoStr) return null;
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return null;
    const month = d.toLocaleString('default', { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    let h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${month} ${day}, ${year} ${h}:${m} ${ampm}`;
  } catch {
    return null;
  }
}

export default function Schedule({ today } = {}) {

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);

  // Loose string normalizer for fuzzy matching (case-insensitive, alnum + spaces only)
  const normalizeLoose = (str) => {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  // Normalize a course bucket title similar to grouping logic
  const normalizeBucketFromNames = (name, short) => {
    const full = (name || '').trim();
    const HND_LEVEL5 = new Set([
      'Level 5 Pearson BTEC HND in Computing (General)',
      'Level 5 Pearson BTEC HND in Computing (Software Engineering)',
      'Level 5 Pearson BTEC HND in Computing (Network Engineering)'
    ]);
    if (HND_LEVEL5.has(full)) return 'Level 5 Pearson BTEC HND in Computing';
    const s = (short || '').trim();
    return full || s || 'Other Courses';
  };

  // Detect if a name points to HND in Computing (handles synonyms like "Higher National Diploma")
  const isHndComputingName = (name, short) => {
    const norm = (v) =>
      normalizeLoose(String(v || ''))
      .replace(/higher national diploma/g, 'hnd')
      .replace(/b\.?t\.?e\.?c\.?/g, 'btec')
      .replace(/pearson/g, 'pearson');
    const n1 = norm(name);
    const n2 = norm(short);
    const hasTokens = (s) => s.includes('hnd') && s.includes('comput');
    return hasTokens(n1) || hasTokens(n2);
  };

  // Precompute editable bucket titles set from coordinateCourses
  const editableBucketTitles = useMemo(() => {
    const set = new Set();
    if (!coordinateCourses || typeof coordinateCourses !== 'object') return set;
    for (const value of Object.values(coordinateCourses)) {
      const c = value;
      if (!c || !(c.isAdd || c.isUpdate)) continue;
      if (isHndComputingName(c.courseName, c.shortName)) {
        set.add('Level 5 Pearson BTEC HND in Computing');
      } else {
        const bucket = normalizeBucketFromNames(c.courseName, c.shortName);
        if (bucket) set.add(bucket);
      }
    }
    return set;
  }, [coordinateCourses]);

  // Note: canEditCourse is defined later after building helper maps from rows

  // Helper to get list of editable course names for display (unique + sorted)
  const getEditableCourseNames = () => {
    if (!coordinateCourses || typeof coordinateCourses !== 'object') return [];
    const set = new Set();
    for (const courseData of Object.values(coordinateCourses)) {
      const course = courseData || {};
      if (!course) continue;
      if (course.isAdd || course.isUpdate) {
        const name = (course.courseName || course.shortName || '').toString().trim();
        if (name) set.add(name);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  // UI: toggle for showing full editable course list in the banner
  const [showAllEditableCourses, setShowAllEditableCourses] = useState(false);

  // Helpers for 12h <-> 24h conversions
  const to24h = (time12h) => {
    const [time, modifier] = time12h.split(' ');
    const [h, m] = time.split(':');
    let hours = parseInt(h, 10);
    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, '0')}:${m}`;
  };

  const toBadgeColor = (val, fallback = '#e5e7eb') => {
    if (!val) return fallback;
    // hash to color-ish
    let hash = 0;
    for (let i = 0; i < val.length; i++) hash = val.charCodeAt(i) + ((hash << 5) - hash);
    let c = '#';
    for (let i = 0; i < 3; i++) {
      let v = (hash >> (i * 8)) & 0xff;
      v = Math.min(200, Math.max(70, v));
      c += ('00' + v.toString(16)).slice(-2);
    }
    return c;
  };

  // ------------------------------
  // Core page state
  // ------------------------------
  const [selectedDate, setSelectedDate] = useState(today || new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(null);

  const [batches, setBatches] = useState([]);

  // Batch group metadata for timetable propagation
  // Flattened view of batch_group_subjects joined with batches/groups
  const [batchGroupSubjects, setBatchGroupSubjects] = useState([]);
  const [batchGroupBatches, setBatchGroupBatches] = useState([]);
  const [batchGroupInfo, setBatchGroupInfo] = useState({});

  const [stagedByBatch, setStagedByBatch] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [courseFilter, setCourseFilter] = useState(null);
  const [showBatchGroups, setShowBatchGroups] = useState(false);

  // Modal state for add/edit schedule
  const [showModal, setShowModal] = useState(false);
  const [modalContext, setModalContext] = useState(null);
  const [modalTime, setModalTime] = useState({ start: '', end: '' });
  const [modalDurationMin, setModalDurationMin] = useState(null);
  const [selectedBatchSubjectId, setSelectedBatchSubjectId] = useState(null);
  const [selectedSubjectInfo, setSelectedSubjectInfo] = useState(null);
  const [modalSubjects, setModalSubjects] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  // Duplicate schedule to specific future dates
  const [duplicateEnabled, setDuplicateEnabled] = useState(false);
  const [duplicateDateInput, setDuplicateDateInput] = useState('');
  const [duplicateSelectedDates, setDuplicateSelectedDates] = useState([]); // array of yyyy-mm-dd

  // (moved earlier)

  // Build a quick lookup of courseTitle -> set of underlying normalized course names
  const underlyingCourseTokens = useMemo(() => {
    const map = new Map();
    const HND_LEVEL5 = new Set([
      'Level 5 Pearson BTEC HND in Computing (General)',
      'Level 5 Pearson BTEC HND in Computing (Software Engineering)',
      'Level 5 Pearson BTEC HND in Computing (Network Engineering)'
    ]);
    const normalizeCourseBucket = (name, short) => {
      const full = (name || '').trim();
      if (HND_LEVEL5.has(full)) return 'Level 5 Pearson BTEC HND in Computing';
      return full || (short || '').trim() || 'Other Courses';
    };
    rows.forEach(r => {
      const title = normalizeCourseBucket(r.courseName, r.courseShortName);
      const set = map.get(title) || new Set();
      if (r.courseName) set.add(normalizeLoose(r.courseName));
      if (r.courseShortName) set.add(normalizeLoose(r.courseShortName));
      map.set(title, set);
    });
    return map;
  }, [rows]);

  // Global duplicate and conflict maps for the current day view
  const { slotDupColor, slotConflictReasons } = useMemo(() => {
    const slots = [];

    // From API rows
    rows.forEach((r) => {
      const start24 = (r.startTime || '').slice(0, 5);
      const end24 = (r.endTime || '').slice(0, 5);
      const [sh = 0, sm = 0] = start24.split(':').map(Number);
      const [eh = 0, em = 0] = end24.split(':').map(Number);
      slots.push({
        key: `api:${r.scheduleId}`,
        room: (r.classroomName || '').toLowerCase().trim(),
        roomLabel: r.classroomName || '',
        start24,
        end24,
        start12h: to12h(start24),
        end12h: to12h(end24),
        startMin: sh * 60 + sm,
        endMin: eh * 60 + em,
        lecturer: (r.staffName || '').toLowerCase().trim(),
        lecturerLabel: r.staffName || '',
        subjectLabel: r.subjectShortName || r.subjectFullName || '',
        batchLabel: r.batchInternalName || '',
        batchGroupId: r.batchGroupId ? Number(r.batchGroupId) : null,
        classroomId: r.classroomId ? Number(r.classroomId) : null,
      });
    });

    // From staged items
    Object.entries(stagedByBatch).forEach(([batchIdStr, arr]) => {
      const batchId = Number(batchIdStr);
      (arr || []).forEach((s, si) => {
        const start24 = s.start24 || '';
        const end24 = s.end24 || '';
        const [sh = 0, sm = 0] = start24.split(':').map(Number);
        const [eh = 0, em = 0] = end24.split(':').map(Number);
        slots.push({
          key: `staged:${batchId}:${si}`,
          room: (s.room || '').toLowerCase().trim(),
          roomLabel: s.room || '',
          start24,
          end24,
          start12h: s.start || to12h(start24),
          end12h: s.end || to12h(end24),
          startMin: sh * 60 + sm,
          endMin: eh * 60 + em,
          lecturer: (s.lecturer || '').toLowerCase().trim(),
          lecturerLabel: s.lecturer || '',
          subjectLabel: s.subject || '',
          batchLabel: s.batchName || '',
          batchGroupId: s.batchGroupId ? Number(s.batchGroupId) : null,
          classroomId: s.classroomId ? Number(s.classroomId) : null,
        });
      });
    });

    // Duplicate groups: same room + same start + same end
    const dupGroupMap = new Map();
    slots.forEach((slot) => {
      if (!slot.room || !slot.start24 || !slot.end24) return;
      const dupKey = `${slot.room}|${slot.start24}|${slot.end24}`;
      if (!dupGroupMap.has(dupKey)) dupGroupMap.set(dupKey, []);
      dupGroupMap.get(dupKey).push(slot.key);
    });

    const pastelPalette = [
      '#fef9c3', '#d1fae5', '#dbeafe', '#fce7f3',
      '#ede9fe', '#ffedd5', '#e0f2fe', '#cffafe',
      '#fef3c7', '#f0fdf4',
    ];
    let palIdx = 0;
    const slotDupColor = new Map();
    for (const [, keys] of dupGroupMap.entries()) {
      if (keys.length < 2) continue;
      const color = pastelPalette[palIdx % pastelPalette.length];
      palIdx++;
      keys.forEach((k) => slotDupColor.set(k, color));
    }

    // Conflict detection: overlapping time + same room OR same lecturer
    // Pairs sharing the same non-null batchGroupId are intentionally co-scheduled — skip them.
    const slotConflictReasons = new Map(); // key -> string[]
    const addReason = (key, reason) => {
      if (!slotConflictReasons.has(key)) slotConflictReasons.set(key, []);
      const list = slotConflictReasons.get(key);
      if (!list.includes(reason)) list.push(reason);
    };
    const timeOverlaps = (s1, e1, s2, e2) => s1 < e2 && s2 < e1 && e1 > 0 && e2 > 0;
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const a = slots[i];
        const b = slots[j];
        if (!timeOverlaps(a.startMin, a.endMin, b.startMin, b.endMin)) continue;
        // Skip conflicts within the same batch group (they share a classroom intentionally)
        if (
          a.batchGroupId !== null &&
          b.batchGroupId !== null &&
          a.batchGroupId === b.batchGroupId
        ) continue;
        const roomConflict = a.room && b.room && a.room === b.room;
        const lecConflict = a.lecturer && b.lecturer && a.lecturer === b.lecturer;
        if (roomConflict) {
          const timeB = `${b.start12h}–${b.end12h}`;
          const timeA = `${a.start12h}–${a.end12h}`;
          const labelB = [b.batchLabel, b.subjectLabel].filter(Boolean).join(' / ');
          const labelA = [a.batchLabel, a.subjectLabel].filter(Boolean).join(' / ');
          addReason(a.key, `Room "${a.roomLabel}" also used by: ${labelB || 'another class'} (${timeB})`);
          addReason(b.key, `Room "${b.roomLabel}" also used by: ${labelA || 'another class'} (${timeA})`);
        }
        if (lecConflict) {
          const timeB = `${b.start12h}–${b.end12h}`;
          const timeA = `${a.start12h}–${a.end12h}`;
          const labelB = [b.batchLabel, b.subjectLabel].filter(Boolean).join(' / ');
          const labelA = [a.batchLabel, a.subjectLabel].filter(Boolean).join(' / ');
          addReason(a.key, `Lecturer "${a.lecturerLabel}" also teaching: ${labelB || 'another class'} (${timeB})`);
          addReason(b.key, `Lecturer "${b.lecturerLabel}" also teaching: ${labelA || 'another class'} (${timeA})`);
        }
      }
    }

    return { slotDupColor, slotConflictReasons };
  }, [rows, stagedByBatch]);

  // Reintroduce robust permission check using multiple strategies
  const canEditCourse = (courseTitle) => {
    if (!courseTitle) return false;
    // 1) Exact bucket match from precomputed set
    if (editableBucketTitles.has(courseTitle)) return true;

    // 2) Loose string equality between courseTitle and each coordinate course name/short/bucket
    const titleLoose = normalizeLoose(courseTitle);
    const titleLooseHnd = titleLoose.replace(/higher national diploma/g, 'hnd');
    for (const value of Object.values(coordinateCourses || {})) {
      const c = value || {};
      if (!c || !(c.isAdd || c.isUpdate)) continue;
      const cn = normalizeLoose(c.courseName);
      const cs = normalizeLoose(c.shortName);
      const cb = normalizeLoose(normalizeBucketFromNames(c.courseName, c.shortName));
      // also compare after HND expansion
      const cnHnd = cn.replace(/higher national diploma/g, 'hnd');
      const csHnd = cs.replace(/higher national diploma/g, 'hnd');
      const cbHnd = cb.replace(/higher national diploma/g, 'hnd');
      if (titleLoose && (titleLoose === cn || titleLoose === cs || titleLoose === cb)) return true;
      if (titleLooseHnd && (titleLooseHnd === cnHnd || titleLooseHnd === csHnd || titleLooseHnd === cbHnd)) return true;
    }

    // 3) Underlying rows: if any underlying course name under this bucket matches a permitted course
    const underlying = underlyingCourseTokens.get(courseTitle) || new Set();
    if (underlying.size > 0) {
      for (const value of Object.values(coordinateCourses || {})) {
        const c = value || {};
        if (!c || !(c.isAdd || c.isUpdate)) continue;
        const cn = normalizeLoose(c.courseName).replace(/higher national diploma/g, 'hnd');
        const cs = normalizeLoose(c.shortName).replace(/higher national diploma/g, 'hnd');
        if ((cn && underlying.has(cn)) || (cs && underlying.has(cs))) return true;
      }
    }

    // 4) HND bucket heuristic
    if (titleLooseHnd.includes('hnd') && titleLooseHnd.includes('comput')) {
      for (const value of Object.values(coordinateCourses || {})) {
        const c = value || {};
        if (!c || !(c.isAdd || c.isUpdate)) continue;
        if (isHndComputingName(c.courseName, c.shortName)) return true;
      }
    }
    return false;
  };

  // Load coordinate course permissions for the logged-in user and enrich with course names
  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        const accessData = (accessRes && accessRes.data) || {};
        const rawPermissions = accessData.coordinateCourses || {};
        const isSuperAdmin = Boolean(accessData.isSuperAdmin);
        const ids = Array.isArray(accessData.accessibleCourseIds) ? accessData.accessibleCourseIds : [];

        if (cancelled) return;
        setHasFullAccess(isSuperAdmin);
        setAccessibleCourseIds(ids);

        // Best-effort enrichment with course names using /admin/courses
        let effectivePermissions = rawPermissions;
        try {
          const headerValue = isSuperAdmin ? 'all' : ids.join(',');
          const coursesResp = await axiosInstance.get('/admin/courses', {
            headers: headerValue ? { 'x-accessible-courses': headerValue } : {},
          });
          const courseList = (coursesResp && coursesResp.data && coursesResp.data.courses) || [];
          const byId = {};
          courseList.forEach((c) => {
            if (!c || typeof c.courseId === 'undefined') return;
            byId[String(c.courseId)] = c;
          });

          const enriched = {};
          Object.entries(rawPermissions).forEach(([courseId, perm]) => {
            const course = byId[String(courseId)];
            enriched[courseId] = {
              ...perm,
              courseName: course && course.fullName ? course.fullName : perm.courseName,
              shortName: course && course.shortName ? course.shortName : perm.shortName,
            };
          });
          effectivePermissions = enriched;
        } catch (err) {
          // If enrichment fails, fall back to rawPermissions
          console.warn('Timetable: failed to enrich coordinateCourses with course names', err);
        }

        if (!cancelled) {
          setCoordinateCourses(effectivePermissions);
        }
      } catch (err) {
        console.error('Timetable: failed to load coordinate course permissions', err);
        if (!cancelled) {
          setCoordinateCourses({});
          setHasFullAccess(false);
          setAccessibleCourseIds([]);
        }
      }
    };

    loadAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load classrooms from API and keep grouped options by floor
  useEffect(() => {
    let alive = true;
    setRoomsLoading(true);
    axiosInstance
      .get('/admin/classrooms/list')
      .then((response) => {
        if (!alive) return;
        const data = response && response.data;
        const mapped = (data || []).map((d) => ({
          classroomId: d.classroomId,
          name: d.name,
          floor: String(d.floor ?? ''),
          type: d.type,
        }));
        setRooms(mapped);
      })
      .catch(() => {
        if (!alive) return;
        setRooms([]);
      })
      .finally(() => {
        if (!alive) return;
        setRoomsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Load batch group metadata (batch_groups + batch_group_subjects) for timetable propagation
  useEffect(() => {
    let cancelled = false;

    const loadBatchGroupMeta = async () => {
      try {
        const groupsRes = await axiosInstance.get('/admin/batch-groups');
        const groupsData = (groupsRes && groupsRes.data && groupsRes.data.batchGroups) || [];

        const collectedSubjects = [];
        const collectedBatches = [];
        const groupInfoMap = {};
        const uniqueGroupIds = new Set();

        groupsData.forEach((group) => {
          if (!group || group.batchGroupId === null || group.batchGroupId === undefined) return;
          const idNum = Number(group.batchGroupId);
          if (!Number.isFinite(idNum)) return;
          uniqueGroupIds.add(idNum);
          groupInfoMap[String(idNum)] = {
            name: typeof group.name === 'string' ? group.name.trim() : '',
            whatsappGroupName: typeof group.whatsappGroupName === 'string' ? group.whatsappGroupName.trim() : '',
          };
        });

        for (const batchGroupId of uniqueGroupIds) {
          try {
            const batchesRes = await axiosInstance.get(`/admin/batch-groups/${batchGroupId}/batches`);
            const batchesData = batchesRes && batchesRes.data && batchesRes.data.debug;
            const pivotRows = (batchesData && batchesData.pivotRows) || [];
            pivotRows.forEach((row) => {
              collectedBatches.push({
                batchGroupId,
                batchId: row && row.batchId ? Number(row.batchId) : null,
              });
            });

            const subjectsRes = await axiosInstance.get(`/admin/batch-groups/${batchGroupId}/subjects`);
            const subjectsData = subjectsRes && subjectsRes.data;
            const assigned = (subjectsData && subjectsData.assignedBatchSubjects) || [];

            assigned.forEach((s) => {
              const subjectName =
                (s.customSubjectName && String(s.customSubjectName).trim()) ||
                (s.subject && s.subject.shortName && String(s.subject.shortName).trim()) ||
                (s.subject && s.subject.fullName && String(s.subject.fullName).trim()) ||
                '';

              collectedSubjects.push({
                batchGroupId,
                batchId: s.batch && s.batch.batchId ? Number(s.batch.batchId) : null,
                batchSubjectId: Number(s.batchSubjectId),
                subjectName,
              });
            });
          } catch (err) {
            console.error('Timetable: failed to load batch group subjects for group', batchGroupId, err);
          }
        }

        if (!cancelled) {
          setBatchGroupSubjects(collectedSubjects);
          setBatchGroupBatches(collectedBatches);
          setBatchGroupInfo(groupInfoMap);
        }
      } catch (err) {
        console.error('Timetable: failed to load batch group metadata for timetable', err);
        if (!cancelled) {
          setBatchGroupSubjects([]);
          setBatchGroupBatches([]);
          setBatchGroupInfo({});
        }
      }
    };

    loadBatchGroupMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load batches and courses so that we can show batches even without schedules
  useEffect(() => {
    let cancelled = false;

    const loadBatches = async () => {
      try {
        // Determine active schedule type from selected date (weekday vs weekend)
        let scheduleType = 'weekday';
        try {
          const d = new Date(`${selectedDate}T00:00:00`);
          const day = d.getDay(); // 0 = Sunday, 6 = Saturday
          if (day === 0 || day === 6) {
            scheduleType = 'weekend';
          }
        } catch (e) {
          scheduleType = 'weekday';
        }
        const batchesRes = await axiosInstance.get('/admin/batches', {
          params: { status: 'ongoing', scheduleType },
          // For timetable view we need to see all ongoing batches across all courses,
          // even if the logged-in user is a coordinator for only some courses.
          // Editing is still restricted in the UI via canEditCourse/coordinateCourses.
          headers: { 'x-accessible-courses': 'all' },
        });
        const batchData = (batchesRes && batchesRes.data) || {};
        if (!cancelled) {
          const allBatches = batchData.batches || [];
          // Backend already filters by status=ongoing; keep this as a safety net
          const ongoingOnly = allBatches.filter((b) => (b.status || '').toLowerCase() === 'ongoing');
          setBatches(ongoingOnly);
        }
      } catch (err) {
        console.error('Timetable: failed to load batches', err);
        if (!cancelled) {
          setBatches([]);
        }
      }
    };

    loadBatches();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  const groupedRoomOptions = useMemo(() => {
    const byFloor = new Map();
    rooms.forEach((r) => {
      const key = (r.floor && String(r.floor).trim()) || 'Unknown';
      const list = byFloor.get(key) || [];
      list.push({ value: r.classroomId, label: r.name, room: r });
      byFloor.set(key, list);
    });
    const floors = Array.from(byFloor.keys()).sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      const aNum = !isNaN(na);
      const bNum = !isNaN(nb);
      if (aNum && bNum) return na - nb;
      if (aNum && !bNum) return -1;
      if (!aNum && bNum) return 1;
      return a.localeCompare(b);
    });
    return floors.map((f) => ({
      label: `Floor ${f}`,
      options: (byFloor.get(f) || []).sort((x, y) => x.label.localeCompare(y.label)),
    }));
  }, [rooms]);

  const selectedRoomOption = useMemo(() => {
    if (!selectedRoomId) return null;
    for (const g of groupedRoomOptions) {
      const found = g.options.find((o) => o.value === selectedRoomId);
      if (found) return found;
    }
    return null;
  }, [groupedRoomOptions, selectedRoomId]);

  const stagedTotal = useMemo(
    () => Object.values(stagedByBatch).reduce((acc, arr) => acc + arr.length, 0),
    [stagedByBatch]
  );
  
  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(stagedTotal > 0);
  }, [stagedTotal]);
  
  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  // Track copied WhatsApp groups for persistent visual confirmation until reset
  const [copiedGroups, setCopiedGroups] = useState({});
  // Summary modal state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryTab, setSummaryTab] = useState('classes');
  const [summarySearchQuery, setSummarySearchQuery] = useState('');
  const [lecturerSchedules, setLecturerSchedules] = useState([]);
  const [lecturerSchedulesLoading, setLecturerSchedulesLoading] = useState(false);
  const [lecturerFilter, setLecturerFilter] = useState('lecturer');
  const [classroomSchedules, setClassroomSchedules] = useState([]);
  const [classroomSchedulesLoading, setClassroomSchedulesLoading] = useState(false);
  // Summary modal: floor filter for Classrooms tab
  const [summaryFloorFilter, setSummaryFloorFilter] = useState('All');
  // Summary modal: Available tab (time-range search)
  const [availableStart, setAvailableStart] = useState('09:00');
  const [availableEnd, setAvailableEnd] = useState('10:00');
  const [availableResults, setAvailableResults] = useState(null);

  // Returns true when a yyyy-mm-dd string falls on Saturday or Sunday
  const isWeekendDate = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(`${dateStr}T00:00:00`);
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  // Minimum date allowed for duplicating schedules (day after selected date)
  const minDuplicateDate = useMemo(() => {
    if (!selectedDate) return '';
    const parts = selectedDate.split('-').map((p) => parseInt(p, 10));
    if (parts.length !== 3 || parts.some((v) => Number.isNaN(v))) return '';
    const [y, m, d] = parts;
    const dt = new Date(y, m - 1, d + 1);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }, [selectedDate]);
  const [availableRange, setAvailableRange] = useState(null); // { startMin, endMin } for last query
  const [availableError, setAvailableError] = useState('');
  const [availableStatusFilter, setAvailableStatusFilter] = useState('all'); // all | fully-free | partially-free | busy
  const [availableClassroomQuery, setAvailableClassroomQuery] = useState('');
  const [availableLecturerQuery, setAvailableLecturerQuery] = useState('');

  // Derived classroom timelines from rows + rooms (fallback or primary source)
  const derivedClassroomSchedules = useMemo(() => {
    // Build map of classroomId -> schedules from rows (API /schedules-by-date)
    const byRoom = new Map();
    rows.forEach((r) => {
      if (!r.classroomId || !r.startTime || !r.endTime) return;
      const list = byRoom.get(r.classroomId) || [];
      list.push({
        subjectName: r.subjectShortName || r.subjectFullName || '',
        batchName: r.batchInternalName || '',
        lecturer: r.staffName || 'TBD',
        startTime: (r.startTime || '').slice(0,5),
        endTime: (r.endTime || '').slice(0,5),
        source: 'api'
      });
      byRoom.set(r.classroomId, list);
    });
    
    // Add staged schedules to classrooms
    Object.entries(stagedByBatch).forEach(([batchIdStr, stagedArr]) => {
      const batchId = parseInt(batchIdStr, 10);
      
      // Find the batch info from rows
      const batchInfo = rows.find(r => r.batchId === batchId);
      const batchName = batchInfo?.batchInternalName || 'Unknown Batch';
      
      stagedArr.forEach((staged) => {
        // Find classroom by name
        const classroom = rooms.find(r => r.name === staged.room);
        if (!classroom || !classroom.classroomId) return;
        
        const list = byRoom.get(classroom.classroomId) || [];
        
        // Check for duplicates before adding
        const isDuplicate = list.some(existing => 
          existing.subjectName === (staged.subject || '') &&
          existing.startTime === (staged.start24 || '') &&
          existing.endTime === (staged.end24 || '') &&
          existing.batchName === batchName
        );
        
        if (!isDuplicate) {
          list.push({
            subjectName: staged.subject || '',
            batchName: batchName,
            lecturer: staged.lecturer || 'TBD',
            startTime: staged.start24 || '',
            endTime: staged.end24 || '',
            source: 'staged'
          });
        }
        
        byRoom.set(classroom.classroomId, list);
      });
    });

    // Create full list of classrooms with schedules
    const all = rooms.map((room) => ({
      classroomId: room.classroomId,
      name: room.name,
      floor: room.floor,
      capacity: room && room.capacity,
      schedules: (byRoom.get(room.classroomId) || []).sort((a, b) => a.startTime.localeCompare(b.startTime)),
    }));
    return all;
  }, [rows, rooms, stagedByBatch]);

  // Distinct floor options for Classrooms tab (All + sorted floors + optional Unknown)
  const summaryFloorOptions = useMemo(() => {
    const floorsSet = new Set();
    derivedClassroomSchedules.forEach((c) => {
      const f = (c.floor && String(c.floor).trim()) || 'Unknown';
      floorsSet.add(f);
    });
    const floors = Array.from(floorsSet);
    floors.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      const aNum = !isNaN(na);
      const bNum = !isNaN(nb);
      if (aNum && bNum) return na - nb;
      if (aNum && !bNum) return -1;
      if (!aNum && bNum) return 1;
      return a.localeCompare(b);
    });
    // Build ordered options:
    // - All
    // - Ground Floor (if present)
    // - Numeric/other floors (excluding Unknown and Ground Floor)
    // - Unknown (last if present)
    const hasUnknown = floors.some((f) => f.toLowerCase() === 'unknown');
    const hasGround = floors.some((f) => f.toLowerCase() === 'ground floor');
    const rest = floors.filter((f) => {
      const v = f.toLowerCase();
      return v !== 'unknown' && v !== 'ground floor';
    });
    return ['All', ...(hasGround ? ['Ground Floor'] : []), ...rest, ...(hasUnknown ? ['Unknown'] : [])];
  }, [derivedClassroomSchedules]);

  // Copy a full-day summary across groups (respecting optional course filter)
  const handleCopySummary = async () => {
    try {
      // Format date string
      const d = new Date(`${selectedDate}T00:00:00`);
      const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
      const month = d.toLocaleDateString(undefined, { month: 'long' });
      const day = d.getDate();
      const year = d.getFullYear();
      const dateStr = `${weekday}-${month} ${day}, ${year}`;

      const lines = [];

      const toItemsForGroup = (batches) => {
        const items = [];
        // API
        Object.values(batches || {}).forEach((b) => {
          (b.items || []).forEach((it) => {
            const [hh, mm] = (it.startTime || '00:00').split(':').map((n) => parseInt(String(n), 10));
            items.push({
              subject: it.subjectShortName || it.subjectFullName || '',
              start: to12h(it.startTime),
              end: to12h(it.endTime),
              startMin: (hh || 0) * 60 + (mm || 0),
              room: it.classroomName || '',
              staff: it.staffName || 'TBD',
              groupChips: (it.subjectGroupNames ? String(it.subjectGroupNames).split(',').map((x) => x.trim()).filter(Boolean) : []),
            });
          });
        });
        // Staged
        Object.values(batches || {}).forEach((b) => {
          const stagedArr = stagedByBatch[b.batchId || -1] || [];
          stagedArr.forEach((s) => {
            items.push({
              subject: s.subject || '',
              start: s.start,
              end: s.end,
              startMin: s.startMin || 0,
              room: s.room || '',
              staff: s.lecturer || 'TBD',
              groupChips: s.groupChips || [],
            });
          });
        });
        // Dedup by time + hall + lecturer (ignore subject duplicates)
        const seen = new Set();
        const deduped = [];
        for (const it of items) {
          const key = [
            (it.start || '').trim(),
            (it.end || '').trim(),
            (it.room || '').trim().toLowerCase(),
            (it.staff || '').trim().toLowerCase(),
          ].join('|');
          if (!seen.has(key)) {
            seen.add(key);
            deduped.push(it);
          }
        }
        // Sort SE-first then time
        const hasSE = (chips, subject) => {
          if (chips && chips.length > 0) return chips.some((c) => c.toUpperCase().includes('SE'));
          return (subject || '').toUpperCase().includes(' SE') || (subject || '').toUpperCase().startsWith('SE');
        };
        deduped.sort((a, b) => {
          const aSE = hasSE(a.groupChips, a.subject);
          const bSE = hasSE(b.groupChips, b.subject);
          if (aSE !== bSE) return aSE ? -1 : 1;
          return a.startMin - b.startMin;
        });
        return deduped;
      };

      // Build summary across courses
      for (const [courseTitle, types] of Object.entries(groupedByCourse)) {
        if (courseFilter && courseTitle !== courseFilter) continue;
        // Merge weekday and weekend groups into one
        const mergedGroups = {};
        const mergeInto = (src) => {
          Object.entries(src || {}).forEach(([gk, batches]) => {
            if (!mergedGroups[gk]) mergedGroups[gk] = {};
            Object.entries(batches || {}).forEach(([bk, val]) => {
              mergedGroups[gk][bk] = val;
            });
          });
        };
        mergeInto((types && types.weekday) || {});
        mergeInto((types && types.weekend) || {});

        const groupEntries = Object.entries(mergedGroups);
        if (groupEntries.length === 0) continue;

        // Optional course header
        lines.push(`===== ${courseTitle} =====`);
        for (const [groupName, batches] of groupEntries) {
          const items = toItemsForGroup(batches);
          if (items.length === 0) continue;
          lines.push(`*${groupName}* on *${dateStr}*:`);
          lines.push('');
          items.forEach((it) => {
            lines.push(`*${it.subject}*`);
            lines.push(`Time : *${it.start} - ${it.end}*`);
            lines.push(`Hall : *${it.room || 'TBD'}*`);
            lines.push(`Lec : *${it.staff}*`);
            lines.push('');
          });
        }
        lines.push('');
      }

      if (lines.length === 0) {
        await Swal.fire({ icon: 'info', title: 'No schedules', text: 'There are no schedules to summarize for this date.' });
        return;
      }

      await navigator.clipboard.writeText(lines.join('\n'));
      await Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Summary copied!',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Copy failed', text: (err && err.message) || 'Unable to copy summary.' });
    }
  };

  const handleSaveAll = async () => {
    if (stagedTotal === 0) return;
    try {
      // Persist staged schedules via Node backend
      for (const arr of Object.values(stagedByBatch)) {
        for (const item of arr) {
          const duplicateDates = Array.isArray(item.duplicateDates) ? item.duplicateDates : [];
          const targetDates = duplicateDates.length > 0 ? [selectedDate, ...duplicateDates] : [selectedDate];

          for (const dateStr of targetDates) {
            const payload = {
              date: dateStr,
              batchSubjectId: item.batchSubjectId,
              classroomId: item.classroomId,
              startTime: item.start24,
              endTime: item.end24,
              batchGroupId: item.batchGroupId ?? null,
            };
            let res;
            if (dateStr === selectedDate && item.mode === 'edit' && item.scheduleId) {
              // Update existing schedule for the current date instead of inserting a new one
              res = await axiosInstance.put(`/admin/attendance/schedules/${item.scheduleId}`, payload);
            } else {
              // Create a new schedule (including duplicates on other dates)
              res = await axiosInstance.post('/admin/attendance/schedules', payload);
            }
            if (!res || (res.status !== 200 && res.status !== 201)) {
              throw new Error((res && res.data && res.data.error) || 'Failed to save a schedule');
            }
          }
        }
      }
      // Clear staged and refresh table from Node backend
      setStagedByBatch({});
      setHasUnsavedChanges(false);
      setLoading(true);
      try {
        const response = await axiosInstance.get('/admin/attendance/schedules', { params: { date: selectedDate } });
        const data = response && response.data;
        setRows(data || []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
      await Swal.fire({
        title: 'Saved!',
        text: 'All staged schedules have been saved successfully.',
        icon: 'success',
        confirmButtonColor: '#16a34a',
        confirmButtonText: 'OK'
      });
    } catch (e) {
      console.error(e);
      await Swal.fire({
        title: 'Error',
        text: (e && e.message) || 'Failed to save schedules',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'Close'
      });
    }
  };

  // Handle date change with unsaved changes warning
  const handleDateChange = async (newDate) => {
    if (hasUnsavedChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        html: `<div class="text-sm">You have <strong>${stagedTotal}</strong> unsaved ${stagedTotal === 1 ? 'schedule' : 'schedules'}.<br/>Changing the date will discard these changes.</div>`,
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#16a34a',
        denyButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '💾 Save & Continue',
        denyButtonText: '🗑️ Discard Changes',
        cancelButtonText: 'Cancel',
      });
      
      if (result.isConfirmed) {
        // Save first, then change date
        await handleSaveAll();
        setSelectedDate(newDate);
      } else if (result.isDenied) {
        // Discard changes and change date
        setStagedByBatch({});
        setHasUnsavedChanges(false);
        setSelectedDate(newDate);
      }
      // If cancelled, do nothing
    } else {
      setSelectedDate(newDate);
    }
  };

  useEffect(() => {
    // Load schedules for selected date
    setLoading(true);
    axiosInstance
      .get('/admin/attendance/schedules', { params: { date: selectedDate } })
      .then((response) => {
        const data = response && response.data;
        setRows(data || []);
      })
      .catch(() => {
        setRows([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedDate]);

  // Load lecturer schedules when summary modal opens on lecturers tab
  const loadLecturerSchedules = async () => {
    setLecturerSchedulesLoading(true);
    try {
      const response = await axiosInstance.get('/admin/staff-schedules', {
        params: { date: selectedDate },
      });
      const data = (response && response.data) || [];
      setLecturerSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load lecturer schedules:', error);
      setLecturerSchedules([]);
    } finally {
      setLecturerSchedulesLoading(false);
    }
  };

  // Load lecturer schedules when switching to lecturers tab
  useEffect(() => {
    if (showSummaryModal && (summaryTab === 'lecturers' || summaryTab === 'available')) {
      loadLecturerSchedules();
      loadStaffUnavailability();
    }
  }, [showSummaryModal, summaryTab, selectedDate]);

  // Merge timetable + staged schedules into lecturer schedules for Daily Summary (Lecturers tab)
  const lecturerSchedulesWithStaged = useMemo(() => {
    if (!lecturerSchedules || lecturerSchedules.length === 0) return [];

    // Build a map of staffName -> schedules coming from the main timetable rows
    const byStaffFromRows = new Map();
    rows.forEach((r) => {
      const staffKey = (r.staffName || '').trim();
      if (!staffKey) return;
      const list = byStaffFromRows.get(staffKey) || [];
      list.push({
        subjectName: r.subjectShortName || r.subjectFullName || '',
        batchName: r.batchInternalName || '',
        classroom: r.classroomName || '',
        startTime: (r.startTime || '').slice(0, 5),
        endTime: (r.endTime || '').slice(0, 5),
        source: 'timetable',
      });
      byStaffFromRows.set(staffKey, list);
    });

    // Build a map of staffName -> staged schedules
    const stagedByStaff = new Map();
    
    // Iterate through all batches and their staged schedules
    Object.entries(stagedByBatch).forEach(([batchIdStr, stagedArr]) => {
      const batchId = parseInt(batchIdStr, 10);
      
      // Find the batch info from rows
      const batchInfo = rows.find(r => r.batchId === batchId);
      const batchName = batchInfo?.batchInternalName || 'Unknown Batch';
      
      stagedArr.forEach((staged) => {
        const staffKey = staged.lecturer || 'TBD';
        if (!stagedByStaff.has(staffKey)) {
          stagedByStaff.set(staffKey, []);
        }

        stagedByStaff.get(staffKey).push({
          subjectName: staged.subject || '',
          batchName: batchName,
          classroom: staged.room || '',
          startTime: staged.start24 || '',
          endTime: staged.end24 || '',
          groupNames: (staged.groupChips || []).join(', '),
          source: 'staged'
        });
      });
    });
    
    // Merge timetable + staged schedules into lecturer data
    return lecturerSchedules.map(staff => {
      const staffSchedules = [...(staff.schedules || [])];
      const nameKey = (staff.name || '').trim();

      // Add schedules coming from the main timetable rows
      const rowsForStaff = byStaffFromRows.get(nameKey) || [];
      rowsForStaff.forEach((rowSched) => {
        const isDuplicate = staffSchedules.some(existing =>
          (existing.subjectName || '') === (rowSched.subjectName || '') &&
          (existing.batchName || '') === (rowSched.batchName || '') &&
          (existing.classroom || '') === (rowSched.classroom || '') &&
          (existing.startTime || '').slice(0, 5) === rowSched.startTime &&
          (existing.endTime || '').slice(0, 5) === rowSched.endTime
        );
        if (!isDuplicate) {
          staffSchedules.push(rowSched);
        }
      });
      
      // Add staged schedules for this staff member (match by name)
      const stagedForStaff = stagedByStaff.get(nameKey) || [];
      stagedForStaff.forEach(staged => {
        // Check for duplicates before adding
        const isDuplicate = staffSchedules.some(existing => 
          existing.subjectName === staged.subjectName &&
          existing.startTime === staged.startTime &&
          existing.endTime === staged.endTime &&
          existing.classroom === staged.classroom
        );
        
        if (!isDuplicate) {
          staffSchedules.push(staged);
        }
      });
      
      return {
        ...staff,
        schedules: staffSchedules
      };
    });
  }, [lecturerSchedules, stagedByBatch, rows]);

  // Daily Summary > Available tab: find free windows for lecturers and classrooms
  const handleFindAvailableLecturers = () => {
    setAvailableError('');

    if (!availableStart || !availableEnd) {
      setAvailableError('Please select both start and end times.');
      setAvailableResults({ lecturers: [], classrooms: [] });
      setAvailableRange(null);
      return;
    }

    const parseTimeToMinutes = (val) => {
      const parts = String(val).split(':');
      if (parts.length < 2) return null;
      const [hRaw, mRaw] = parts;
      const h = parseInt(hRaw, 10);
      const m = parseInt(mRaw, 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    const startMin = parseTimeToMinutes(availableStart);
    const endMin = parseTimeToMinutes(availableEnd);

    if (startMin === null || endMin === null) {
      setAvailableError('Invalid time format. Please use HH:MM.');
      setAvailableResults({ lecturers: [], classrooms: [] });
      setAvailableRange(null);
      return;
    }

    if (endMin <= startMin) {
      setAvailableError('End time must be after start time.');
      setAvailableResults({ lecturers: [], classrooms: [] });
      setAvailableRange(null);
      return;
    }

    const DAY_START = 8 * 60 + 30; // 8:30 AM
    const DAY_END = 17 * 60; // 5:00 PM

    // Clip query to working day window
    let queryStart = Math.max(startMin, DAY_START);
    let queryEnd = Math.min(endMin, DAY_END);

    if (queryEnd <= queryStart) {
      setAvailableError('Selected time is outside working hours (8:30 AM - 5:00 PM).');
      setAvailableResults({ lecturers: [], classrooms: [] });
      setAvailableRange(null);
      return;
    }

    // Persist the effective range for the UI
    setAvailableRange({ startMin: queryStart, endMin: queryEnd });

    // Helper: given blocked segments, compute free slots inside [queryStart, queryEnd]
    const computeFreeSlots = (blocked) => {
      if (!Array.isArray(blocked) || blocked.length === 0) {
        return [
          {
            startMin: queryStart,
            endMin: queryEnd,
            start: `${String(Math.floor(queryStart / 60)).padStart(2, '0')}:${String(queryStart % 60).padStart(2, '0')}`,
            end: `${String(Math.floor(queryEnd / 60)).padStart(2, '0')}:${String(queryEnd % 60).padStart(2, '0')}`,
          },
        ];
      }

      // Clip blocked segments to query window and normalize
      const clipped = blocked
        .map((seg) => ({
          start: Math.max(seg.start, queryStart),
          end: Math.min(seg.end, queryEnd),
        }))
        .filter((seg) => seg.end > seg.start)
        .sort((a, b) => a.start - b.start);

      // Merge overlapping blocked segments
      const merged = [];
      for (const seg of clipped) {
        if (!merged.length || seg.start > merged[merged.length - 1].end) {
          merged.push({ ...seg });
        } else {
          merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, seg.end);
        }
      }

      const free = [];
      let cursor = queryStart;
      for (const seg of merged) {
        if (seg.start > cursor) {
          const fsStart = cursor;
          const fsEnd = seg.start;
          if (fsEnd > fsStart) {
            free.push({
              startMin: fsStart,
              endMin: fsEnd,
              start: `${String(Math.floor(fsStart / 60)).padStart(2, '0')}:${String(fsStart % 60).padStart(2, '0')}`,
              end: `${String(Math.floor(fsEnd / 60)).padStart(2, '0')}:${String(fsEnd % 60).padStart(2, '0')}`,
            });
          }
        }
        cursor = Math.max(cursor, seg.end);
      }

      if (cursor < queryEnd) {
        const fsStart = cursor;
        const fsEnd = queryEnd;
        if (fsEnd > fsStart) {
          free.push({
            startMin: fsStart,
            endMin: fsEnd,
            start: `${String(Math.floor(fsStart / 60)).padStart(2, '0')}:${String(fsStart % 60).padStart(2, '0')}`,
            end: `${String(Math.floor(fsEnd / 60)).padStart(2, '0')}:${String(fsEnd % 60).padStart(2, '0')}`,
          });
        }
      }

      return free;
    };

    const timeToMinutes = (time24) => {
      if (!time24) return null;
      const parts = String(time24).split(':');
      if (parts.length < 2) return null;
      const [hRaw, mRaw] = parts;
      const h = parseInt(hRaw, 10);
      const m = parseInt(mRaw, 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    // ----- Lecturers -----
    const lecturerResults = (lecturerSchedulesWithStaged || [])
      .map((staff) => {
        if (!staff) return null;

        const blocked = [];
        const roster = staff.roster;

        // Roster-based unavailability
        if (roster) {
          if (roster.dayType === 'off day') {
            blocked.push({ start: DAY_START, end: DAY_END });
          } else if (roster.dayType === 'morning half day') {
            // Works 12:30 PM - 5:00 PM, unavailable before 12:30 PM
            blocked.push({ start: DAY_START, end: 12 * 60 + 30 });
          } else if (roster.dayType === 'evening half day') {
            // Works 9:00 AM - 1:00 PM, unavailable after 1:00 PM
            blocked.push({ start: 13 * 60, end: DAY_END });
          }
        }

        const isHalfDay =
          roster &&
          (roster.dayType === 'morning half day' || roster.dayType === 'evening half day');

        // Lunch break (1:00 PM - 2:00 PM) is not available for full-day staff
        if (!isHalfDay) {
          blocked.push({ start: 13 * 60, end: 14 * 60 });
        }

        // staff_unavailability blocks (leave = full day, unavailability = time range or full day)
        (staffUnavailability || []).forEach((rec) => {
          if (rec.staffId !== staff.staffId) return;
          if (rec.type === 'leave' || (!rec.startTime && !rec.endTime)) {
            blocked.push({ start: DAY_START, end: DAY_END });
          } else if (rec.startTime && rec.endTime) {
            const uStartParts = String(rec.startTime).split(':').map(Number);
            const uEndParts = String(rec.endTime).split(':').map(Number);
            const uStart = (uStartParts[0] || 0) * 60 + (uStartParts[1] || 0);
            const uEnd = (uEndParts[0] || 0) * 60 + (uEndParts[1] || 0);
            if (uEnd > uStart) blocked.push({ start: uStart, end: uEnd });
          } else {
            blocked.push({ start: DAY_START, end: DAY_END });
          }
        });

        // Scheduled classes are busy periods
        (staff.schedules || []).forEach((schedule) => {
          const sStart = timeToMinutes(schedule.startTime);
          const sEnd = timeToMinutes(schedule.endTime);
          if (sStart === null || sEnd === null) return;
          blocked.push({ start: sStart, end: sEnd });
        });

        const freeSlots = computeFreeSlots(blocked);

        let category;
        if (!freeSlots || freeSlots.length === 0) {
          category = 'busy';
        } else if (
          freeSlots.length === 1 &&
          freeSlots[0].startMin === queryStart &&
          freeSlots[0].endMin === queryEnd
        ) {
          category = 'fully-free';
        } else {
          category = 'partially-free';
        }

        return { staff, freeSlots: freeSlots || [], category };
      })
      .filter(Boolean)
      .sort((a, b) => (a.staff.name || '').localeCompare(b.staff.name || ''));

    // ----- Classrooms -----
    const classroomResults = (derivedClassroomSchedules || [])
      .map((classroom) => {
        if (!classroom) return null;

        const blocked = [];
        (classroom.schedules || []).forEach((schedule) => {
          const sStart = timeToMinutes(schedule.startTime);
          const sEnd = timeToMinutes(schedule.endTime);
          if (sStart === null || sEnd === null) return;
          blocked.push({ start: sStart, end: sEnd });
        });

        const freeSlots = computeFreeSlots(blocked);

        let category;
        if (!freeSlots || freeSlots.length === 0) {
          category = 'busy';
        } else if (
          freeSlots.length === 1 &&
          freeSlots[0].startMin === queryStart &&
          freeSlots[0].endMin === queryEnd
        ) {
          category = 'fully-free';
        } else {
          category = 'partially-free';
        }

        return { classroom, freeSlots: freeSlots || [], category };
      })
      .filter(Boolean)
      .sort((a, b) => (a.classroom.name || '').localeCompare(b.classroom.name || ''));

    setAvailableResults({ lecturers: lecturerResults, classrooms: classroomResults });
  };

  // --- Staff image errors (Daily Summary > Lecturers tab) ---
  const [staffImageErrors, setStaffImageErrors] = useState({});

  // --- Staff unavailability for Daily Summary (Lecturers + Available tabs) ---
  const [staffUnavailability, setStaffUnavailability] = useState([]);

  // Cache for staff roster data keyed by date (avoids repeated API calls per session)
  const staffRosterCacheRef = useRef({ date: null, byName: new Map() });

  const loadStaffUnavailability = async () => {
    if (!selectedDate) return;
    try {
      const response = await axiosInstance.get('/admin/staff-unavailability', {
        params: { dateFrom: selectedDate, dateTo: selectedDate },
      });
      const data = response?.data;
      setStaffUnavailability(Array.isArray(data?.records) ? data.records : []);
    } catch {
      setStaffUnavailability([]);
    }
  };

  const staffUnavailabilityByStaffId = useMemo(() => {
    const map = new Map();
    staffUnavailability.forEach((rec) => {
      const list = map.get(rec.staffId) || [];
      list.push(rec);
      map.set(rec.staffId, list);
    });
    return map;
  }, [staffUnavailability]);

  // Always keep staffUnavailability in sync with the selected date so the
  // confirmation check can access it without the Daily Summary modal being open.
  useEffect(() => {
    if (!selectedDate) return;
    axiosInstance
      .get('/admin/staff-unavailability', {
        params: { dateFrom: selectedDate, dateTo: selectedDate },
      })
      .then((response) => {
        const data = response?.data;
        setStaffUnavailability(Array.isArray(data?.records) ? data.records : []);
      })
      .catch(() => {
        setStaffUnavailability([]);
      });
    // Also invalidate the roster cache for the previous date
    staffRosterCacheRef.current = { date: null, byName: new Map() };
  }, [selectedDate]);

  // Load classroom schedules when summary modal opens on classrooms tab
  const loadClassroomSchedules = async () => {
    setClassroomSchedulesLoading(true);
    try {
      const params = new URLSearchParams({ date: selectedDate });
  const response = await fetch(`/admin/api/classroom-schedules?${params.toString()}`, { credentials: 'same-origin' });
      if (response.ok) {
        const data = await response.json();
        console.log('Classroom schedules loaded:', data);
        setClassroomSchedules(data || []);
      } else {
        console.error('Failed to load classroom schedules:', response.status, response.statusText);
        setClassroomSchedules([]);
      }
    } catch (error) {
      console.error('Failed to load classroom schedules:', error);
      setClassroomSchedules([]);
    } finally {
      setClassroomSchedulesLoading(false);
    }
  };

  // Load classroom schedules when switching to classrooms tab
  useEffect(() => {
    if (showSummaryModal && summaryTab === 'classrooms') {
      loadClassroomSchedules();
    }
  }, [showSummaryModal, summaryTab, selectedDate]);

  // Derived: group by Course -> (weekday/weekend) -> WhatsApp group -> batches -> sorted schedules
  const groupedByCourse = useMemo(() => {
    // Course title -> { weekday: groups, weekend: groups }
    const courses = {};
    const toKey = (v) => (v === null || v === undefined ? 'null' : String(v));

    const HND_LEVEL5 = new Set([
      'Level 5 Pearson BTEC HND in Computing (General)',
      'Level 5 Pearson BTEC HND in Computing (Software Engineering)',
      'Level 5 Pearson BTEC HND in Computing (Network Engineering)'
    ]);
    const normalizeCourseBucket = (name, short) => {
      const full = (name || '').trim();
      if (HND_LEVEL5.has(full)) return 'Level 5 Pearson BTEC HND in Computing';
      return (full || (short || '').trim() || 'Other Courses');
    };

    const sortByStartTime = (list) => {
      return [...list].sort((a, b) => {
        // a.startTime and b.startTime are HH:MM:SS
        const [ah, am] = (a.startTime || '00:00').split(':').map((n) => parseInt(n, 10));
        const [bh, bm] = (b.startTime || '00:00').split(':').map((n) => parseInt(n, 10));
        return ah !== bh ? ah - bh : am - bm;
      });
    };

    // Active schedule type for the selected date
    let activeScheduleType = 'weekday';
    try {
      const d = new Date(`${selectedDate}T00:00:00`);
      const day = d.getDay();
      if (day === 0 || day === 6) {
        activeScheduleType = 'weekend';
      }
    } catch (e) {
      activeScheduleType = 'weekday';
    }

    // First, seed structure from batches (ensures batches appear even without schedules)
    batches.forEach((b) => {
      const course = b.course || {};
      const courseTitle = normalizeCourseBucket(
        course.fullName || b.courseFullName || '',
        course.shortName || b.courseShortName || ''
      );
      const schedType = (course.scheduleType || b.scheduleType) === 'weekend' ? 'weekend' : 'weekday';
      if (schedType !== activeScheduleType) {
        return; // skip batches not matching the active schedule type
      }
      if (!courses[courseTitle]) {
        courses[courseTitle] = { weekday: {}, weekend: {} };
      }
      const groups = courses[courseTitle][schedType];
      const groupKey = (b.whatsappGroupName || 'No Group').trim();
      if (!groups[groupKey]) groups[groupKey] = {};
      const batchKey = toKey(b.batchId);
      if (!groups[groupKey][batchKey]) {
        groups[groupKey][batchKey] = {
          batchId: b.batchId,
          batchInternalName: b.batchInternalName || b.batchName,
          whatsappGroupName: b.whatsappGroupName,
          items: [],
          studentCount: b.activeRegistrations || 0,
        };
      }
    });

    // Then, merge in schedule rows for the selected date
    rows.forEach((r) => {
      const courseTitle = normalizeCourseBucket(r.courseName, r.courseShortName);
      const schedType = r.courseScheduleType === 'weekend' ? 'weekend' : 'weekday';
      if (schedType !== activeScheduleType) {
        return; // skip schedules not matching the active schedule type
      }
      if (!courses[courseTitle]) {
        courses[courseTitle] = { weekday: {}, weekend: {} };
      }
      const groups = courses[courseTitle][schedType];
      const groupKey = (r.whatsappGroupName || 'No Group').trim();
      if (!groups[groupKey]) groups[groupKey] = {};
      const batchKey = toKey(r.batchId);
      if (!groups[groupKey][batchKey]) {
        groups[groupKey][batchKey] = {
          batchId: r.batchId,
          batchInternalName: r.batchInternalName,
          whatsappGroupName: r.whatsappGroupName,
          items: [],
          studentCount: r.studentCount || 0,
        };
      }
      if (r.scheduleId) {
        groups[groupKey][batchKey].items.push(r);
      }
    });

    // Sort schedules within each batch by start time
    Object.values(courses).forEach((types) => {
      ['weekday', 'weekend'].forEach((t) => {
        const wg = types[t] || {};
        Object.values(wg).forEach((batches) => {
          Object.values(batches).forEach((b) => {
            b.items = sortByStartTime(b.items);
          });
        });
      });
    });

    return courses;
  }, [rows, batches, selectedDate]);

  // (debugPerms logging removed as per request)

  // Helper: build merged groups (weekday + weekend) for a course
  const buildMergedGroups = (types) => {
    const merged = {};
    const mergeInto = (src) => {
      Object.entries(src || {}).forEach(([gk, batches]) => {
        if (!merged[gk]) merged[gk] = {};
        Object.entries(batches).forEach(([bk, val]) => {
          merged[gk][bk] = val;
        });
      });
    };
    mergeInto((types || {}).weekday || {});
    mergeInto((types || {}).weekend || {});
    return merged;
  };

  // Helper: collect merged (API + staged) items for a batch and sort/dedup
  const collectBatchItems = (b) => {
    const items = [];
    
    // Get staged items first to check for edited items
    const stagedArr = stagedByBatch[b.batchId || -1] || [];
    
    // Find which batchSubjectIds are being replaced by edits
    const hideSubjects = new Set(
      stagedArr
        .filter((s) => s.mode === 'edit' && !!s.replaceBatchSubjectId)
        .map((s) => s.replaceBatchSubjectId)
    );
    
    // API items (exclude those being edited)
    (b.items || []).forEach((it) => {
      // Skip if this item is being replaced by an edit
      if (hideSubjects.has(it.batchSubjectId || -1)) {
        return;
      }
      const [hh, mm] = (it.startTime || '00:00').split(':').map((n) => parseInt(String(n), 10));
      items.push({
        subject: it.subjectShortName || it.subjectFullName || '',
        start: to12h(it.startTime),
        end: to12h(it.endTime),
        startMin: (hh || 0) * 60 + (mm || 0),
        room: it.classroomName || '',
        staff: it.staffName || 'TBD',
        groupChips: (it.subjectGroupNames ? String(it.subjectGroupNames).split(',').map((x) => x.trim()).filter(Boolean) : []),
        scheduleLabel: it.scheduleBatchGroupName ? String(it.scheduleBatchGroupName).trim() : undefined,
        batchSubjectId: it.batchSubjectId,
        batchGroupId: it.batchGroupId ?? null,
      });
    });
    
    // Staged items
    stagedArr.forEach((s) => {
      items.push({
        subject: s.subject || '',
        start: s.start,
        end: s.end,
        startMin: s.startMin || 0,
        room: s.room || '',
        staff: s.lecturer || 'TBD',
        groupChips: s.groupChips || [],
        scheduleLabel: undefined,
        batchSubjectId: s.batchSubjectId,
        batchGroupId: s.batchGroupId ?? null,
      });
    });
    
    // Dedup by time + hall + lecturer (ignore subject duplicates)
    const seen = new Set();
    const deduped = [];
    for (const it of items) {
      const key = [
        (it.start || '').trim(),
        (it.end || '').trim(),
        (it.room || '').trim().toLowerCase(),
        (it.staff || '').trim().toLowerCase(),
      ].join('|');
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(it);
      }
    }
    // Sort by start time
    deduped.sort((a, b) => a.startMin - b.startMin);
    return deduped;
  };

  const batchGroupNamesByBatchId = useMemo(() => {
    const map = new Map();
    (batchGroupBatches || []).forEach((entry) => {
      if (!entry) return;
      const batchIdRaw = entry.batchId;
      const groupIdRaw = entry.batchGroupId;
      if (batchIdRaw === null || batchIdRaw === undefined || groupIdRaw === null || groupIdRaw === undefined) return;
      const batchIdNum = Number(batchIdRaw);
      if (!Number.isFinite(batchIdNum)) return;
      const groupInfo = batchGroupInfo[String(groupIdRaw)];
      const groupName = groupInfo && typeof groupInfo.name === 'string' ? groupInfo.name : '';
      if (!groupName) return;
      const existing = map.get(batchIdNum) || [];
      if (!existing.includes(groupName)) {
        map.set(batchIdNum, [...existing, groupName]);
      }
    });
    return map;
  }, [batchGroupBatches, batchGroupInfo]);

  // Helper: filter batches and items based on search query
  const filterBySearch = (groupName, batch, query) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase().trim();
    // Check group name
    if ((groupName || '').toLowerCase().includes(q)) return true;
    // Check batch name
    if ((batch.batchInternalName || '').toLowerCase().includes(q)) return true;
    // Check items
    const items = collectBatchItems(batch);
    return items.some((it) => 
      (it.subject || '').toLowerCase().includes(q) ||
      (it.staff || '').toLowerCase().includes(q) ||
      (it.room || '').toLowerCase().includes(q)
    );
  };

  // Derived: course options for filter dropdown
  // Show only courses that the logged-in user coordinates (editable),
  // while leaving the main timetable view unfiltered when no course is selected.
  const courseOptions = Object.keys(groupedByCourse)
    .filter((title) => canEditCourse(title))
    .map((k) => ({ value: k, label: k }));

  const computeDurationFromStartEnd = (start, end) => {
    if (!start || !end) return null;
    const [sh, sm] = start.split(':').map((n) => parseInt(n, 10));
    const [eh, em] = end.split(':').map((n) => parseInt(n, 10));
    const s = (sh || 0) * 60 + (sm || 0);
    const e = (eh || 0) * 60 + (em || 0);
    let d = e - s;
    if (d < 0) d += 24 * 60; // handle crossing midnight just in case
    return d;
  };

  const openCellModal = (
    courseTitle,
    groupName,
    batchName,
    slotIndex,
    batchId,
    slot
  ) => {
    setModalContext({ courseTitle, groupName, batchInternalName: batchName, batchId, slotIndex });
    // Reset duplicate configuration every time the modal opens
    setDuplicateEnabled(false);
    setDuplicateDateInput('');
    setDuplicateSelectedDates([]);
    if (slot && slot.batchSubjectId) {
      const start24 = slot.start24 || '';
      const end24 = slot.end24 || '';
      const duration = computeDurationFromStartEnd(start24, end24);
      setModalTime({ start: start24, end: end24 });
      setModalDurationMin(duration);
      setSelectedRoomId(slot.classroomId ?? null);
      setSelectedBatchSubjectId(slot.batchSubjectId);
      setSelectedSubjectInfo({ short: slot.subject || undefined, full: slot.subject || undefined, chips: slot.groupChips || [] });
      if (batchId && slot.source === 'api') {
        setEditing({
          type: 'api',
          batchId,
          batchSubjectId: slot.batchSubjectId,
          scheduleId: slot.id,
          originalStart24: start24,
          originalEnd24: end24,
          originalClassroomId: slot.classroomId ?? null,
          batchGroupId: slot.batchGroupId ?? null,
        });
      } else if (batchId && slot.source === 'staged' && typeof slot.stagedIndex === 'number' && slot.batchSubjectId) {
        setEditing({
          type: 'staged',
          batchId,
          batchSubjectId: slot.batchSubjectId,
          stagedIndex: slot.stagedIndex,
          batchGroupId: slot.batchGroupId ?? null,
        });
        // Restore any existing duplicate dates from the staged item
        const stagedBatchIdKey = typeof batchId === 'number' ? batchId : (batchId != null ? Number(batchId) : -1);
        const existingStaged = (stagedByBatch[stagedBatchIdKey] || [])[slot.stagedIndex];
        const restoredDates = Array.isArray(existingStaged?.duplicateDates)
          ? existingStaged.duplicateDates.filter(Boolean)
          : [];
        if (restoredDates.length > 0) {
          setDuplicateEnabled(true);
          setDuplicateSelectedDates(restoredDates);
        }
      } else {
        setEditing(null);
      }
    } else {
      setModalTime({ start: '', end: '' });
      setModalDurationMin(null);
      setSelectedRoomId(null);
      setSelectedBatchSubjectId(null);
      setSelectedSubjectInfo(null);
      setEditing(null);
    }
    setModalSubjects(null);
    setModalLoading(true);
    setShowModal(true);
    // Fetch subjects for this batch from backend and group by status (ongoing / not_started / completed)
    if (batchId) {
      axiosInstance
        .get(`/admin/batches/${batchId}/subjects`)
        .then((response) => {
          const data = (response && response.data) || {};
          const subjects = Array.isArray(data.subjects) ? data.subjects : [];
          const grouped = { ongoing: [], not_started: [], completed: [] };

          subjects.forEach((s) => {
            const rawStatus = (s.status || '').toLowerCase();
            let bucket = 'not_started';
            if (rawStatus === 'ongoing') bucket = 'ongoing';
            else if (rawStatus === 'completed') bucket = 'completed';

            const mapped = {
              batchSubjectId: s.id,
              subjectShortName: s.subjectCode,
              subjectFullName: s.subjectName,
              lecturerName: s.staffName,
              semester: s.semester,
              // These fields are optional in the UI; keep undefined when not available
              subjectGroupNames: s.subjectGroupNames,
              avgAttendance: s.avgAttendance,
              avgParticipation: s.avgParticipation,
              totalStudentsInGroup: s.totalStudentsInGroup,
              expectedNumberOfClasses: s.expectedNumberOfClasses,
              scheduledClassesCount: s.scheduledClassesCount,
              balanceClasses: s.balanceClasses,
            };

            grouped[bucket].push(mapped);
          });

          setModalSubjects(grouped);
        })
        .catch(() => {
          setModalSubjects({ ongoing: [], not_started: [], completed: [] });
        })
        .finally(() => {
          setModalLoading(false);
        });
    } else {
      setModalLoading(false);
    }
  };
  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setDuplicateEnabled(false);
    setDuplicateDateInput('');
    setDuplicateSelectedDates([]);
  };

  // Compute end time based on start and duration (in minutes)
  const computeEndFromStartAndDuration = (start, durationMin) => {
    if (!start || !durationMin) return '';
    const [hStr, mStr] = start.split(':');
    const startMin = parseInt(hStr || '0', 10) * 60 + parseInt(mStr || '0', 10);
    const endTotal = (startMin + durationMin) % (24 * 60);
    const eh = Math.floor(endTotal / 60);
    const em = endTotal % 60;
    return `${eh.toString().padStart(2, '0')}:${em.toString().padStart(2, '0')}`;
  };

  const isAllocationValid = useMemo(
    () =>
      !!selectedBatchSubjectId &&
      !!(modalContext && modalContext.batchId) &&
      !!modalTime.start &&
      !!modalDurationMin &&
      !!modalTime.end &&
      !!selectedRoomId,
    [selectedBatchSubjectId, modalContext, modalTime.start, modalDurationMin, modalTime.end, selectedRoomId]
  );

  // Detect room / lecturer conflicts for the current modal selection
  const conflictWarnings = useMemo(() => {
    const conflicts = [];
    if (!modalTime.start || !modalTime.end) return conflicts;

    const parseMin = (t) => {
      if (!t) return null;
      const parts = String(t).split(':');
      if (parts.length < 2) return null;
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    const selStart = parseMin(modalTime.start);
    const selEnd = parseMin(modalTime.end);
    if (selStart === null || selEnd === null || selEnd <= selStart) return conflicts;

    const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart;

    const editingScheduleId = editing?.type === 'api' ? editing.scheduleId : null;
    const editingBatchSubjectId = editing?.batchSubjectId ?? null;
    const editingBatchId = editing?.batchId ?? null;

    // Resolve lecturer name for the selected subject
    let lecturerName = '';
    if (modalSubjects && selectedBatchSubjectId) {
      const allSubjects = [
        ...(modalSubjects.ongoing || []),
        ...(modalSubjects.not_started || []),
        ...(modalSubjects.completed || []),
      ];
      const found = allSubjects.find((s) => s.batchSubjectId === selectedBatchSubjectId);
      lecturerName = (found?.lecturerName || '').trim();
    }

    // Check API rows (already scoped to selectedDate)
    rows.forEach((r) => {
      if (editingScheduleId && r.scheduleId === editingScheduleId) return;
      const rowStart = parseMin((r.startTime || '').slice(0, 5));
      const rowEnd = parseMin((r.endTime || '').slice(0, 5));
      if (rowStart === null || rowEnd === null) return;
      if (!overlaps(selStart, selEnd, rowStart, rowEnd)) return;

      if (selectedRoomId && r.classroomId === selectedRoomId) {
        conflicts.push({
          type: 'room',
          name: r.classroomName || `Room ${selectedRoomId}`,
          time: `${to12h((r.startTime || '').slice(0, 5))} – ${to12h((r.endTime || '').slice(0, 5))}`,
          detail: [r.batchInternalName, r.subjectShortName, r.staffName].filter(Boolean).join(' • '),
        });
      }

      if (lecturerName && r.staffName && r.staffName.trim().toLowerCase() === lecturerName.toLowerCase()) {
        conflicts.push({
          type: 'lecturer',
          name: lecturerName,
          time: `${to12h((r.startTime || '').slice(0, 5))} – ${to12h((r.endTime || '').slice(0, 5))}`,
          detail: [r.batchInternalName, r.subjectShortName, r.classroomName].filter(Boolean).join(' • '),
        });
      }
    });

    // Check staged schedules
    Object.entries(stagedByBatch).forEach(([batchIdStr, arr]) => {
      const batchId = parseInt(batchIdStr, 10);
      arr.forEach((staged) => {
        if (editing?.type === 'staged' && batchId === editingBatchId && staged.batchSubjectId === editingBatchSubjectId) return;
        const stagedStart = parseMin(staged.start24);
        const stagedEnd = parseMin(staged.end24);
        if (stagedStart === null || stagedEnd === null) return;
        if (!overlaps(selStart, selEnd, stagedStart, stagedEnd)) return;

        const stagedRoomObj = rooms.find((rm) => rm.name === staged.room);

        if (selectedRoomId && stagedRoomObj && stagedRoomObj.classroomId === selectedRoomId) {
          conflicts.push({
            type: 'room',
            name: staged.room,
            time: `${staged.start} – ${staged.end}`,
            detail: [staged.subject, staged.lecturer].filter(Boolean).join(' • ') + ' (unsaved)',
          });
        }

        if (lecturerName && staged.lecturer && staged.lecturer.trim().toLowerCase() === lecturerName.toLowerCase()) {
          conflicts.push({
            type: 'lecturer',
            name: lecturerName,
            time: `${staged.start} – ${staged.end}`,
            detail: [staged.subject, staged.room].filter(Boolean).join(' • ') + ' (unsaved)',
          });
        }
      });
    });

    return conflicts;
  }, [selectedRoomId, modalTime.start, modalTime.end, selectedBatchSubjectId, rows, stagedByBatch, editing, rooms, modalSubjects]);

  // Compute batch group propagation targets (batchId + batchSubjectId)
  // using batch_group_subjects metadata loaded on page init.
  const computeGroupPropagationTargets = (primaryBatchId, primaryBatchSubjectId) => {
    if (!primaryBatchSubjectId) return { batchGroupId: null, targets: [] };
    const primaryId = Number(primaryBatchSubjectId);
    const entries = Array.isArray(batchGroupSubjects) ? batchGroupSubjects : [];

    const primaryEntry = entries.find((row) => Number(row.batchSubjectId) === primaryId);
    if (!primaryEntry || !primaryEntry.batchGroupId) {
      return { batchGroupId: null, targets: [] };
    }

    const groupId = Number(primaryEntry.batchGroupId);
    if (!Number.isFinite(groupId)) {
      return { batchGroupId: null, targets: [] };
    }
    const subjectKey = (primaryEntry.subjectName || '').trim().toLowerCase();

    const targets = entries
      .filter(
        (row) =>
          Number(row.batchGroupId) === groupId &&
          (row.subjectName || '').trim().toLowerCase() === subjectKey &&
          row.batchId !== null &&
          row.batchSubjectId !== null
      )
      .map((row) => ({
        batchId: Number(row.batchId),
        batchSubjectId: Number(row.batchSubjectId),
      }));

    const hasPrimary = targets.some(
      (t) => t.batchId === primaryBatchId && t.batchSubjectId === primaryId
    );
    if (!hasPrimary && primaryBatchId && primaryId) {
      targets.push({ batchId: primaryBatchId, batchSubjectId: primaryId });
    }

    return { batchGroupId: groupId, targets };
  };

  // Confirm scheduling: stage locally only (no DB write). Include batch_group_subjects propagation targets.
  const handleConfirmSchedule = async () => {
    if (!isAllocationValid || !selectedBatchSubjectId || !selectedRoomId) return;
    const subjectLabel = (selectedSubjectInfo?.short || selectedSubjectInfo?.full || '').trim();
    const roomLabel = (selectedRoomOption?.label || '').trim();
    const [sh, sm] = (modalTime.start || '00:00').split(':').map((n) => parseInt(n, 10));
    const startMin = (sh || 0) * 60 + (sm || 0);
    
    // Find lecturer name from modalSubjects
    let lecturerName = '';
    if (modalSubjects) {
      const allSubjects = [...modalSubjects.ongoing, ...modalSubjects.not_started, ...modalSubjects.completed];
      const selectedSubject = allSubjects.find((s) => s.batchSubjectId === selectedBatchSubjectId);
      lecturerName = selectedSubject?.lecturerName || '';
    }

    // ── Staff availability check ──────────────────────────────────────────────
    // If the lecturer is on leave, unavailable, off-day or half-day we show a
    // clear warning and let the scheduler decide whether to proceed.
    if (lecturerName) {
      const parseMin = (t) => {
        if (!t) return null;
        const parts = String(t).split(':').map(Number);
        if (parts.length < 2) return null;
        return (parts[0] || 0) * 60 + (parts[1] || 0);
      };
      const schedStart = parseMin(modalTime.start);
      const schedEnd   = parseMin(modalTime.end);
      const nameKey    = lecturerName.trim().toLowerCase();
      const overlapsRange = (aS, aE, bS, bE) => aS < bE && aE > bS;

      const staffWarnings = [];

      // 1. Leave / unavailability records already loaded for the selected date
      const unavailRecords = staffUnavailability.filter(
        (rec) => (rec.staffName || '').trim().toLowerCase() === nameKey
      );
      for (const rec of unavailRecords) {
        if (rec.type === 'leave') {
          staffWarnings.push(
            `🏖️ <strong>On Leave:</strong> ${lecturerName} is on leave on <strong>${selectedDate}</strong>` +
            (rec.reason ? ` &mdash; <em>&ldquo;${rec.reason}&rdquo;</em>` : '') + '.'
          );
        } else if (rec.type === 'unavailability') {
          const uStart = parseMin(rec.startTime);
          const uEnd   = parseMin(rec.endTime);
          if (!rec.startTime && !rec.endTime) {
            staffWarnings.push(
              `⛔ <strong>Unavailable (Full Day):</strong> ${lecturerName} is marked as unavailable for the entire day` +
              (rec.reason ? ` &mdash; <em>&ldquo;${rec.reason}&rdquo;</em>` : '') + '.'
            );
          } else if (uStart !== null && uEnd !== null && schedStart !== null && schedEnd !== null &&
                     overlapsRange(schedStart, schedEnd, uStart, uEnd)) {
            staffWarnings.push(
              `⏰ <strong>Unavailable During Scheduled Time:</strong> ${lecturerName} is marked unavailable from ` +
              `<strong>${to12h(rec.startTime)}</strong> to <strong>${to12h(rec.endTime)}</strong>` +
              (rec.reason ? ` &mdash; <em>&ldquo;${rec.reason}&rdquo;</em>` : '') +
              `. The class (${to12h(modalTime.start)} &ndash; ${to12h(modalTime.end)}) overlaps this period.`
            );
          }
        }
      }

      // 2. Off-day / half-day check via roster (cached per date to avoid repeated calls)
      if (staffRosterCacheRef.current.date !== selectedDate) {
        try {
          const rosterRes = await axiosInstance.get('/admin/staff-schedules', {
            params: { date: selectedDate },
          });
          const staffList = Array.isArray(rosterRes?.data) ? rosterRes.data : [];
          const byName = new Map();
          staffList.forEach((s) => {
            if (s.name) byName.set(s.name.trim().toLowerCase(), s);
          });
          staffRosterCacheRef.current = { date: selectedDate, byName };
        } catch {
          staffRosterCacheRef.current = { date: selectedDate, byName: new Map() };
        }
      }
      const staffInfo = staffRosterCacheRef.current.byName.get(nameKey);
      const roster    = staffInfo?.roster;
      if (roster) {
        if (roster.dayType === 'off day') {
          staffWarnings.push(
            `🚫 <strong>Off Day:</strong> <strong>${roster.rosterDay || 'Today'}</strong> is ${lecturerName}'s designated day off. ` +
            `Scheduling a class on their regular off day.`
          );
        } else if (roster.dayType === 'morning half day') {
          // Works 12:30 PM – 5:00 PM; unavailable before 12:30 PM
          if (schedStart !== null && schedStart < 12 * 60 + 30) {
            staffWarnings.push(
              `🌅 <strong>Morning Half Day:</strong> ${lecturerName} works <strong>12:30 PM – 5:00 PM only</strong> on ${roster.rosterDay || 'this day'}. ` +
              `The class (${to12h(modalTime.start)} &ndash; ${to12h(modalTime.end)}) starts before their working hours begin.`
            );
          }
        } else if (roster.dayType === 'evening half day') {
          // Works 8:30 AM – 1:00 PM; unavailable after 1:00 PM
          if (schedEnd !== null && schedEnd > 13 * 60) {
            staffWarnings.push(
              `🌆 <strong>Evening Half Day:</strong> ${lecturerName} works <strong>8:30 AM – 1:00 PM only</strong> on ${roster.rosterDay || 'this day'}. ` +
              `The class (${to12h(modalTime.start)} &ndash; ${to12h(modalTime.end)}) runs beyond their working hours.`
            );
          }
        }
      }

      // Show Swal warning if any issues were detected
      if (staffWarnings.length > 0) {
        const warningList = staffWarnings
          .map((w) => `<li style="margin-bottom:10px;padding-left:4px;">${w}</li>`)
          .join('');
        const result = await Swal.fire({
          icon: 'warning',
          title: '\u26A0\uFE0F Staff Availability Warning',
          html: `<div style="text-align:left;font-size:14px;line-height:1.65;">
            <p style="margin-bottom:12px;">
              The following ${staffWarnings.length === 1 ? 'issue was' : 'issues were'} detected for
              <strong>${lecturerName}</strong> on <strong>${selectedDate}</strong>:
            </p>
            <ul style="padding-left:0;list-style:none;margin:0 0 14px 0;border-left:3px solid #f59e0b;padding-left:12px;">
              ${warningList}
            </ul>
            <p style="color:#6b7280;font-size:13px;margin:0;">
              In critical situations a class can still be scheduled.<br/>
              Press <strong>Schedule Anyway</strong> to proceed, or <strong>Cancel</strong> to go back.
            </p>
          </div>`,
          showCancelButton: true,
          confirmButtonText: 'Schedule Anyway',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#d97706',
          cancelButtonColor: '#6b7280',
          reverseButtons: true,
          width: 560,
        });
        if (!result.isConfirmed) return;
      }
    }
    // ── End staff availability check ──────────────────────────────────────────

    const primaryBatchId = modalContext && modalContext.batchId ? modalContext.batchId : null;
    const propagation = computeGroupPropagationTargets(primaryBatchId, selectedBatchSubjectId) || { batchGroupId: null, targets: [] };
    const propagationTargets = propagation.targets || [];
    const normalizeGroupId = (value) => {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const resolvedBatchGroupId = normalizeGroupId(propagation.batchGroupId) ?? normalizeGroupId(editing?.batchGroupId);

    const baseItem = {
      subject: subjectLabel,
      room: roomLabel,
      start: to12h(modalTime.start),
      end: to12h(modalTime.end),
      start24: modalTime.start,
      end24: modalTime.end,
      startMin,
      lecturer: lecturerName,
      groupLabel: undefined,
      groupChips: selectedSubjectInfo?.chips || [],
      batchGroupId: resolvedBatchGroupId ?? null,
    };
    try {
      // Collect duplicate dates — for new schedules AND staged-item edits (not API edits)
      const duplicateDates = duplicateEnabled && (!editing || editing?.type === 'staged') && Array.isArray(duplicateSelectedDates)
        ? duplicateSelectedDates.filter((d) => d && d !== selectedDate)
        : [];

      // For editing existing API schedules, try to locate the corresponding
      // scheduleId for each (batchId, batchSubjectId) target based on the
      // original time + classroom of the clicked schedule.
      const scheduleIdByBatchSubjectId = new Map();
      if (editing && editing.type === 'api' && editing.scheduleId) {
        const primaryId = Number(editing.batchSubjectId);
        scheduleIdByBatchSubjectId.set(primaryId, editing.scheduleId);

        const originalStart24 = (editing.originalStart24 || '').slice(0, 5);
        const originalEnd24 = (editing.originalEnd24 || '').slice(0, 5);
        const originalClassroomId = editing.originalClassroomId ?? null;

        propagationTargets.forEach((t) => {
          const targetBatchSubjectId = Number(t.batchSubjectId);
          if (scheduleIdByBatchSubjectId.has(targetBatchSubjectId)) return;

          const match = rows.find((r) => {
            if (Number(r.batchId) !== Number(t.batchId) || Number(r.batchSubjectId) !== targetBatchSubjectId) {
              return false;
            }
            const rStart = (r.startTime || '').slice(0, 5);
            const rEnd = (r.endTime || '').slice(0, 5);
            const rClassroomId = r.classroomId ?? null;
            const matchTime = (!originalStart24 || rStart === originalStart24) && (!originalEnd24 || rEnd === originalEnd24);
            const matchRoom = originalClassroomId === null || rClassroomId === originalClassroomId;
            return matchTime && matchRoom && r.scheduleId;
          });

          if (match && match.scheduleId) {
            scheduleIdByBatchSubjectId.set(targetBatchSubjectId, match.scheduleId);
          }
        });
      }

      // Stage the primary-date schedule locally (saved to DB when user clicks "Save all")
      // duplicateDates is intentionally [] here because duplicates were already saved above.
      setStagedByBatch((prev) => {
        const copy = { ...prev };
        const apply = (batchId, batchSubjectId, mode, batchGroupIdOverride = null) => {
          const arr = copy[batchId] ? [...copy[batchId]] : [];
          // If editing, remove any prior staged replacement for this batchSubjectId
          const filtered = mode === 'edit' ? arr.filter((x) => x.replaceBatchSubjectId !== batchSubjectId) : arr;
          const scheduleIdForEdit =
            mode === 'edit' && editing && editing.type === 'api'
              ? scheduleIdByBatchSubjectId.get(Number(batchSubjectId)) || null
              : mode === 'edit' && editing && editing.type === 'staged'
                ? null
                : null;
          filtered.push({
            ...baseItem,
            batchSubjectId,
            classroomId: selectedRoomId,
            batchGroupId: batchGroupIdOverride ?? resolvedBatchGroupId ?? null,
            mode,
            replaceBatchSubjectId: mode === 'edit' ? batchSubjectId : undefined,
            scheduleId: mode === 'edit' ? scheduleIdForEdit : undefined,
            // duplicateDates are staged and saved when user clicks "Save all"
            duplicateDates,
          });
          copy[batchId] = filtered;
        };
        if (editing && editing.type === 'api') {
          // Editing an existing API schedule: stage replacements and hide originals in UI
          if (propagationTargets.length > 0) {
            propagationTargets.forEach((t) => apply(t.batchId, t.batchSubjectId, 'edit', resolvedBatchGroupId));
          } else if (modalContext?.batchId) {
            apply(modalContext.batchId, selectedBatchSubjectId, 'edit', resolvedBatchGroupId);
          }
        } else if (editing && editing.type === 'staged') {
          // Editing a staged item: replace the staged instances across propagation targets
          const toRemove = new Set(
            propagationTargets.length > 0
              ? propagationTargets.map((t) => t.batchSubjectId)
              : modalContext?.batchId
                ? [selectedBatchSubjectId]
                : []
          );
          Object.keys(copy).forEach((bk) => {
            const bId = Number(bk);
            copy[bId] = (copy[bId] || []).filter((it) => !toRemove.has(it.batchSubjectId));
          });
          if (propagationTargets.length > 0) {
            propagationTargets.forEach((t) => apply(t.batchId, t.batchSubjectId, 'create', resolvedBatchGroupId));
          } else if (modalContext?.batchId) {
            apply(modalContext.batchId, selectedBatchSubjectId, 'create', resolvedBatchGroupId);
          }
        } else {
          // Creating a new schedule from empty cell
          if (propagationTargets.length > 0) {
            propagationTargets.forEach((t) => apply(t.batchId, t.batchSubjectId, 'create', resolvedBatchGroupId));
          } else if (modalContext?.batchId) {
            apply(modalContext.batchId, selectedBatchSubjectId, 'create', resolvedBatchGroupId);
          }
        }
        return copy;
      });
    } finally {
      setShowModal(false);
      setEditing(null);
    }
  };

  // Validate and add a duplicate date: check for clashes on room and lecturer for that date
  const handleAddDuplicateDate = async () => {
    if (!duplicateDateInput) return;

    const dateStr = duplicateDateInput;

    // Enforce same basic rules as before
    if (minDuplicateDate && dateStr < minDuplicateDate) {
      await Swal.fire({
        icon: 'warning',
        title: 'Invalid date',
        text: 'You can only duplicate schedules to future dates after the selected day.',
      });
      return;
    }

    if (dateStr === selectedDate) {
      await Swal.fire({
        icon: 'info',
        title: 'Same date selected',
        text: 'The schedule is already being created for this date. Please pick a different date to duplicate.',
      });
      return;
    }

    if (duplicateSelectedDates.includes(dateStr)) {
      await Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: 'This date is already added',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });
      return;
    }

    // Enforce weekday / weekend constraint
    const isCurrentWeekend = isWeekendDate(selectedDate);
    const isTargetWeekend = isWeekendDate(dateStr);
    if (isCurrentWeekend !== isTargetWeekend) {
      await Swal.fire({
        icon: 'warning',
        title: 'Date type mismatch',
        text: isCurrentWeekend
          ? 'This is a weekend course. Please select only weekend dates (Saturday or Sunday) to duplicate.'
          : 'This is a weekday course. Please select only weekday dates (Monday – Friday) to duplicate.',
      });
      return;
    }

    // Safety: ensure current schedule details are complete
    if (!isAllocationValid || !selectedRoomId || !modalTime.start || !modalTime.end) {
      await Swal.fire({
        icon: 'info',
        title: 'Complete schedule first',
        text: 'Please select subject, start time, duration and room before adding duplicate dates.',
      });
      return;
    }

    // Get lecturer name for the selected subject
    let lecturerName = '';
    if (modalSubjects) {
      const allSubjects = [
        ...(modalSubjects.ongoing || []),
        ...(modalSubjects.not_started || []),
        ...(modalSubjects.completed || []),
      ];
      const selectedSubject = allSubjects.find((s) => s.batchSubjectId === selectedBatchSubjectId);
      lecturerName = (selectedSubject && selectedSubject.lecturerName) || '';
    }

    // Fetch existing schedules for the target date from backend
    let existing = [];
    try {
      const resp = await axiosInstance.get('/admin/attendance/schedules', { params: { date: dateStr } });
      const data = resp && resp.data;
      if (Array.isArray(data)) existing = data;
      else if (data) existing = data;
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Unable to check schedules',
        text: 'Could not check existing schedules for this date. Please try again in a moment.',
      });
      return;
    }

    const timeToMinutes = (val) => {
      if (!val) return null;
      const parts = String(val).split(':');
      if (parts.length < 2) return null;
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    const newStart = timeToMinutes(modalTime.start);
    const newEnd = timeToMinutes(modalTime.end);
    if (newStart === null || newEnd === null || newEnd <= newStart) {
      await Swal.fire({
        icon: 'error',
        title: 'Invalid time range',
        text: 'Please check the start and end time before adding duplicate dates.',
      });
      return;
    }

    const roomConflicts = [];
    const lecturerConflicts = [];
    const lecturerKey = (lecturerName || '').trim().toLowerCase();

    existing.forEach((r) => {
      const rStart = timeToMinutes((r.startTime || '').slice(0, 5));
      const rEnd = timeToMinutes((r.endTime || '').slice(0, 5));
      if (rStart === null || rEnd === null) return;

      const overlap = !(newEnd <= rStart || newStart >= rEnd);
      if (!overlap) return;

      // Same room clash
      if (r.classroomId != null && Number(r.classroomId) === Number(selectedRoomId)) {
        roomConflicts.push(r);
      }

      // Same lecturer clash
      if (lecturerKey) {
        const rLecturer = (r.staffName || '').trim().toLowerCase();
        if (rLecturer && rLecturer === lecturerKey) {
          lecturerConflicts.push(r);
        }
      }
    });

    if (roomConflicts.length || lecturerConflicts.length) {
      const totalConflicts = roomConflicts.length + lecturerConflicts.length;
      const requestedTime = `${to12h(modalTime.start)} – ${to12h(modalTime.end)}`;

      let roomBlock = '';
      if (roomConflicts.length) {
        const roomName = roomConflicts[0].classroomName || 'TBD';
        const rows = roomConflicts
          .map(
            (c) =>
              `<tr>
                <td style="padding:4px 10px 4px 0;color:#6b7280;white-space:nowrap;">⏰</td>
                <td style="padding:4px 0;text-align:left;">
                  <span style="font-weight:600;color:#111827;">${to12h((c.startTime || '').slice(0,5))} – ${to12h((c.endTime || '').slice(0,5))}</span>
                  <span style="color:#6b7280;font-size:11px;margin-left:6px;">(${c.batchInternalName || c.courseName || 'another class'})</span>
                </td>
              </tr>`
          )
          .join('');
        roomBlock = `
          <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:12px 14px;margin-bottom:10px;text-align:left;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
              <span style="font-size:16px;">🏫</span>
              <span style="font-weight:700;color:#9a3412;font-size:13px;">Hall Conflict</span>
              <span style="background:#ea580c;color:#fff;border-radius:999px;padding:1px 8px;font-size:11px;font-weight:700;margin-left:auto;">${roomConflicts.length} clash${roomConflicts.length > 1 ? 'es' : ''}</span>
            </div>
            <div style="font-size:12px;color:#374151;margin-bottom:6px;">
              <b>${roomName}</b> is already booked during your requested time:
            </div>
            <table style="width:100%;font-size:12px;border-collapse:collapse;">${rows}</table>
          </div>`;
      }

      let lecturerBlock = '';
      if (lecturerConflicts.length) {
        const lecName = lecturerConflicts[0].staffName || 'TBD';
        const rows = lecturerConflicts
          .map(
            (c) =>
              `<tr>
                <td style="padding:4px 10px 4px 0;color:#6b7280;white-space:nowrap;">⏰</td>
                <td style="padding:4px 0;text-align:left;">
                  <span style="font-weight:600;color:#111827;">${to12h((c.startTime || '').slice(0,5))} – ${to12h((c.endTime || '').slice(0,5))}</span>
                  <span style="color:#6b7280;font-size:11px;margin-left:6px;">(${c.batchInternalName || c.courseName || 'another class'})</span>
                </td>
              </tr>`
          )
          .join('');
        lecturerBlock = `
          <div style="background:#fdf4ff;border:1px solid #e9d5ff;border-radius:8px;padding:12px 14px;margin-bottom:10px;text-align:left;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
              <span style="font-size:16px;">👨‍🏫</span>
              <span style="font-weight:700;color:#6b21a8;font-size:13px;">Lecturer Conflict</span>
              <span style="background:#7c3aed;color:#fff;border-radius:999px;padding:1px 8px;font-size:11px;font-weight:700;margin-left:auto;">${lecturerConflicts.length} clash${lecturerConflicts.length > 1 ? 'es' : ''}</span>
            </div>
            <div style="font-size:12px;color:#374151;margin-bottom:6px;">
              <b>${lecName}</b> already has a scheduled class during your requested time:
            </div>
            <table style="width:100%;font-size:12px;border-collapse:collapse;">${rows}</table>
          </div>`;
      }

      await Swal.fire({
        icon: 'warning',
        title: `⚠️ ${totalConflicts} Schedule Conflict${totalConflicts > 1 ? 's' : ''} Found`,
        html: `
          <div style="font-size:12px;color:#6b7280;text-align:center;margin-bottom:14px;">
            Cannot duplicate to <span style="font-weight:700;color:#111827;background:#f3f4f6;padding:2px 8px;border-radius:4px;">${dateStr}</span>
            &nbsp;·&nbsp; Requested time: <span style="font-weight:700;color:#1d4ed8;">${requestedTime}</span>
          </div>
          ${roomBlock}
          ${lecturerBlock}
          <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px 14px;text-align:left;margin-top:4px;">
            <span style="font-size:15px;">💡</span>
            <span style="font-size:12px;color:#166534;margin-left:6px;">
              Please choose a different time slot, a different hall, or pick another date to duplicate.
            </span>
          </div>`,
        confirmButtonColor: '#4f46e5',
        confirmButtonText: 'Got it',
        width: 480,
      });
      return;
    }

    // No conflicts: add date to list
    setDuplicateSelectedDates((prev) => {
      if (prev.includes(dateStr)) return prev;
      const next = [...prev, dateStr];
      next.sort();
      return next;
    });
  };

  return (
    <DashboardLayout>
      <div className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Timetable</h1>
              <p className="mt-1 text-sm text-gray-600">
                Plan and review the class schedule per date
                {hasUnsavedChanges && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 animate-pulse">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                    </svg>
                    {stagedTotal} unsaved {stagedTotal === 1 ? 'change' : 'changes'}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <label className="mb-1 text-xs font-semibold text-gray-700">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="rounded-lg border-2 border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/20"
                />
              </div>
              <div className="w-72">
                <label className="mb-1 block text-xs font-semibold text-gray-700">Course</label>
                <Select
                  options={courseOptions}
                  isClearable
                  placeholder="All courses"
                  value={courseFilter ? { value: courseFilter, label: courseFilter } : null}
                  onChange={(opt) => setCourseFilter(opt ? opt.value : null)}
                  classNamePrefix="react-select"
                />
              </div>
              <div className="flex items-end gap-2">
                <div className="relative">
                  <button
                    onClick={handleSaveAll}
                    disabled={stagedTotal === 0}
                    className={`rounded-md px-4 py-2 text-sm font-semibold text-white ${stagedTotal > 0 ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'}`}
                    title={stagedTotal > 0 ? `Save ${stagedTotal} staged ${stagedTotal === 1 ? 'schedule' : 'schedules'}` : 'No staged schedules to save'}
                  >
                    Save all
                  </button>
                  {stagedTotal > 0 && (
                    <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-600 rounded-full border-2 border-white shadow-lg animate-pulse">
                      {stagedTotal}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => { setSummaryTab('classes'); setShowSummaryModal(true); }}
                  className="rounded-md px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
                  title="Copy daily summary"
                >
                  Summary
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Informational banner about course permissions (compact + chip-based) */}
            {getEditableCourseNames().length > 0 && (() => {
              const editable = getEditableCourseNames();
              const visible = showAllEditableCourses ? editable : editable.slice(0, 4);
              const remaining = Math.max(0, editable.length - visible.length);
              return (
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-blue-900">Course Editing Permissions</h3>
                        <span className="text-xs text-blue-700">{editable.length} editable course{editable.length === 1 ? '' : 's'}</span>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2">
                        {visible.map((name) => (
                          <span key={name} className="inline-flex items-center rounded-full bg-white/80 border border-blue-200 px-2 py-0.5 text-xs text-blue-900" title={name}>
                            {name}
                          </span>
                        ))}
                        {remaining > 0 && (
                          <button
                            type="button"
                            className="text-xs font-medium text-blue-700 hover:text-blue-900 underline"
                            onClick={() => setShowAllEditableCourses((v) => !v)}
                          >
                            {showAllEditableCourses ? 'Show less' : `+${remaining} more`}
                          </button>
                        )}
                      </div>

                      <div className="mt-2 text-xs text-blue-700 flex flex-wrap items-center gap-4">
                        <span>Legend:</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                          Editable
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-400 px-2 py-0.5 text-[10px] font-semibold text-white">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                          View Only
                        </span>
                        <span className="text-[11px] text-blue-700">Double‑click works only on editable courses</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Timetable grouped by Course, separated into Weekday and Weekend */}
            <div className="space-y-10">
              {/* Grouped tables by Course with loading overlay */}
              <div className="relative">
                {loading && (
                  <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 backdrop-blur-sm pt-8" aria-busy="true" aria-live="polite" role="status">
                    <div className="h-10 w-10 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" aria-label="Loading" />
                  </div>
                )}
                {/* Removed "No schedules for this date" empty-state banner as requested */}

              {!loading && Object.entries(groupedByCourse)
                .filter(([name]) => !courseFilter || name === courseFilter)
                .sort(([titleA], [titleB]) => {
                  // Sort coordinated courses first
                  const aCoordinated = canEditCourse(titleA);
                  const bCoordinated = canEditCourse(titleB);
                  if (aCoordinated && !bCoordinated) return -1;
                  if (!aCoordinated && bCoordinated) return 1;
                  return titleA.localeCompare(titleB);
                })
                .map(([courseTitle, types]) => {
                // Check if this course is editable by logged user
                const isEditable = canEditCourse(courseTitle);
                // Merge weekday and weekend into one WhatsApp grouping (single table per course)
                const mergedGroups = {};
                const mergeInto = (src) => {
                  Object.entries(src).forEach(([gk, batches]) => {
                    if (!mergedGroups[gk]) mergedGroups[gk] = {};
                    Object.entries(batches).forEach(([bk, val]) => {
                      mergedGroups[gk][bk] = val;
                    });
                  });
                };
                mergeInto((types && types.weekday) || {});
                mergeInto((types && types.weekend) || {});

                const groupEntries = Object.entries(mergedGroups);
                if (groupEntries.length === 0) return null;

                // Clipboard copy helper in requested format
                const copyGroup = async (groupName) => {
                  const batches = mergedGroups[groupName];
                  // Build combined list from API + staged across all batches in this group
                  const itemsForCopy = [];

                  // API-backed items
                  Object.values(batches).forEach((b) => {
                    (b.items || []).forEach((it) => {
                      const [hh, mm] = (it.startTime || '00:00').split(':').map((n) => parseInt(n, 10));
                      itemsForCopy.push({
                        subject: it.subjectShortName || it.subjectFullName || '',
                        start: to12h(it.startTime),
                        end: to12h(it.endTime),
                        startMin: (hh || 0) * 60 + (mm || 0),
                        room: it.classroomName || '',
                        staff: it.staffName || 'TBD',
                        groupChips: (it && it.subjectGroupNames ? String(it.subjectGroupNames).split(',').map((x) => x.trim()).filter(Boolean) : []),
                        batchId: b.batchId || null,
                        batchSubjectId: it.batchSubjectId || null,
                      });
                    });
                  });

                  // Staged items
                  Object.values(batches).forEach((b) => {
                    const stagedArr = stagedByBatch[b.batchId || -1] || [];
                    stagedArr.forEach((s) => {
                      itemsForCopy.push({
                        subject: s.subject || '',
                        start: s.start,
                        end: s.end,
                        startMin: s.startMin || 0,
                        room: s.room || '',
                        staff: s.lecturer || 'TBD',
                        groupChips: s.groupChips || [],
                        batchId: b.batchId || null,
                        batchSubjectId: s.batchSubjectId || null,
                      });
                    });
                  });

                  // Prepare lookup of subject-level plan/schedule stats
                  const toNumOrNull = (val) => {
                    if (val === null || val === undefined || val === '') return null;
                    const n = Number(val);
                    return Number.isNaN(n) ? null : n;
                  };

                  // subjectKey -> Set of "batchId:batchSubjectId" pairs
                  const subjectTargets = new Map();
                  const batchIdsToFetch = new Set();
                  itemsForCopy.forEach((it) => {
                    const subjectKey = (it.subject || '').trim().toLowerCase();
                    if (!subjectKey || !it.batchId || !it.batchSubjectId) return;
                    batchIdsToFetch.add(it.batchId);
                    let set = subjectTargets.get(subjectKey);
                    if (!set) {
                      set = new Set();
                      subjectTargets.set(subjectKey, set);
                    }
                    set.add(`${it.batchId}:${it.batchSubjectId}`);
                  });

                  // Special handling for Level 5 HND in Computing:
                  // For each subject:
                  // - If it appears in multiple batches (e.g. SE + NE),
                  //   prefer the Software Eng batch; if none, prefer Network;
                  // - If it appears in only one batch, use that batch.
                  if (courseTitle === 'Level 5 Pearson BTEC HND in Computing' && batchIdsToFetch.size > 1) {
                    const batchArray = Object.values(batches || {});
                    const toLower = (v) => String(v || '').toLowerCase();
                    const hasToken = (b, token) => {
                      const internal = toLower(b.batchInternalName);
                      const whats = toLower(b.whatsappGroupName);
                      return internal.includes(token) || whats.includes(token);
                    };

                    const batchMetaById = new Map();
                    batchArray.forEach((b) => {
                      if (b && b.batchId) {
                        batchMetaById.set(String(b.batchId), b);
                      }
                    });

                    // Re-select batches per subject based on the preference rules
                    for (const [subjectKey, pairSet] of subjectTargets.entries()) {
                      const pairsByBatchId = new Map();
                      pairSet.forEach((pair) => {
                        const [batchIdStr] = pair.split(':');
                        if (!pairsByBatchId.has(batchIdStr)) {
                          pairsByBatchId.set(batchIdStr, new Set());
                        }
                        pairsByBatchId.get(batchIdStr).add(pair);
                      });

                      const batchIdsForSubject = Array.from(pairsByBatchId.keys());
                      if (batchIdsForSubject.length <= 1) {
                        // Subject only comes from one batch; keep as-is
                        continue;
                      }

                      let preferredBatchIdStr = null;

                      // 1) Prefer Software Engineering batch for this subject
                      for (const idStr of batchIdsForSubject) {
                        const b = batchMetaById.get(idStr);
                        if (!b) continue;
                        if (hasToken(b, 'software') || hasToken(b, ' se') || hasToken(b, '/se') || hasToken(b, 'se/')) {
                          preferredBatchIdStr = idStr;
                          break;
                        }
                      }

                      // 2) Otherwise, prefer Network Engineering batch
                      if (!preferredBatchIdStr) {
                        for (const idStr of batchIdsForSubject) {
                          const b = batchMetaById.get(idStr);
                          if (!b) continue;
                          if (hasToken(b, 'network') || hasToken(b, ' ne') || hasToken(b, '/ne') || hasToken(b, 'ne/')) {
                            preferredBatchIdStr = idStr;
                            break;
                          }
                        }
                      }

                      // 3) Fallback: first batch that has this subject
                      if (!preferredBatchIdStr) {
                        preferredBatchIdStr = batchIdsForSubject[0];
                      }

                      const filteredPairs = pairsByBatchId.get(preferredBatchIdStr) || new Set();
                      subjectTargets.set(subjectKey, filteredPairs);
                    }

                    // Rebuild batchIdsToFetch from the (potentially filtered) subjectTargets
                    batchIdsToFetch.clear();
                    for (const [, pairSet] of subjectTargets.entries()) {
                      pairSet.forEach((pair) => {
                        const [batchIdStr] = pair.split(':');
                        const batchIdNum = Number(batchIdStr);
                        if (Number.isFinite(batchIdNum)) {
                          batchIdsToFetch.add(batchIdNum);
                        }
                      });
                    }
                  }

                  // Fetch subject stats per batch
                  const batchSubjectsByBatchId = new Map();
                  for (const batchId of batchIdsToFetch) {
                    try {
                      const resp = await axiosInstance.get(`/admin/batches/${batchId}/subjects`);
                      const data = (resp && resp.data) || {};
                      const subjects = Array.isArray(data.subjects) ? data.subjects : [];
                      const map = new Map();
                      subjects.forEach((s) => {
                        const id = s.id != null ? Number(s.id) : null;
                        if (!id) return;
                        // Backend already derives expectedNumberOfClasses using batch override
                        // and master subject.numberOfClasses fallback, and also provides
                        // scheduledClassesCount and balanceClasses based on actual schedules.
                        map.set(id, {
                          expectedNumberOfClasses: toNumOrNull(s.expectedNumberOfClasses),
                          scheduledClassesCount: toNumOrNull(s.scheduledClassesCount),
                          balanceClasses: toNumOrNull(s.balanceClasses),
                        });
                      });
                      batchSubjectsByBatchId.set(batchId, map);
                    } catch (err) {
                      // If fetching fails for a batch, just skip stats for its subjects
                      // eslint-disable-next-line no-console
                      console.error('Failed to load subject stats for batch', batchId, err);
                    }
                  }

                  // Aggregate stats per subject label across all underlying batchSubjectIds
                  const subjectStats = new Map(); // subjectKey -> { expectedClasses, scheduledClasses, balanceClasses }
                  for (const [subjectKey, pairSet] of subjectTargets.entries()) {
                    // To stay consistent with Delivery, treat each underlying
                    // batchSubjectId independently rather than summing across
                    // the group, then approximate classes from hours when needed.
                    let expectedTotal = null;
                    let scheduledTotal = null;

                    pairSet.forEach((pair) => {
                      const [batchIdStr, batchSubjectIdStr] = pair.split(':');
                      const batchIdNum = Number(batchIdStr);
                      const batchSubjectIdNum = Number(batchSubjectIdStr);
                      if (!Number.isFinite(batchIdNum) || !Number.isFinite(batchSubjectIdNum)) return;
                      const bySubject = batchSubjectsByBatchId.get(batchIdNum);
                      if (!bySubject) return;
                      const meta = bySubject.get(batchSubjectIdNum);
                      if (!meta) return;

                      if (meta.expectedNumberOfClasses !== null) {
                        expectedTotal = (expectedTotal === null ? 0 : expectedTotal) + meta.expectedNumberOfClasses;
                      }
                      if (meta.scheduledClassesCount !== null) {
                        scheduledTotal = (scheduledTotal === null ? 0 : scheduledTotal) + meta.scheduledClassesCount;
                      }
                    });

                    // If we have an expected total but no scheduled data yet,
                    // treat scheduled as 0 so that Pending Classes still show
                    // up instead of being suppressed entirely.
                    const balanceTotal = expectedTotal !== null
                      ? expectedTotal - (scheduledTotal !== null ? scheduledTotal : 0)
                      : null;

                    subjectStats.set(subjectKey, {
                      expectedClasses: expectedTotal,
                      scheduledClasses: scheduledTotal,
                      balanceClasses: balanceTotal,
                    });
                  }

                  // Remove duplicates when time + hall + lecturer are the same
                  const seen = new Set();
                  const deduped = [];
                  for (const it of itemsForCopy) {
                    const key = [
                      (it.start || '').trim(),
                      (it.end || '').trim(),
                      (it.room || '').trim().toLowerCase(),
                      (it.staff || '').trim().toLowerCase(),
                    ].join('|');
                    if (!seen.has(key)) {
                      seen.add(key);
                      const subjectKey = (it.subject || '').trim().toLowerCase();
                      deduped.push({ ...it, subjectKey });
                    }
                  }

                  // Sort with SE-first, then by start time
                  const hasSE = (chips, subject) => {
                    if (chips && chips.length > 0) {
                      return chips.some((c) => c.toUpperCase().includes('SE'));
                    }
                    return (subject || '').toUpperCase().includes(' SE') || (subject || '').toUpperCase().startsWith('SE');
                  };
                  deduped.sort((a, b) => {
                    const aSE = hasSE(a.groupChips, a.subject);
                    const bSE = hasSE(b.groupChips, b.subject);
                    if (aSE !== bSE) return aSE ? -1 : 1;
                    return a.startMin - b.startMin;
                  });

                  // Format date: Weekday-Month DD, YYYY
                  const d = new Date(`${selectedDate}T00:00:00`);
                  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
                  const month = d.toLocaleDateString(undefined, { month: 'long' });
                  const day = d.getDate();
                  const year = d.getFullYear();
                  const dateStr = `${weekday}-${month} ${day}, ${year}`;

                  const lines = [];
                  lines.push(`*${groupName}* on *${dateStr}*:`);
                  lines.push('');
                  const HOURS_PER_CLASS = 2;
                  deduped.forEach((it) => {
                    lines.push(`*${it.subject}*`);
                    lines.push(`Time : *${it.start} - ${it.end}*`);
                    lines.push(`Hall : *${it.room || 'TBD'}*`);
                    lines.push(`Lec : *${it.staff}*`);

                    const stats = it.subjectKey ? subjectStats.get(it.subjectKey) : null;
                    if (stats && stats.expectedClasses !== null) {
                      const expectedClasses = stats.expectedClasses;
                      const expectedHours = expectedClasses * HOURS_PER_CLASS;
                      const scheduledClasses = stats.scheduledClasses !== null ? stats.scheduledClasses : null;
                      const scheduledHours = scheduledClasses !== null ? scheduledClasses * HOURS_PER_CLASS : null;
                      const balanceClasses = stats.balanceClasses !== null ? stats.balanceClasses : null;

                      // Derive pending vs extra based on balance
                      let pendingClasses = null;
                      let pendingHours = null;
                      let extraClasses = null;
                      let extraHours = null;

                      if (balanceClasses !== null) {
                        const roundedBalance = Math.round(balanceClasses * 10) / 10;
                        if (roundedBalance > 0) {
                          // Still have work remaining
                          pendingClasses = roundedBalance;
                          pendingHours = Math.max(0, Math.round((expectedHours - (scheduledHours || 0)) * 10) / 10);
                        } else if (roundedBalance < 0) {
                          // Exceeded the plan: show extra instead of pending 0
                          extraClasses = Math.abs(roundedBalance);
                          if (scheduledHours !== null) {
                            extraHours = Math.max(0, Math.round((scheduledHours - expectedHours) * 10) / 10);
                          } else {
                            extraHours = extraClasses * HOURS_PER_CLASS;
                          }
                        }
                      }

                      if (scheduledClasses !== null) {
                        lines.push(`Scheduled Classes :  *${scheduledClasses} Cls (${scheduledHours} hrs)*`);
                      }

                      if (pendingClasses !== null && pendingClasses > 0) {
                        lines.push(`Pending Classes : *${pendingClasses} Cls (${pendingHours} hrs)*`);
                      }

                      if (extraClasses !== null && extraClasses > 0) {
                        lines.push(`*Over Scheduled* Classes : *${extraClasses} Cls (${extraHours} hrs)*`);
                      }
                    }

                    lines.push('');
                  });

                  try {
                    await navigator.clipboard.writeText(lines.join('\n'));
                    // Visual toast
                    await Swal.fire({
                      toast: true,
                      position: 'top-end',
                      icon: 'success',
                      title: 'Copied!',
                      showConfirmButton: false,
                      timer: 1500,
                      timerProgressBar: true,
                    });
                    // Mark this group's rows as copied until reset
                    setCopiedGroups((prev) => ({ ...prev, [groupName]: true }));
                  } catch (err) {
                    await Swal.fire({
                      icon: 'error',
                      title: 'Copy failed',
                      text: (err && err.message) || 'Unable to copy to clipboard.',
                    });
                  }
                };

                const cellBadge = (text, colorSeed) => (
                  <span
                    className="inline-block rounded-md px-2 py-1 text-[10px] font-semibold text-white"
                    style={{ backgroundColor: toBadgeColor(colorSeed || text) }}
                  >
                    {text}
                  </span>
                );

                const renderSlot = (
                  groupName,
                  batchName,
                  slotIndex,
                  slot,
                  batchId
                ) => {
                const resolvedBatchId =
                  typeof batchId === 'number'
                    ? batchId
                    : batchId !== null && batchId !== undefined && !Number.isNaN(Number(batchId))
                      ? Number(batchId)
                      : null;
                const batchGroupNamesRaw = resolvedBatchId !== null ? batchGroupNamesByBatchId.get(resolvedBatchId) : undefined;
                const batchGroupNames = Array.isArray(batchGroupNamesRaw) ? batchGroupNamesRaw : [];

                // Compute slot key for dup/conflict lookup
                const slotKey = slot
                  ? (slot.source === 'api'
                    ? `api:${slot.id}`
                    : slot.source === 'staged' && typeof slot.batchIdForStaged === 'number'
                      ? `staged:${slot.batchIdForStaged}:${slot.stagedIndex}`
                      : null)
                  : null;
                const dupBg = slotKey ? slotDupColor.get(slotKey) : null;
                const conflictReasons = slotKey ? (slotConflictReasons.get(slotKey) || null) : null;
                const isConflict = !!(conflictReasons && conflictReasons.length > 0);
                const hasUpdate = slot?.updatedAt && slot.updatedAt !== slot.createdAt;
                const scheduledAtLabel = hasUpdate
                  ? formatScheduledAt(slot.updatedAt)
                  : (slot?.createdAt ? formatScheduledAt(slot.createdAt) : null);
                const scheduledAtPrefix = hasUpdate ? 'Updated' : 'Scheduled';

                // Derive cell styling based on conflict/dup state
                const tdSharedClass = (extra = '') => {
                  const cursor = isEditable ? 'cursor-pointer' : 'cursor-not-allowed';
                  if (isConflict) return `border-2 border-red-500 bg-red-50 p-1 text-[11px] ${cursor} ${extra}`;
                  if (dupBg) return `border-2 border-amber-300 p-1 text-[11px] ${cursor} ${extra}`;
                  return `border-2 border-gray-300 p-1 text-[11px] ${isEditable ? 'cursor-pointer hover:bg-blue-50' : 'cursor-not-allowed'} ${extra}`;
                };
                const tdStyle = dupBg && !isConflict ? { backgroundColor: dupBg } : {};
                const conflictTooltip = isConflict
                  ? `⚠ CONFLICT DETECTED\n${conflictReasons.join('\n')}${isEditable ? '\n\nDouble-click to edit' : ''}`
                  : null;
                const tdTitle = conflictTooltip
                  || (dupBg ? `Same room & time as another batch${isEditable ? ' — Double-click to edit' : ''}` : null)
                  || (isEditable ? 'Double-click to edit' : 'You cannot edit this course');

                // Only allow double-click if course is editable
                const handleCellClick = () => {
                  if (isEditable) {
                    openCellModal(courseTitle, groupName, batchName, slotIndex, batchId, slot);
                  }
                };
                return (
                  <>
                    <td
                      className={tdSharedClass('align-top')}
                      style={tdStyle}
                      onDoubleClick={handleCellClick}
                      title={tdTitle}
                    >
                      {isConflict && slot && (
                        <div className="flex items-center gap-0.5 mb-0.5">
                          <span className="inline-flex items-center gap-0.5 rounded bg-red-600 px-1 py-0 text-[9px] font-bold text-white leading-none" title={conflictTooltip || ''}>
                            ⚠ Conflict — hover for details
                          </span>
                        </div>
                      )}
                      {dupBg && !isConflict && slot && (
                        <div className="flex items-center gap-0.5 mb-0.5">
                          <span className="inline-flex items-center gap-0.5 rounded px-1 py-0 text-[9px] font-bold text-amber-800 leading-none" style={{ backgroundColor: dupBg }}>
                            ⧉ Duplicate
                          </span>
                        </div>
                      )}
                      <div className="text-gray-800 text-[11px] break-words whitespace-normal leading-snug font-semibold">{slot?.subject || ''}</div>
                      {slot?.lecturer && (
                        <div className="text-gray-600 text-[10px] mt-0.5 italic break-words whitespace-normal leading-snug">{slot.lecturer}</div>
                      )}
                      {scheduledAtLabel && (
                        <div
                          className={`text-[9px] mt-0.5 leading-snug ${hasUpdate ? 'text-amber-600' : 'text-gray-400'}`}
                          title={`${scheduledAtPrefix} on ${scheduledAtLabel}`}
                        >
                          {hasUpdate ? '✏︎' : '🕐'} {scheduledAtPrefix}: {scheduledAtLabel}
                        </div>
                      )}
                      {slot && batchGroupNames.length > 0 && showBatchGroups && (
                        <div className="text-indigo-700 text-[10px] mt-0.5 font-semibold break-words whitespace-normal leading-snug">
                          Batch Group{batchGroupNames.length > 1 ? 's' : ''}: {batchGroupNames.join(', ')}
                        </div>
                      )}
                      {slot?.groupLabel ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">{slot.groupLabel}</span>
                        </div>
                      ) : (slot?.groupChips && slot.groupChips.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {slot.groupChips.map((g, i) => (
                            <span key={i} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">{g}</span>
                          ))}
                        </div>
                      ) : null)}
                    </td>
                    <td
                      className={tdSharedClass()}
                      style={tdStyle}
                      onDoubleClick={handleCellClick}
                      title={tdTitle}
                    >{slot?.room ? cellBadge(slot.room, slot.room) : ''}</td>
                    <td
                      className={tdSharedClass()}
                      style={tdStyle}
                      onDoubleClick={handleCellClick}
                      title={tdTitle}
                    >{slot?.start ? cellBadge(slot.start, slot.start) : ''}</td>
                    <td
                      className={tdSharedClass()}
                      style={tdStyle}
                      onDoubleClick={handleCellClick}
                      title={tdTitle}
                    >{slot?.end ? cellBadge(slot.end, slot.end) : ''}</td>
                    {isEditable && (
                    <td className="border-2 border-gray-300 p-1 text-[11px] text-center">
                      {slot?.source === 'staged' && typeof slot.stagedIndex === 'number' && typeof slot.batchIdForStaged === 'number' ? (
                        <button
                          className="inline-flex items-center justify-center rounded-md bg-red-600 px-2 py-1 text-white text-xs hover:bg-red-700 cursor-pointer"
                          title="Remove this staged schedule"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const confirm = await Swal.fire({
                              title: 'Remove staged schedule?',
                              html: `<div class="text-sm">${slot?.subject || ''}<br/><span class="text-gray-500">${slot?.start || ''} - ${slot?.end || ''}</span></div>`,
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonColor: '#dc2626',
                              cancelButtonColor: '#6b7280',
                              confirmButtonText: 'Yes, remove',
                              cancelButtonText: 'Cancel',
                            });
                            if (!confirm.isConfirmed) return;
                            // Determine propagation targets to remove using front-end batch_group_subjects metadata
                            const primaryBatchId =
                              typeof slot.batchIdForStaged === 'number' ? slot.batchIdForStaged : null;
                            const primaryBatchSubjectId =
                              typeof slot.batchSubjectId === 'number' ? slot.batchSubjectId : null;

                            const key = (b, s) => `${b}:${s}`;
                            const toRemove = new Set();
                            if (primaryBatchId !== null && primaryBatchSubjectId !== null) {
                              toRemove.add(key(primaryBatchId, primaryBatchSubjectId));
                              const { targets: removalTargets = [] } =
                                computeGroupPropagationTargets(primaryBatchId, primaryBatchSubjectId) || { targets: [] };
                              removalTargets.forEach((t) => {
                                if (t && typeof t.batchId === 'number' && typeof t.batchSubjectId === 'number') {
                                  toRemove.add(key(t.batchId, t.batchSubjectId));
                                }
                              });
                            }

                            const start24 = slot?.start24;
                            const end24 = slot?.end24;
                            const classroomId = slot?.classroomId ?? null;

                            // Filter stagedByBatch: remove any entry that matches (batchId,batchSubjectId) and same time/room
                            setStagedByBatch((prev) => {
                              const next = { ...prev };
                              Object.keys(next).forEach((bk) => {
                                const bId = Number(bk);
                                const list = next[bId] || [];
                                const filtered = list.filter((it) => {
                                  const pairKey = key(bId, it.batchSubjectId);
                                  if (!toRemove.has(pairKey)) return true;
                                  const matchTime = (!start24 || it.start24 === start24) && (!end24 || it.end24 === end24);
                                  const matchRoom = classroomId === null || it.classroomId === classroomId;
                                  return !(matchTime && matchRoom);
                                });
                                next[bId] = filtered;
                              });
                              return next;
                            });
                          }}
                        >
                          X
                        </button>
                      ) : slot && slot.source === 'api' && slot.id ? (
                        <button
                          className="inline-flex items-center justify-center rounded-md bg-red-600 px-2 py-1 text-white text-xs hover:bg-red-700 cursor-pointer"
                          title="Delete this schedule"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const result = await Swal.fire({
                              title: 'Delete schedule?',
                              html: `<div class=\"text-sm\">${slot?.subject || ''}<br/><span class=\"text-gray-500\">${slot?.start || ''} - ${slot?.end || ''}</span></div>`,
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonColor: '#dc2626',
                              cancelButtonColor: '#6b7280',
                              confirmButtonText: 'Yes, delete',
                              cancelButtonText: 'Cancel',
                            });
                            if (!result.isConfirmed) return;
                            try {
                              // Determine all related schedules to delete using shared group propagation logic
                              const primary = rows.find((r) => r.scheduleId === slot.id);
                              const primaryBatchId = slot.batchId || (primary && primary.batchId) || null;
                              const primaryBatchSubjectId = primary && primary.batchSubjectId ? primary.batchSubjectId : null;

                              let scheduleIdsToDelete = [slot.id];
                              if (primaryBatchId && primaryBatchSubjectId) {
                                const { targets: deleteTargets = [] } =
                                  computeGroupPropagationTargets(primaryBatchId, primaryBatchSubjectId) || { targets: [] };
                                if (deleteTargets.length > 0) {
                                  const start24 = (slot.start24 || '').slice(0, 5);
                                  const end24 = (slot.end24 || '').slice(0, 5);
                                  const classroomId = slot.classroomId ?? null;

                                  const ids = [];
                                  deleteTargets.forEach((t) => {
                                    rows.forEach((r) => {
                                      if (Number(r.batchId) !== Number(t.batchId) || Number(r.batchSubjectId) !== Number(t.batchSubjectId)) {
                                        return;
                                      }
                                      const rStart = (r.startTime || '').slice(0, 5);
                                      const rEnd = (r.endTime || '').slice(0, 5);
                                      const rClassroomId = r.classroomId ?? null;
                                      const matchTime = (!start24 || rStart === start24) && (!end24 || rEnd === end24);
                                      const matchRoom = classroomId === null || rClassroomId === classroomId;
                                      if (matchTime && matchRoom && r.scheduleId) {
                                        ids.push(r.scheduleId);
                                      }
                                    });
                                  });

                                  if (ids.length > 0) {
                                    scheduleIdsToDelete = Array.from(new Set(ids.concat([slot.id])));
                                  }
                                }
                              }

                              // Delete all related schedules one by one
                              for (const scheduleId of scheduleIdsToDelete) {
                                const resp = await axiosInstance.delete(`/admin/attendance/schedules/${scheduleId}`);
                                if (!resp || resp.status !== 200) {
                                  throw new Error((resp && resp.data && resp.data.error) || 'Failed to delete schedule');
                                }
                              }

                              // Refresh table after deletion from Node backend
                              setLoading(true);
                              try {
                                const response = await axiosInstance.get('/admin/attendance/schedules', { params: { date: selectedDate } });
                                const data = response && response.data;
                                setRows(data || []);
                              } catch {
                                setRows([]);
                              } finally {
                                setLoading(false);
                              }
                            } catch (err) {
                              Swal.fire('Error', (err && err.message) || 'Failed to delete schedule', 'error');
                            }
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 0 1 3.878.512.75.75 0 1 1-.256 1.478l-.209-.036-1.005 12.06A3.75 3.75 0 0 1 15.164 22H8.836a3.75 3.75 0 0 1-3.744-3.281L4.087 6.659l-.209.036a.75.75 0 1 1-.256-1.478A48.567 48.567 0 0 1 7.5 4.705v-.227c0-1.564 1.214-2.9 2.816-2.974a52.662 52.662 0 0 1 3.369 0c1.602.074 2.815 1.41 2.815 2.974ZM10.343 3.3a51.196 51.196 0 0 1 3.314 0C14.606 3.34 15.75 4.425 15.75 5.75v.113a49.488 49.488 0 0 0-7.5 0V5.75c0-1.325 1.143-2.41 2.093-2.45Zm.407 5.95a.75.75 0 0 1 .8.698l.375 6.75a.75.75 0 1 1-1.498.083l-.375-6.75a.75.75 0 0 1 .698-.831Zm5.1.83a.75.75 0 0 0-1.498-.083l-.375 6.75a.75.75 0 1 0 1.498.083l.375-6.75Z" clipRule="evenodd" />
                          </svg>
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    )}
                  </>
                );
                };

                return (
                  <div key={courseTitle} className="pb-6">
                    <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border-2 px-4 py-3 ${
                      isEditable 
                        ? 'border-green-400 bg-green-50' 
                        : 'border-gray-300 bg-gray-100 opacity-70'
                    }`}>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">{courseTitle}</h2>
                        {isEditable ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-0.5 text-xs font-semibold text-white" title="You can edit schedules for this course">
                            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Editable
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-400 px-2.5 py-0.5 text-xs font-semibold text-white" title="You cannot edit schedules for this course">
                            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            View Only
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          aria-pressed={showBatchGroups}
                          onClick={() => setShowBatchGroups((prev) => !prev)}
                          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            showBatchGroups
                              ? 'border-indigo-600 bg-indigo-600 text-white shadow'
                              : 'border-indigo-400 bg-white text-indigo-700'
                          }`}
                        >
                          {showBatchGroups ? 'Hide Batch Group' : 'Show Batch Group'}
                        </button>
                        {!isEditable && (
                          <p className="text-sm text-gray-600 italic">
                            You don't have permission to edit this course
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="w-full overflow-x-auto">
                      <table className="min-w-[1100px] border-collapse w-full shadow-xl rounded-lg overflow-hidden text-[11px] leading-tight">
                        <thead>
                          <tr className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
                            {isEditable && (
                              <th rowSpan={2} className="border-2 border-indigo-800 p-2 text-center font-bold text-[10px] uppercase tracking-wider align-middle">Fun</th>
                            )}
                            <th rowSpan={2} className="border-2 border-indigo-800 p-2 text-left font-bold text-[10px] uppercase tracking-wider align-middle">WhatsApp Group Name</th>
                            <th rowSpan={2} className="border-2 border-indigo-800 p-2 text-left font-bold text-[10px] uppercase tracking-wider align-middle">Group Internal Name</th>
                            <th colSpan={isEditable ? 5 : 4} className="border-2 border-indigo-800 p-2 text-center font-bold text-[10px] uppercase tracking-wider">Subject 1</th>
                            <th colSpan={isEditable ? 5 : 4} className="border-2 border-indigo-800 p-2 text-center font-bold text-[10px] uppercase tracking-wider">Subject 2</th>
                            <th colSpan={isEditable ? 5 : 4} className="border-2 border-indigo-800 p-2 text-center font-bold text-[10px] uppercase tracking-wider">Subject 3</th>
                          </tr>
                          <tr className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
                            {Array.from({ length: 3 }).map((_, i) => (
                              <React.Fragment key={i}>
                                <th className="border-2 border-indigo-800 p-1 text-left text-[10px] uppercase tracking-wide">Subject</th>
                                <th className="border-2 border-indigo-800 p-1 text-left text-[10px] uppercase tracking-wide">Room</th>
                                <th className="border-2 border-indigo-800 p-1 text-left text-[10px] uppercase tracking-wide">Start At</th>
                                <th className="border-2 border-indigo-800 p-1 text-left text-[10px] uppercase tracking-wide">End At</th>
                                {isEditable && (
                                  <th className="border-2 border-indigo-800 p-1 text-center text-[10px] uppercase tracking-wide">Fun</th>
                                )}
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {groupEntries.map(([groupName, batches]) => {
                            const batchList = Object.values(batches);
                            return batchList.map((b, idx) => {
                              const isGroupCopied = !!copiedGroups[groupName];
                              const items = b.items;
                              const displayApi = items.map((it) => ({
                                id: it.scheduleId,
                                batchId: b.batchId || null,
                                subject: it.subjectShortName || it.subjectFullName || '',
                                room: it.classroomName || '',
                                start: to12h(it.startTime),
                                end: to12h(it.endTime),
                                lecturer: it.staffName || '',
                                groupLabel: it.scheduleBatchGroupName || undefined,
                                groupChips: it.subjectGroupNames ? String(it.subjectGroupNames).split(',').map((x) => x.trim()).filter(Boolean) : [],
                                startMin: (() => { const [hh, mm] = (it.startTime || '00:00').split(':').map((n) => parseInt(n, 10)); return (hh || 0) * 60 + (mm || 0); })(),
                                source: 'api',
                                batchSubjectId: it.batchSubjectId,
                                classroomId: it.classroomId,
                                start24: (it.startTime || '').slice(0,5),
                                end24: (it.endTime || '').slice(0,5),
                                batchGroupId: it.batchGroupId ?? null,
                                createdAt: it.createdAt || null,
                                updatedAt: it.updatedAt || null,
                              }));
                              const stagedArr = stagedByBatch[b.batchId || -1] || [];
                              // If there are staged 'edit' items, hide matching API items by batchSubjectId
                              const hideSubjects = new Set(stagedArr
                                .filter((s) => s.mode === 'edit' && !!s.replaceBatchSubjectId)
                                .map((s) => s.replaceBatchSubjectId)
                              );
                              const displayApiFiltered = displayApi.filter((it) => !hideSubjects.has(it.batchSubjectId));
                              const staged = stagedArr.map((s, si) => ({
                                ...s,
                                source: 'staged',
                                batchId: b.batchId || null,
                                batchIdForStaged: (b.batchId || -1),
                                stagedIndex: si,
                                batchSubjectId: s.batchSubjectId,
                                start24: s.start24,
                                end24: s.end24,
                                classroomId: s.classroomId,
                                batchGroupId: s.batchGroupId ?? null,
                              }));
                              const combined = [...displayApiFiltered, ...staged];
                              combined.sort((a, b) => a.startMin - b.startMin);
                              const display = combined.map((it) => ({
                                id: it.id,
                                batchId: it.batchId,
                                subject: it.subject,
                                room: it.room,
                                start: it.start,
                                end: it.end,
                                lecturer: it.lecturer,
                                groupLabel: it.groupLabel,
                                groupChips: it.groupChips,
                                source: it.source,
                                stagedIndex: it.stagedIndex,
                                batchIdForStaged: it.batchIdForStaged,
                                batchSubjectId: it.batchSubjectId,
                                start24: it.start24,
                                end24: it.end24,
                                classroomId: it.classroomId,
                                batchGroupId: it.batchGroupId ?? null,
                                createdAt: it.createdAt || null,
                                updatedAt: it.updatedAt || null,
                              }));
                              const pad = 3 - Math.min(3, display.length);
                              return (
                                <tr key={`${groupName}-${b.batchId || 'null'}-${idx}`} className={`hover:bg-indigo-50 ${isGroupCopied ? 'bg-emerald-50/70 transition-colors' : ''}`}>
                                  {isEditable && idx === 0 && (
                                    <td rowSpan={batchList.length} className={`border-2 p-2 text-center align-top space-y-2 ${isGroupCopied ? 'border-emerald-300 bg-emerald-50/70' : 'border-gray-300'}`}>
                                      <div className="flex flex-col items-center gap-2">
                                        <button
                                          onClick={() => copyGroup(groupName)}
                                          className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-2 py-1.5 text-white hover:bg-emerald-700"
                                          title="Copy message"
                                        >
                                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8M8 15h6M6 3h12a2 2 0 012 2v12a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2z" /></svg>
                                        </button>
                                        {isGroupCopied && (
                                          <button
                                            onClick={() => setCopiedGroups((prev) => { const copy = { ...prev }; delete copy[groupName]; return copy; })}
                                            className="inline-flex items-center justify-center rounded-md bg-gray-200 px-2 py-1.5 text-gray-700 hover:bg-gray-300"
                                            title="Reset copied highlight"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path fillRule="evenodd" d="M12 4.5a7.5 7.5 0 1 0 7.06 9.75.75.75 0 1 1 1.44.5A9 9 0 1 1 12 3v1.5Zm8.03-2.03a.75.75 0 0 1 .22.53v4.5a.75.75 0 0 1-.75.75h-4.5a.75.75 0 0 1 0-1.5h2.69l-3.22-3.22a.75.75 0 1 1 1.06-1.06l3.22 3.22V3a.75.75 0 0 1 .53-.22c.2 0 .39.08.53.22Z" clipRule="evenodd"/></svg>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                  {idx === 0 && (
                                    <td rowSpan={batchList.length} className={`border-2 p-2 text-[11px] align-top ${isGroupCopied ? 'border-emerald-300 bg-emerald-50/70' : 'border-gray-300'}`}>
                                      <div className="max-w-[240px]">
                                        <span className={`inline-block rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide break-words whitespace-normal ${isGroupCopied ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                          {groupName}
                                        </span>
                                      </div>
                                    </td>
                                  )}
                                  <td className="border-2 border-gray-300 p-2 text-[11px] align-top break-words whitespace-normal">
                                    <div className="flex items-center gap-2">
                                      <span>{b.batchInternalName || 'N/A'}</span>
                                      {typeof b.studentCount === 'number' && b.studentCount > 0 && (
                                        <span className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                                          {b.studentCount}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {display.slice(0, 3).map((d, slotIdx) => (
                                    <React.Fragment key={slotIdx}>{renderSlot(groupName, b.batchInternalName || 'N/A', slotIdx, d, b.batchId)}</React.Fragment>
                                  ))}
                                  {Array.from({ length: pad > 0 ? pad : 0 }).map((_, slotIdx) => (
                                    <React.Fragment key={`pad-${slotIdx}`}>{renderSlot(groupName, b.batchInternalName || 'N/A', display.length + slotIdx, undefined, b.batchId)}</React.Fragment>
                                  ))}
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Selection Modal (double-click) */}
                    {showModal && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="w-full max-w-2xl max-h-[90vh] rounded-2xl bg-white shadow-2xl flex flex-col">
                          <div className="rounded-t-2xl bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white font-bold">
                            {modalContext?.batchInternalName ? `${modalContext.batchInternalName} - schedule` : 'Schedule'}
                          </div>
                          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                            <div className="text-sm text-gray-700 break-words whitespace-normal">
                              {modalContext ? (
                                <>
                                  Course: <b>{modalContext.courseTitle}</b> • Group: {modalContext.groupName ? (
                                    <span className="inline-block align-middle rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                                      {modalContext.groupName}
                                    </span>
                                  ) : (
                                    <b>N/A</b>
                                  )} • Batch: <b>{modalContext.batchInternalName}</b> • Slot #{modalContext.slotIndex + 1}
                                </>
                              ) : null}
                            </div>
                            {/* Subjects categorized by status */}
                            <div className="rounded-md border-2 border-gray-200 p-3">
                              {modalLoading ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" aria-busy="true" aria-live="polite">
                                  {[{ title: 'Ongoing', color: 'text-amber-700' }, { title: 'Not Started', color: 'text-gray-700' }, { title: 'Completed', color: 'text-emerald-700' }].map((col) => (
                                    <div key={col.title}>
                                      <div className={`mb-2 text-xs font-bold uppercase ${col.color}`}>{col.title}</div>
                                      <div className="space-y-2">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                          <div key={i} className="animate-pulse rounded border px-2 py-2">
                                            <div className="h-3 w-24 bg-gray-200 rounded mb-1"></div>
                                            <div className="h-2 w-36 bg-gray-100 rounded"></div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (modalSubjects && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <div className="mb-2 text-xs font-bold uppercase text-amber-700">Ongoing</div>
                                    <div className="max-h-44 overflow-auto space-y-2">
                                      {modalSubjects.ongoing.length === 0 && <div className="text-xs text-gray-500">No ongoing subjects</div>}
                                      {modalSubjects.ongoing.map((s) => (
                                        <div
                                          key={s.batchSubjectId}
                                          className={`rounded border px-2 py-1 text-xs flex items-center justify-between cursor-pointer ${selectedBatchSubjectId === s.batchSubjectId ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''}`}
                                          onClick={() => { setSelectedBatchSubjectId(s.batchSubjectId); setSelectedSubjectInfo({ short: s.subjectShortName, full: s.subjectFullName, chips: s.subjectGroupNames ? String(s.subjectGroupNames).split(',').map((g) => g.trim()).filter(Boolean) : [] }); }}
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="font-semibold leading-snug break-words whitespace-normal">{s.subjectShortName || s.subjectFullName}</div>
                                            <div className="text-[10px] text-gray-500 leading-snug whitespace-normal break-words">
                                              Sem {s.semester} • {s.lecturerName || 'TBD'}
                                              {s.avgAttendance !== null && s.avgAttendance !== undefined && (
                                                <span className={`ml-1.5 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-semibold ${
                                                  s.avgAttendance >= 75 ? 'bg-emerald-100 text-emerald-700' : 
                                                  s.avgAttendance >= 50 ? 'bg-amber-100 text-amber-700' : 
                                                  'bg-red-100 text-red-700'
                                                }`}>
                                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-2.5 h-2.5">
                                                    <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
                                                  </svg>
                                                  {s.avgParticipation !== null && s.avgParticipation !== undefined && s.totalStudentsInGroup ? (
                                                    <span>
                                                      ~{Math.round(s.avgParticipation)}/{s.totalStudentsInGroup} students ({s.avgAttendance}%)
                                                    </span>
                                                  ) : s.avgParticipation !== null && s.avgParticipation !== undefined ? (
                                                    <span>
                                                      ~{Math.round(s.avgParticipation)} students ({s.avgAttendance}%)
                                                    </span>
                                                  ) : (
                                                    <span>{s.avgAttendance}%</span>
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                            {(() => {
                                              const toNumOrNull = (val) => {
                                                if (val === null || val === undefined || val === '') return null;
                                                const n = Number(val);
                                                return Number.isNaN(n) ? null : n;
                                              };
                                              const expected = toNumOrNull(s.expectedNumberOfClasses);
                                              const scheduled = toNumOrNull(s.scheduledClassesCount);
                                              const balanceExplicit = toNumOrNull(s.balanceClasses);
                                              const balance = balanceExplicit !== null
                                                ? balanceExplicit
                                                : expected !== null && scheduled !== null
                                                  ? expected - scheduled
                                                  : null;
                                              if (expected === null && scheduled === null && balance === null) return null;
                                              const hoursPerClass = 2;
                                              const expectedHours = expected !== null ? expected * hoursPerClass : null;
                                              const scheduledHours = scheduled !== null ? scheduled * hoursPerClass : null;
                                              const balanceHours = balance !== null ? balance * hoursPerClass : null;
                                              return (
                                                <div className="mt-0.5 text-[10px] text-gray-600 leading-snug">
                                                  {expected !== null && (
                                                    <span>
                                                      Plan: <b>{expected}</b>{expectedHours !== null && <> ({expectedHours}h)</>}
                                                    </span>
                                                  )}
                                                  {scheduled !== null && (
                                                    <>
                                                      {' '}
                                                      • Done: <b>{scheduled}</b>{scheduledHours !== null && <> ({scheduledHours}h)</>}
                                                    </>
                                                  )}
                                                  {balance !== null && (
                                                    <>
                                                      {' '}
                                                      • {balance < 0 ? (
                                                        <span className="text-red-600">
                                                          Extra: <b>{Math.abs(balance)}</b>{balanceHours !== null && <> ({Math.abs(balanceHours)}h)</>}
                                                        </span>
                                                      ) : (
                                                        <span>
                                                          Left: <b>{balance}</b>{balanceHours !== null && <> ({balanceHours}h)</>}
                                                        </span>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                            {s.subjectGroupNames ? (
                                              <div className="mt-1 flex flex-wrap gap-1">
                                                {String(s.subjectGroupNames)
                                                  .split(',')
                                                  .map((g, i) => (
                                                    <span key={i} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">
                                                      {g.trim()}
                                                    </span>
                                                  ))}
                                              </div>
                                            ) : null}
                                          </div>
                                          <button type="button" className="ml-2 rounded bg-indigo-600 px-2 py-1 text-[10px] text-white" onClick={() => { setSelectedBatchSubjectId(s.batchSubjectId); setSelectedSubjectInfo({ short: s.subjectShortName, full: s.subjectFullName, chips: s.subjectGroupNames ? String(s.subjectGroupNames).split(',').map((g) => g.trim()).filter(Boolean) : [] }); }}>Select</button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="mb-2 text-xs font-bold uppercase text-gray-700">Not Started</div>
                                    <div className="max-h-44 overflow-auto space-y-2">
                                      {modalSubjects.not_started.length === 0 && <div className="text-xs text-gray-500">No pending subjects</div>}
                                      {modalSubjects.not_started.map((s) => (
                                        <div
                                          key={s.batchSubjectId}
                                          className={`rounded border px-2 py-1 text-xs flex items-center justify-between cursor-pointer ${selectedBatchSubjectId === s.batchSubjectId ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''}`}
                                          onClick={() => { setSelectedBatchSubjectId(s.batchSubjectId); setSelectedSubjectInfo({ short: s.subjectShortName, full: s.subjectFullName, chips: s.subjectGroupNames ? String(s.subjectGroupNames).split(',').map((g) => g.trim()).filter(Boolean) : [] }); }}
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="font-semibold leading-snug break-words whitespace-normal">{s.subjectShortName || s.subjectFullName}</div>
                                            <div className="text-[10px] text-gray-500 leading-snug whitespace-normal break-words">Sem {s.semester} • {s.lecturerName || 'TBD'}</div>
                                            {(() => {
                                              const toNumOrNull = (val) => {
                                                if (val === null || val === undefined || val === '') return null;
                                                const n = Number(val);
                                                return Number.isNaN(n) ? null : n;
                                              };
                                              const expected = toNumOrNull(s.expectedNumberOfClasses);
                                              const scheduled = toNumOrNull(s.scheduledClassesCount);
                                              const balanceExplicit = toNumOrNull(s.balanceClasses);
                                              const balance = balanceExplicit !== null
                                                ? balanceExplicit
                                                : expected !== null && scheduled !== null
                                                  ? expected - scheduled
                                                  : null;
                                              if (expected === null && scheduled === null && balance === null) return null;
                                              const hoursPerClass = 2;
                                              const expectedHours = expected !== null ? expected * hoursPerClass : null;
                                              const scheduledHours = scheduled !== null ? scheduled * hoursPerClass : null;
                                              const balanceHours = balance !== null ? balance * hoursPerClass : null;
                                              return (
                                                <div className="mt-0.5 text-[10px] text-gray-600 leading-snug">
                                                  {expected !== null && (
                                                    <span>
                                                      Plan: <b>{expected}</b>{expectedHours !== null && <> ({expectedHours}h)</>}
                                                    </span>
                                                  )}
                                                  {scheduled !== null && (
                                                    <>
                                                      {' '}
                                                      • Done: <b>{scheduled}</b>{scheduledHours !== null && <> ({scheduledHours}h)</>}
                                                    </>
                                                  )}
                                                  {balance !== null && (
                                                    <>
                                                      {' '}
                                                      • {balance < 0 ? (
                                                        <span className="text-red-600">
                                                          Extra: <b>{Math.abs(balance)}</b>{balanceHours !== null && <> ({Math.abs(balanceHours)}h)</>}
                                                        </span>
                                                      ) : (
                                                        <span>
                                                          Left: <b>{balance}</b>{balanceHours !== null && <> ({balanceHours}h)</>}
                                                        </span>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                            {s.subjectGroupNames ? (
                                              <div className="mt-1 flex flex-wrap gap-1">
                                                {String(s.subjectGroupNames)
                                                  .split(',')
                                                  .map((g, i) => (
                                                    <span key={i} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">
                                                      {g.trim()}
                                                    </span>
                                                  ))}
                                              </div>
                                            ) : null}
                                          </div>
                                          <button type="button" className="ml-2 rounded bg-indigo-600 px-2 py-1 text-[10px] text-white" onClick={() => { setSelectedBatchSubjectId(s.batchSubjectId); setSelectedSubjectInfo({ short: s.subjectShortName, full: s.subjectFullName, chips: s.subjectGroupNames ? String(s.subjectGroupNames).split(',').map((g) => g.trim()).filter(Boolean) : [] }); }}>Select</button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="mb-2 text-xs font-bold uppercase text-emerald-700">Completed</div>
                                    <div className="max-h-44 overflow-auto space-y-2">
                                      {modalSubjects.completed.length === 0 && <div className="text-xs text-gray-500">No completed subjects</div>}
                                      {modalSubjects.completed.map((s) => (
                                        <div
                                          key={s.batchSubjectId}
                                          className={`rounded border px-2 py-1 text-xs flex items-center justify-between cursor-pointer ${selectedBatchSubjectId === s.batchSubjectId ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''}`}
                                          onClick={() => { setSelectedBatchSubjectId(s.batchSubjectId); setSelectedSubjectInfo({ short: s.subjectShortName, full: s.subjectFullName, chips: s.subjectGroupNames ? String(s.subjectGroupNames).split(',').map((g) => g.trim()).filter(Boolean) : [] }); }}
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="font-semibold leading-snug break-words whitespace-normal">{s.subjectShortName || s.subjectFullName}</div>
                                            <div className="text-[10px] text-gray-500 leading-snug whitespace-normal break-words">
                                              Sem {s.semester} • {s.lecturerName || 'TBD'}
                                              {s.avgAttendance !== null && s.avgAttendance !== undefined && (
                                                <span className={`ml-1.5 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-semibold ${
                                                  s.avgAttendance >= 75 ? 'bg-emerald-100 text-emerald-700' : 
                                                  s.avgAttendance >= 50 ? 'bg-amber-100 text-amber-700' : 
                                                  'bg-red-100 text-red-700'
                                                }`}>
                                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-2.5 h-2.5">
                                                    <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
                                                  </svg>
                                                  {s.avgParticipation !== null && s.avgParticipation !== undefined && s.totalStudentsInGroup ? (
                                                    <span>
                                                      ~{Math.round(s.avgParticipation)}/{s.totalStudentsInGroup} students ({s.avgAttendance}%)
                                                    </span>
                                                  ) : s.avgParticipation !== null && s.avgParticipation !== undefined ? (
                                                    <span>
                                                      ~{Math.round(s.avgParticipation)} students ({s.avgAttendance}%)
                                                    </span>
                                                  ) : (
                                                    <span>{s.avgAttendance}%</span>
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                            {(() => {
                                              const toNumOrNull = (val) => {
                                                if (val === null || val === undefined || val === '') return null;
                                                const n = Number(val);
                                                return Number.isNaN(n) ? null : n;
                                              };
                                              const expected = toNumOrNull(s.expectedNumberOfClasses);
                                              const scheduled = toNumOrNull(s.scheduledClassesCount);
                                              const balanceExplicit = toNumOrNull(s.balanceClasses);
                                              const balance = balanceExplicit !== null
                                                ? balanceExplicit
                                                : expected !== null && scheduled !== null
                                                  ? expected - scheduled
                                                  : null;
                                              if (expected === null && scheduled === null && balance === null) return null;
                                              const hoursPerClass = 2;
                                              const expectedHours = expected !== null ? expected * hoursPerClass : null;
                                              const scheduledHours = scheduled !== null ? scheduled * hoursPerClass : null;
                                              const balanceHours = balance !== null ? balance * hoursPerClass : null;
                                              return (
                                                <div className="mt-0.5 text-[10px] text-gray-600 leading-snug">
                                                  {expected !== null && (
                                                    <span>
                                                      Plan: <b>{expected}</b>{expectedHours !== null && <> ({expectedHours}h)</>}
                                                    </span>
                                                  )}
                                                  {scheduled !== null && (
                                                    <>
                                                      {' '}
                                                      • Done: <b>{scheduled}</b>{scheduledHours !== null && <> ({scheduledHours}h)</>}
                                                    </>
                                                  )}
                                                  {balance !== null && (
                                                    <>
                                                      {' '}
                                                      • {balance < 0 ? (
                                                        <span className="text-red-600">
                                                          Extra: <b>{Math.abs(balance)}</b>{balanceHours !== null && <> ({Math.abs(balanceHours)}h)</>}
                                                        </span>
                                                      ) : (
                                                        <span>
                                                          Left: <b>{balance}</b>{balanceHours !== null && <> ({balanceHours}h)</>}
                                                        </span>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                            {s.subjectGroupNames ? (
                                              <div className="mt-1 flex flex-wrap gap-1">
                                                {String(s.subjectGroupNames)
                                                  .split(',')
                                                  .map((g, i) => (
                                                    <span key={i} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">
                                                      {g.trim()}
                                                    </span>
                                                  ))}
                                              </div>
                                            ) : null}
                                          </div>
                                          <button type="button" className="ml-2 rounded bg-indigo-600 px-2 py-1 text-[10px] text-white" onClick={() => { setSelectedBatchSubjectId(s.batchSubjectId); setSelectedSubjectInfo({ short: s.subjectShortName, full: s.subjectFullName, chips: s.subjectGroupNames ? String(s.subjectGroupNames).split(',').map((g) => g.trim()).filter(Boolean) : [] }); }}>Select</button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Start Time</label>
                                <input
                                  type="time"
                                  value={modalTime.start}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setModalTime((t) => ({ ...t, start: v, end: computeEndFromStartAndDuration(v, modalDurationMin) }));
                                  }}
                                  className="w-full rounded-md border-2 border-gray-300 px-2 py-2 text-sm focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Duration</label>
                                <select
                                  value={modalDurationMin ?? ''}
                                  onChange={(e) => {
                                    const minutes = e.target.value ? parseInt(e.target.value, 10) : null;
                                    setModalDurationMin(minutes);
                                    setModalTime((t) => ({ ...t, end: computeEndFromStartAndDuration(t.start, minutes) }));
                                  }}
                                  disabled={!modalTime.start}
                                  className="w-full rounded-md border-2 border-gray-300 px-2 py-2 text-sm focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                  <option value="">Select duration…</option>
                                  <option value="60">1 hr</option>
                                  <option value="90">1 hr 30 min</option>
                                  <option value="120">2 hr</option>
                                  <option value="150">2 hr 30 min</option>
                                  <option value="180">3 hr</option>
                                  <option value="210">3 hr 30 min</option>
                                  <option value="240">4 hr</option>
                                  <option value="270">4 hr 30 min</option>
                                  <option value="300">5 hr</option>
                                  <option value="330">5 hr 30 min</option>
                                  <option value="360">6 hr</option>
                                  <option value="390">6 hr 30 min</option>
                                  <option value="420">7 hr</option>
                                  <option value="450">7 hr 30 min</option>
                                  <option value="480">8 hr</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">End Time</label>
                                <input
                                  type="time"
                                  value={modalTime.end}
                                  readOnly
                                  className="w-full rounded-md border-2 border-gray-300 px-2 py-2 text-sm bg-gray-100 text-gray-600"
                                  title="End time is calculated from start time and duration"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Room</label>
                              <Select
                                options={groupedRoomOptions}
                                isLoading={roomsLoading}
                                isClearable
                                placeholder="Select a room…"
                                value={selectedRoomOption}
                                onChange={(opt) => setSelectedRoomId(opt ? opt.value : null)}
                                classNamePrefix="react-select"
                              />
                              <div className="mt-1 text-[11px] text-gray-500">Grouped by floor</div>
                              {conflictWarnings.length > 0 && (
                                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
                                  <div className="flex items-center gap-1.5 text-amber-800 font-semibold text-xs">
                                    <span>⚠️</span>
                                    <span>Schedule Conflict{conflictWarnings.length > 1 ? 's' : ''} Detected</span>
                                  </div>
                                  {conflictWarnings.map((w, i) => (
                                    <div key={i} className="text-xs text-amber-700 border-t border-amber-200 pt-2 first:border-t-0 first:pt-0">
                                      {w.type === 'room' ? (
                                        <span>🏫 <strong>Room "{w.name}"</strong> is already booked at <strong>{w.time}</strong>{w.detail ? ` — ${w.detail}` : ''}</span>
                                      ) : (
                                        <span>👤 <strong>Lecturer "{w.name}"</strong> already has a class at <strong>{w.time}</strong>{w.detail ? ` — ${w.detail}` : ''}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {modalContext?.courseTitle !== 'Level 5 Pearson BTEC HND in Computing' && isAllocationValid && (
                              <div className="mt-4 rounded-md border-2 border-dashed border-gray-200 p-3 bg-gray-50">
                                <div className="flex items-center justify-between gap-2">
                                  <div>
                                    <div className="text-xs font-semibold text-gray-700">Duplicate this schedule to other dates?</div>
                                    <div className="text-[11px] text-gray-500">Optional – add specific future dates to repeat the same time, subject and room.</div>
                                  </div>
                                  <div className="inline-flex gap-1 rounded-full bg-white p-1 border border-gray-200">
                                    <button
                                      type="button"
                                      onClick={() => setDuplicateEnabled(true)}
                                      className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                                        duplicateEnabled
                                          ? 'bg-emerald-600 text-white shadow-sm'
                                          : 'bg-transparent text-gray-700 hover:bg-emerald-50'
                                      }`}
                                    >
                                      Yes
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDuplicateEnabled(false);
                                        setDuplicateDateInput('');
                                        setDuplicateSelectedDates([]);
                                      }}
                                      className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                                        !duplicateEnabled
                                          ? 'bg-gray-800 text-white shadow-sm'
                                          : 'bg-transparent text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      No
                                    </button>
                                  </div>
                                </div>
                                {editing?.type === 'api' && (
                                  <p className="mt-2 text-[11px] text-gray-500">
                                    Duplication applies only when creating a new schedule. Existing schedules will be updated for this date only.
                                  </p>
                                )}
                                {duplicateEnabled && (!editing || editing?.type === 'staged') && (
                                  <>
                                    <div className="mt-3 space-y-3">
                                      <div className="flex flex-col md:flex-row md:items-end gap-3">
                                        <div className="flex-1">
                                          <label className="block text-[11px] font-semibold text-gray-700 mb-1">Pick a date to add</label>
                                          <input
                                            type="date"
                                            min={minDuplicateDate || undefined}
                                            value={duplicateDateInput}
                                            onChange={(e) => {
                                              let v = e.target.value;
                                              if (minDuplicateDate && v && v < minDuplicateDate) {
                                                v = minDuplicateDate;
                                              }
                                              if (v && duplicateSelectedDates.includes(v)) {
                                                // Treat already-added dates as disabled: clear selection and show a gentle warning
                                                setDuplicateDateInput('');
                                                Swal.fire({
                                                  toast: true,
                                                  position: 'top-end',
                                                  icon: 'info',
                                                  title: 'This date is already added',
                                                  showConfirmButton: false,
                                                  timer: 1500,
                                                  timerProgressBar: true,
                                                });
                                                return;
                                              }
                                              // Enforce weekday / weekend constraint
                                              if (v) {
                                                const isCurrentWeekend = isWeekendDate(selectedDate);
                                                const isTargetWeekend = isWeekendDate(v);
                                                if (isCurrentWeekend !== isTargetWeekend) {
                                                  setDuplicateDateInput('');
                                                  Swal.fire({
                                                    toast: true,
                                                    position: 'top-end',
                                                    icon: 'warning',
                                                    title: isCurrentWeekend
                                                      ? 'Please pick a weekend date (Sat / Sun)'
                                                      : 'Please pick a weekday date (Mon – Fri)',
                                                    showConfirmButton: false,
                                                    timer: 2000,
                                                    timerProgressBar: true,
                                                  });
                                                  return;
                                                }
                                              }
                                              setDuplicateDateInput(v);
                                            }}
                                            className="w-full rounded-md border-2 border-gray-300 px-2 py-2 text-sm focus:border-indigo-500"
                                          />
                                        </div>
                                        <button
                                          type="button"
                                          onClick={handleAddDuplicateDate}
                                          className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 whitespace-nowrap"
                                        >
                                          + Add date
                                        </button>
                                      </div>
                                      {duplicateSelectedDates.length > 0 && (
                                        <div>
                                          <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-[11px] font-semibold text-gray-600">
                                              {duplicateSelectedDates.length} date{duplicateSelectedDates.length > 1 ? 's' : ''} added
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setDuplicateSelectedDates([]);
                                                setDuplicateDateInput('');
                                                setDuplicateEnabled(false);
                                              }}
                                              className="text-[11px] text-red-600 hover:text-red-800 font-semibold underline"
                                              title="Remove all duplicate dates"
                                            >
                                              Clear all
                                            </button>
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {duplicateSelectedDates.map((d) => {
                                              const dayName = (() => {
                                                try {
                                                  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short' });
                                                } catch { return ''; }
                                              })();
                                              return (
                                              <span
                                                key={d}
                                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold"
                                              >
                                                <span className="rounded bg-emerald-600 text-white px-1 py-px text-[10px] font-bold leading-none">{dayName}</span>
                                                {d}
                                                <button
                                                  type="button"
                                                  onClick={() => setDuplicateSelectedDates((prev) => prev.filter((x) => x !== d))}
                                                  className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white text-[10px] hover:bg-emerald-700"
                                                  title="Remove date"
                                                >
                                                  ×
                                                </button>
                                              </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      <p className="text-[11px] text-gray-500">
                                        Only future {isWeekendDate(selectedDate) ? 'weekend (Sat / Sun)' : 'weekday (Mon – Fri)'} dates are allowed. The schedule will be created once for each added date.
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-2 rounded-b-2xl bg-gray-50 p-3">
                            {!isAllocationValid && (
                              <div className="text-[11px] text-gray-500">
                                Select a subject, start time, duration, and room to proceed.
                              </div>
                            )}
                            <div className="ml-auto flex gap-2">
                              <button onClick={closeModal} className="rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700">Close</button>
                              <button
                                onClick={handleConfirmSchedule}
                                disabled={!isAllocationValid}
                                className={`rounded-md px-4 py-2 text-sm font-semibold text-white ${isAllocationValid ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`}
                              >
                                Confirm to schedule
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>

            {/* Right area removed as per request (Rooms panel not needed) */}
          </div>
          {/* Summary Modal */}
          {showSummaryModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
              <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="relative rounded-t-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
                        <path d="M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
                        <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <h2 className="text-xl font-bold">Daily Summary</h2>
                        <p className="text-indigo-100 text-xs mt-0.5">Schedule overview for {new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowSummaryModal(false); setSummarySearchQuery(''); }}
                      className="rounded-lg p-2 text-white/80 hover:bg-white/20 hover:text-white transition-all"
                      title="Close"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                        <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-6 bg-gray-50">
                  <div className="flex gap-6">
                    {/* Left tabs */}
                    <div className="w-48 shrink-0">
                      <div className="flex flex-col gap-2 sticky top-0">
                        {[
                          { key: 'classes', label: 'Classes', icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M11.7 2.805a.75.75 0 0 1 .6 0A60.65 60.65 0 0 1 22.83 8.72a.75.75 0 0 1-.231 1.337 49.948 49.948 0 0 0-9.902 3.912l-.003.002c-.114.06-.227.119-.34.18a.75.75 0 0 1-.707 0A50.88 50.88 0 0 0 7.5 12.173v-.224c0-.131.067-.248.172-.311a54.615 54.615 0 0 1 4.653-2.52.75.75 0 0 0-.65-1.352 56.123 56.123 0 0 0-4.78 2.589 1.858 1.858 0 0 0-.859 1.228 49.803 49.803 0 0 0-4.634-1.527.75.75 0 0 1-.231-1.337A60.653 60.653 0 0 1 11.7 2.805Z" /><path d="M13.06 15.473a48.45 48.45 0 0 1 7.666-3.282c.134 1.414.22 2.843.255 4.284a.75.75 0 0 1-.46.711 47.87 47.87 0 0 0-8.105 4.342.75.75 0 0 1-.832 0 47.87 47.87 0 0 0-8.104-4.342.75.75 0 0 1-.461-.71c.035-1.442.121-2.87.255-4.286.921.304 1.83.634 2.726.99v1.27a1.5 1.5 0 0 0-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.66a6.727 6.727 0 0 0 .551-1.607 1.5 1.5 0 0 0 .14-2.67v-.645a48.549 48.549 0 0 1 3.44 1.667 2.25 2.25 0 0 0 2.12 0Z" /></svg> },
                          { key: 'lecturers', label: 'Lecturers', icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38Z" clipRule="evenodd" /><path d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047ZM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.486l-.115.04c-.567.2-1.156.349-1.764.441Z" /></svg> },
                          { key: 'classrooms', label: 'Classrooms', icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M11.584 2.376a.75.75 0 0 1 .832 0l9 6a.75.75 0 1 1-.832 1.248L12 3.901 3.416 9.624a.75.75 0 0 1-.832-1.248l9-6Z" /><path fillRule="evenodd" d="M20.25 10.332v9.918H21a.75.75 0 0 1 0 1.5H3a.75.75 0 0 1 0-1.5h.75v-9.918a.75.75 0 0 1 .634-.74A49.109 49.109 0 0 1 12 9c2.59 0 5.134.202 7.616.592a.75.75 0 0 1 .634.74Zm-7.5 2.418a.75.75 0 0 0-1.5 0v6.75a.75.75 0 0 0 1.5 0v-6.75Zm3-.75a.75.75 0 0 1 .75.75v6.75a.75.75 0 0 1-1.5 0v-6.75a.75.75 0 0 1 .75-.75ZM9 12.75a.75.75 0 0 0-1.5 0v6.75a.75.75 0 0 0 1.5 0v-6.75Z" clipRule="evenodd" /><path d="M12 7.875a1.125 1.125 0 1 0 0-2.25 1.125 1.125 0 0 0 0 2.25Z" /></svg> },
                          { key: 'available', label: 'Available', icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path fillRule="evenodd" d="M12 4.5a7.5 7.5 0 1 0 4.546 13.5.75.75 0 1 1 .908 1.2A9 9 0 1 1 13.5 3.058V3a.75.75 0 0 1 1.5 0v3.75a.75.75 0 0 1-.75.75H10.5a.75.75 0 0 1 0-1.5h2.708A7.52 7.52 0 0 0 12 4.5Zm5.78 6.22a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.97.97 2.47-2.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg> },
                        ].map((t) => (
                          <button
                            key={t.key}
                            onClick={() => { setSummaryTab(t.key); setSummarySearchQuery(''); }}
                            className={`group w-full rounded-xl px-4 py-3 text-left text-sm font-semibold transition-all duration-200 flex items-center gap-3 ${
                              summaryTab === t.key 
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30 scale-105' 
                                : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow-md hover:scale-102'
                            }`}
                          >
                            <span className={`transition-transform ${summaryTab === t.key ? 'scale-110' : 'group-hover:scale-110'}`}>
                              {t.icon}
                            </span>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Right content */}
                    <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {summaryTab === 'classes' && (
                        <div className="h-full flex flex-col">
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4 space-y-3">
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                              </svg>
                              <span><b className="font-semibold text-gray-900">{new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</b></span>
                              {courseFilter ? (<span className="inline-flex items-center gap-1.5 ml-2 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" /></svg>{courseFilter}</span>) : null}
                            </div>
                            {/* Search bar */}
                            <div className="relative">
                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-400">
                                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <input
                                type="text"
                                value={summarySearchQuery}
                                onChange={(e) => setSummarySearchQuery(e.target.value)}
                                placeholder="Search by group, batch, subject, lecturer, or hall..."
                                className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                              />
                              {summarySearchQuery && (
                                <button
                                  onClick={() => setSummarySearchQuery('')}
                                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                  title="Clear search"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-8 relative scroll-smooth" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                            {Object.entries(groupedByCourse)
                              .filter(([courseTitle]) => !courseFilter || courseTitle === courseFilter)
                              .map(([courseTitle, types]) => {
                                const mergedGroups = buildMergedGroups(types);
                                const groupEntries = Object.entries(mergedGroups).sort(([a], [b]) => a.localeCompare(b));
                                const hasAny = groupEntries.some(([_, batches]) => Object.values(batches).some((b) => collectBatchItems(b).length > 0));
                                if (!hasAny) return null;
                                return (
                                  <div key={courseTitle} className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                                    <div className="flex items-center gap-3">
                                      <div className="flex-shrink-0 w-1 h-8 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                                      <h3 className="text-base font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">{courseTitle}</h3>
                                    </div>
                                    <div className="space-y-4">
                                      {groupEntries.map(([groupName, batches]) => {
                                        const batchList = Object.values(batches || {});
                                        // Filter by search query
                                        const filteredBatchList = batchList.filter((b) => filterBySearch(groupName, b, summarySearchQuery));
                                        // Compute total sessions across batches for small stat
                                        const totalSessions = filteredBatchList.reduce((acc, b) => acc + collectBatchItems(b).length, 0);
                                        if (totalSessions === 0) return null;
                                        return (
                                          <div key={groupName} className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200">
                                            <div className="flex items-center justify-between rounded-t-xl bg-gradient-to-r from-gray-50 to-gray-100 px-5 py-3 border-b border-gray-200">
                                              <div className="flex items-center gap-3">
                                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-bold shadow-md">{groupName?.charAt(0) || 'G'}</span>
                                                <div>
                                                  <div className="text-sm font-semibold text-gray-900">{groupName}</div>
                                                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                                                    </svg>
                                                    {totalSessions} session{totalSessions === 1 ? '' : 's'}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="p-4">
                                              {courseTitle === 'Level 5 Pearson BTEC HND in Computing' ? (
                                                <>
                                                  {(() => {
                                                    const batchHasToken = (b, token) => {
                                                      const name = String(b.batchInternalName || '').toUpperCase();
                                                      const tokens = name.split(/[^A-Z0-9]+/).filter(Boolean);
                                                      return tokens.includes(token);
                                                    };
                                                    const seBatches = filteredBatchList.filter((b) => batchHasToken(b, 'SE'));
                                                    const neBatches = filteredBatchList.filter((b) => batchHasToken(b, 'NE'));
                                                    const totalSE = seBatches.reduce((acc, b) => acc + collectBatchItems(b).length, 0);
                                                    const totalNE = neBatches.reduce((acc, b) => acc + collectBatchItems(b).length, 0);
                                                    const renderSection = (label, list, total) => (
                                                      total > 0 ? (
                                                        <div className="mb-5">
                                                          <div className="mb-3 flex items-center gap-2">
                                                            <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold shadow-sm ${label === 'SE' ? 'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800' : 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'}`}>
                                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                                <path fillRule="evenodd" d="M10 2a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-1.5 0v-7.5A.75.75 0 0 1 10 2ZM5.404 4.343a.75.75 0 0 1 0 1.06 6.5 6.5 0 1 0 9.192 0 .75.75 0 1 1 1.06-1.06 8 8 0 1 1-11.313 0 .75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                                                              </svg>
                                                              {label}
                                                            </span>
                                                            <span className="text-xs text-gray-600 font-medium">{total} session{total === 1 ? '' : 's'}</span>
                                                          </div>
                                                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                                            {list.map((b) => {
                                                              const items = collectBatchItems(b);
                                                              if (items.length === 0) return null;
                                                              return (
                                                                <div key={`${String(b.batchId)}-${label}`} className="rounded-lg border border-gray-200 bg-white hover:shadow-lg hover:border-indigo-200 transition-all duration-200 overflow-hidden group">
                                                                  <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-3 border-b border-gray-100">
                                                                    <div className="text-xs text-gray-500 font-medium">Batch</div>
                                                                    <div className="text-sm font-semibold text-gray-900 mt-1">{b.batchInternalName || 'N/A'}</div>
                                                                  </div>
                                                                  <div className="px-4 py-3">
                                                                    <div className="divide-y divide-gray-100">
                                                                      {items.map((it, idx) => (
                                                                        <div key={idx} className="py-2">
                                                                          <div className="flex items-center justify-between">
                                                                            <div className="font-semibold text-gray-900 text-sm break-words whitespace-normal">{it.subject}</div>
                                                                            <div className="text-[10px] text-gray-600 ml-2">{it.start} - {it.end}</div>
                                                                          </div>
                                                                          <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-gray-700">
                                                                            <div>
                                                                              <div className="text-gray-500">Hall</div>
                                                                              <div className="font-medium">{it.room || 'TBD'}</div>
                                                                            </div>
                                                                            <div>
                                                                              <div className="text-gray-500">Lecturer</div>
                                                                              <div className="font-medium">{it.staff || 'TBD'}</div>
                                                                            </div>
                                                                          </div>
                                                                          {it.groupChips && it.groupChips.length > 0 ? (
                                                                            <div className="mt-2 flex flex-wrap gap-1">
                                                                              {it.groupChips.map((g, gi) => (
                                                                                <span key={gi} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">{g}</span>
                                                                              ))}
                                                                            </div>
                                                                          ) : null}
                                                                        </div>
                                                                      ))}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              );
                                                            })}
                                                          </div>
                                                        </div>
                                                      ) : null
                                                    );
                                                    return (
                                                      <>
                                                        {renderSection('SE', seBatches, totalSE)}
                                                        {renderSection('NE', neBatches, totalNE)}
                                                        {(totalSE === 0 && totalNE === 0) && (
                                                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                                            {filteredBatchList.map((b) => {
                                                              const items = collectBatchItems(b);
                                                              if (items.length === 0) return null;
                                                              return (
                                                                <div key={String(b.batchId)} className="rounded-lg border border-gray-200">
                                                                  <div className="rounded-t-lg bg-white px-3 py-2">
                                                                    <div className="text-[11px] text-gray-500">Batch</div>
                                                                    <div className="text-sm font-semibold text-gray-800 break-words whitespace-normal">{b.batchInternalName || 'N/A'}</div>
                                                                  </div>
                                                                  <div className="px-4 py-3">
                                                                    <div className="divide-y divide-gray-100">
                                                                      {items.map((it, idx) => (
                                                                        <div key={idx} className="py-2">
                                                                          <div className="flex items-center justify-between">
                                                                            <div className="font-semibold text-gray-900 text-sm break-words whitespace-normal">{it.subject}</div>
                                                                            <div className="text-[10px] text-gray-600 ml-2">{it.start} - {it.end}</div>
                                                                          </div>
                                                                          <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-gray-700">
                                                                            <div>
                                                                              <div className="text-gray-500">Hall</div>
                                                                              <div className="font-medium">{it.room || 'TBD'}</div>
                                                                            </div>
                                                                            <div>
                                                                              <div className="text-gray-500">Lecturer</div>
                                                                              <div className="font-medium">{it.staff || 'TBD'}</div>
                                                                            </div>
                                                                          </div>
                                                                          {it.groupChips && it.groupChips.length > 0 ? (
                                                                            <div className="mt-2 flex flex-wrap gap-1">
                                                                              {it.groupChips.map((g, gi) => (
                                                                                <span key={gi} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">{g}</span>
                                                                              ))}
                                                                            </div>
                                                                          ) : null}
                                                                        </div>
                                                                      ))}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              );
                                                            })}
                                                          </div>
                                                        )}
                                                      </>
                                                    );
                                                  })()}
                                                </>
                                              ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                                  {filteredBatchList.map((b) => {
                                                    const items = collectBatchItems(b);
                                                    if (items.length === 0) return null;
                                                    return (
                                                      <div key={String(b.batchId)} className="rounded-lg border border-gray-200">
                                                        <div className="rounded-t-lg bg-white px-3 py-2">
                                                          <div className="text-[11px] text-gray-500">Batch</div>
                                                          <div className="text-sm font-semibold text-gray-800 break-words whitespace-normal">{b.batchInternalName || 'N/A'}</div>
                                                        </div>
                                                        <div className="px-4 py-3">
                                                          <div className="divide-y divide-gray-100">
                                                            {items.map((it, idx) => (
                                                              <div key={idx} className="py-2">
                                                                <div className="flex items-center justify-between">
                                                                  <div className="font-semibold text-gray-900 text-sm break-words whitespace-normal">{it.subject}</div>
                                                                  <div className="text-[10px] text-gray-600 ml-2">{it.start} - {it.end}</div>
                                                                </div>
                                                                <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] text-gray-700">
                                                                  <div>
                                                                    <div className="text-gray-500">Hall</div>
                                                                    <div className="font-medium">{it.room || 'TBD'}</div>
                                                                  </div>
                                                                  <div>
                                                                    <div className="text-gray-500">Lecturer</div>
                                                                    <div className="font-medium">{it.staff || 'TBD'}</div>
                                                                  </div>
                                                                </div>
                                                                {it.groupChips && it.groupChips.length > 0 ? (
                                                                  <div className="mt-2 flex flex-wrap gap-1">
                                                                    {it.groupChips.map((g, gi) => (
                                                                      <span key={gi} className="inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold leading-none">{g}</span>
                                                                    ))}
                                                                  </div>
                                                                ) : null}
                                                              </div>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            {/* If nothing to show */}
                            {Object.keys(groupedByCourse).filter((k) => !courseFilter || k === courseFilter).length === 0 && !summarySearchQuery && (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-3">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-gray-400">
                                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="text-sm font-medium text-gray-700">No courses match the current filter</div>
                                <div className="text-xs text-gray-500 mt-1">Try adjusting your filter settings</div>
                              </div>
                            )}
                            {/* No search results */}
                            {summarySearchQuery && Object.entries(groupedByCourse)
                              .filter(([courseTitle]) => !courseFilter || courseTitle === courseFilter)
                              .every(([_, types]) => {
                                const mergedGroups = buildMergedGroups(types);
                                return Object.entries(mergedGroups).every(([groupName, batches]) => {
                                  const batchList = Object.values(batches || {});
                                  const filteredBatchList = batchList.filter((b) => filterBySearch(groupName, b, summarySearchQuery));
                                  return filteredBatchList.reduce((acc, b) => acc + collectBatchItems(b).length, 0) === 0;
                                });
                              }) && (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-3">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-amber-500">
                                    <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="text-sm font-medium text-gray-700">No results found for "{summarySearchQuery}"</div>
                                <div className="text-xs text-gray-500 mt-1">Try a different search term</div>
                                <button
                                  onClick={() => setSummarySearchQuery('')}
                                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                  </svg>
                                  Clear search
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                                                      {summaryTab === 'lecturers' && (
                                                        <div className="h-full flex flex-col">
                                                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4 space-y-3">
                                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                                                <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                                                              </svg>
                                                              <span><b className="font-semibold text-gray-900">{new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</b></span>
                                                            </div>
                                                            {/* Lecturer/Others Filter Toggle */}
                                                            <div className="flex items-center justify-center gap-2">
                                                              <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                                                                <button
                                                                  onClick={() => setLecturerFilter('lecturer')}
                                                                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                                                                    lecturerFilter === 'lecturer'
                                                                      ? 'bg-indigo-600 text-white shadow-sm'
                                                                      : 'text-gray-700 hover:bg-gray-100'
                                                                  }`}
                                                                >
                                                                  👨‍🏫 Lecturers
                                                                </button>
                                                                <button
                                                                  onClick={() => setLecturerFilter('others')}
                                                                  className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                                                                    lecturerFilter === 'others'
                                                                      ? 'bg-indigo-600 text-white shadow-sm'
                                                                      : 'text-gray-700 hover:bg-gray-100'
                                                                  }`}
                                                                >
                                                                  👥 Others
                                                                </button>
                                                              </div>
                                                            </div>
                                                            {/* Search bar */}
                                                            <div className="relative">
                                                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-400">
                                                                  <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                                                                </svg>
                                                              </div>
                                                              <input
                                                                type="text"
                                                                value={summarySearchQuery}
                                                                onChange={(e) => setSummarySearchQuery(e.target.value)}
                                                                placeholder="Search by lecturer name, subject, or batch..."
                                                                className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                                              />
                                                              {summarySearchQuery && (
                                                                <button
                                                                  onClick={() => setSummarySearchQuery('')}
                                                                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                                                  title="Clear search"
                                                                >
                                                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                                                  </svg>
                                                                </button>
                                                              )}
                                                            </div>
                                                          </div>
                                                          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 relative scroll-smooth" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                                                            {lecturerSchedulesLoading ? (
                                                              <div className="flex flex-col items-center justify-center py-12">
                                                                <div className="h-10 w-10 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin mb-3" aria-label="Loading" />
                                                                <div className="text-sm text-gray-600">Loading lecturer schedules...</div>
                                                              </div>
                                                            ) : (
                                                              <>
                                                                {(() => {
                                                                  // Use the same search behavior as Staff page
                                                                  const filteredStaff = lecturerSchedulesWithStaged.filter((staff) => {
                                                                    const q = summarySearchQuery.toLowerCase().trim();

                                                                    // First filter by lecturer/others based on positionName
                                                                    const positionName = (staff.positionName || '').toLowerCase();
                                                                    const isLecturer = positionName.includes('lecturer') || positionName.includes('tutor');
                                                                    
                                                                    if (lecturerFilter === 'lecturer' && !isLecturer) return false;
                                                                    if (lecturerFilter === 'others' && isLecturer) return false;
                                                                    
                                                                    // Then apply search filter (but do NOT hide staff just because they have no schedules)
                                                                    if (!q) return true;
                                                                    const staffNameLower = (staff.name || '').toLowerCase();
                                                                    const nameMatch = staffNameLower.includes(q);
                                                                    const empMatch = (staff.employeeNumber || '').toLowerCase().includes(q);
                                                                    const emailMatch = (staff.officeEmail || '').toLowerCase().includes(q);
                                                                    const mobileMatch = (staff.mobileNumber || '').toLowerCase().includes(q);
                                                                    const scheduleMatch = (staff.schedules || []).some((s) =>
                                                                      (s.subjectName || '').toLowerCase().includes(q) ||
                                                                      (s.batchName || '').toLowerCase().includes(q) ||
                                                                      (s.classroom || '').toLowerCase().includes(q)
                                                                    );
                                                                    return nameMatch || empMatch || emailMatch || mobileMatch || scheduleMatch;
                                                                  });

                                                                  const to12h = (time24) => {
                                                                    if (!time24) return '';
                                                                    const [h, m] = time24.split(':');
                                                                    let hours = parseInt(h, 10);
                                                                    const modifier = hours >= 12 ? 'PM' : 'AM';
                                                                    hours = hours % 12 || 12;
                                                                    return `${hours}:${m} ${modifier}`;
                                                                  };

                                                                  const timeToMinutes = (time24) => {
                                                                    const [h, m] = time24.split(':').map((n) => parseInt(n, 10));
                                                                    return (h || 0) * 60 + (m || 0);
                                                                  };

                                                                  // Time slots from 8:30 AM to 5:00 PM
                                                                  const startMinutes = 8 * 60 + 30; // 8:30 AM
                                                                  const endMinutes = 17 * 60; // 5:00 PM
                                                                  const totalMinutes = endMinutes - startMinutes;

                                                                  return (
                                                                    <>
                                                                      {filteredStaff.length === 0 && !lecturerSchedulesLoading && (
                                                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                                                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-3">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-amber-500">
                                                                              <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clipRule="evenodd" />
                                                                            </svg>
                                                                          </div>
                                                                          <div className="text-sm font-medium text-gray-700">
                                                                            {summarySearchQuery 
                                                                              ? `No results found for "${summarySearchQuery}"` 
                                                                              : `No ${lecturerFilter === 'lecturer' ? 'lecturers' : 'other staff'} found for this date`}
                                                                          </div>
                                                                          {summarySearchQuery && (
                                                                            <button
                                                                              onClick={() => setSummarySearchQuery('')}
                                                                              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
                                                                            >
                                                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                                                              </svg>
                                                                              Clear search
                                                                            </button>
                                                                          )}
                                                                        </div>
                                                                      )}
                                                                      <div className="grid grid-cols-1 gap-4">
                                                                        {filteredStaff.map((staff) => {
                                                                          const genderIcon = staff.gender === 'male' ? '👨' : staff.gender === 'female' ? '👩' : '👤';
                                                                          const uniqueBatchCount = new Set(
                                                                            (staff.schedules || [])
                                                                              .map((s) => (s.batchName || '').toString().trim())
                                                                              .filter(Boolean)
                                                                          ).size;
                                                                          return (
                                                                            <div key={staff.staffId} className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                                                                              {/* Staff Header */}
                                                                              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-4 border-b border-gray-200">
                                                                                <div className="flex items-center gap-4">
                                                                                  {/* Staff Image */}
                                                                                  <div className="flex-shrink-0">
                                                                                    <div className="relative w-16 h-16">
                                                                                      {(() => {
                                                                                        const hasImage = staff.imagePath && !staffImageErrors[staff.staffId];
                                                                                        if (hasImage) {
                                                                                          return (
                                                                                            <img
                                                                                              src={getImageURL(staff.imagePath)}
                                                                                              alt={staff.name}
                                                                                              className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-md"
                                                                                              onError={() => {
                                                                                                setStaffImageErrors(prev => ({ ...prev, [staff.staffId]: true }));
                                                                                              }}
                                                                                            />
                                                                                          );
                                                                                        }
                                                                                        // Fallback: use placehold.co when there is no valid staff image
                                                                                        const placeholderText = encodeURIComponent(staff.name || 'Staff');
                                                                                        const fallbackUrl = `https://placehold.co/128x128?text=${placeholderText}`;
                                                                                        return (
                                                                                          <img
                                                                                            src={fallbackUrl}
                                                                                            alt={staff.name}
                                                                                            className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-md bg-gray-100"
                                                                                          />
                                                                                        );
                                                                                      })()}
                                                                                    </div>
                                                                                  </div>
                                                                                  {/* Staff Details */}
                                                                                  <div className="flex-1 min-w-0">
                                                                                    <div className="flex items-center gap-2">
                                                                                      <h3 className="text-lg font-bold text-gray-900">{staff.title ? `${staff.title}. ` : ''}{staff.name}</h3>
                                                                                      {/* Prefer showing staff position instead of legacy type */}
                                                                                      {staff.positionName ? (
                                                                                        <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800">
                                                                                          {staff.positionName}
                                                                                        </span>
                                                                                      ) : staff.type ? (
                                                                                        <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
                                                                                          {staff.type}
                                                                                        </span>
                                                                                      ) : null}
                                                                                      {staff.roster && (
                                                                                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                                                                          staff.roster.dayType === 'off day' 
                                                                                            ? 'bg-red-100 text-red-800' 
                                                                                            : staff.roster.dayType === 'morning half day'
                                                                                            ? 'bg-amber-100 text-amber-800'
                                                                                            : 'bg-blue-100 text-blue-800'
                                                                                        }`}>
                                                                                          {staff.roster.dayType === 'off day' 
                                                                                            ? '🏠 Off Day' 
                                                                                            : staff.roster.dayType === 'morning half day'
                                                                                            ? '🌅 Morning Half'
                                                                                            : '🌆 Evening Half'}
                                                                                        </span>
                                                                                      )}
                                                                                    </div>
                                                                                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600">
                                                                                      {staff.officeEmail && (
                                                                                        <div className="flex items-center gap-1">
                                                                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                            <path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z" />
                                                                                            <path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />
                                                                                          </svg>
                                                                                          {staff.officeEmail}
                                                                                        </div>
                                                                                      )}
                                                                                      {staff.mobileNumber && (
                                                                                        <div className="flex items-center gap-1">
                                                                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                            <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 0 1 3.5 2h1.148a1.5 1.5 0 0 1 1.465 1.175l.716 3.223a1.5 1.5 0 0 1-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 0 0 6.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 0 1 1.767-1.052l3.223.716A1.5 1.5 0 0 1 18 15.352V16.5a1.5 1.5 0 0 1-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 0 1 2.43 8.326 13.019 13.019 0 0 1 2 5V3.5Z" clipRule="evenodd" />
                                                                                          </svg>
                                                                                          {staff.mobileNumber}
                                                                                        </div>
                                                                                      )}
                                                                                    </div>
                                                                                  </div>
                                                                                  {/* Schedule Count Badge */}
                                                                                  <div className="flex-shrink-0">
                                                                                    <div className="inline-flex flex-col items-center justify-center bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
                                                                                      <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">{uniqueBatchCount}</div>
                                                                                      <div className="text-xs text-gray-600 font-medium">{uniqueBatchCount === 1 ? 'Batch' : 'Batches'}</div>
                                                                                    </div>
                                                                                  </div>
                                                                                </div>
                                                                                
                                                                                {/* Roster Information Section */}
                                                                                {staff.roster && (
                                                                                  <div className={`mt-3 mx-5 px-4 py-3 rounded-lg border-2 ${
                                                                                    staff.roster.dayType === 'off day'
                                                                                      ? 'bg-red-50 border-red-200'
                                                                                      : staff.roster.dayType === 'morning half day'
                                                                                      ? 'bg-amber-50 border-amber-200'
                                                                                      : 'bg-blue-50 border-blue-200'
                                                                                  }`}>
                                                                                    <div className="flex items-center gap-3">
                                                                                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-2xl ${
                                                                                        staff.roster.dayType === 'off day'
                                                                                          ? 'bg-red-100'
                                                                                          : staff.roster.dayType === 'morning half day'
                                                                                          ? 'bg-amber-100'
                                                                                          : 'bg-blue-100'
                                                                                      }`}>
                                                                                        {staff.roster.dayType === 'off day' 
                                                                                          ? '🏠' 
                                                                                          : staff.roster.dayType === 'morning half day'
                                                                                          ? '🌅'
                                                                                          : '🌆'}
                                                                                      </div>
                                                                                      <div className="flex-1">
                                                                                        <div className={`text-sm font-bold ${
                                                                                          staff.roster.dayType === 'off day'
                                                                                            ? 'text-red-800'
                                                                                            : staff.roster.dayType === 'morning half day'
                                                                                            ? 'text-amber-800'
                                                                                            : 'text-blue-800'
                                                                                        }`}>
                                                                                          {staff.roster.dayType === 'off day' 
                                                                                            ? 'Off Day' 
                                                                                            : staff.roster.dayType === 'morning half day'
                                                                                            ? 'Morning Half Day'
                                                                                            : 'Evening Half Day'}
                                                                                        </div>
                                                                                        <div className={`text-xs mt-0.5 ${
                                                                                          staff.roster.dayType === 'off day'
                                                                                            ? 'text-red-600'
                                                                                            : staff.roster.dayType === 'morning half day'
                                                                                            ? 'text-amber-600'
                                                                                            : 'text-blue-600'
                                                                                        }`}>
                                                                                          {staff.roster.dayType === 'off day' 
                                                                                            ? 'Staff is not scheduled to work today' 
                                                                                            : staff.roster.dayType === 'morning half day'
                                                                                            ? 'Available: 12:30 PM - 5:00 PM (Arrives at 12:30 PM)'
                                                                                            : 'Available: 9:00 AM - 1:00 PM (Leaves at 1:00 PM)'}
                                                                                        </div>
                                                                                      </div>
                                                                                      <div className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold ${
                                                                                        staff.roster.dayType === 'off day'
                                                                                          ? 'bg-red-200 text-red-900'
                                                                                          : staff.roster.dayType === 'morning half day'
                                                                                          ? 'bg-amber-200 text-amber-900'
                                                                                          : 'bg-blue-200 text-blue-900'
                                                                                      }`}>
                                                                                        {staff.roster.rosterDay}
                                                                                      </div>
                                                                                    </div>
                                                                                  </div>
                                                                                )}
                                                                                {/* Staff Unavailability Section */}
                                                                                {(() => {
                                                                                  const unavailList = staffUnavailabilityByStaffId.get(staff.staffId) || [];
                                                                                  if (!unavailList.length) return null;
                                                                                  return (
                                                                                    <div className="mt-3 mx-5 space-y-2">
                                                                                      {unavailList.map((rec) => {
                                                                                        const isLeave = rec.type === 'leave';
                                                                                        const hasTime = rec.startTime && rec.endTime;
                                                                                        return (
                                                                                          <div
                                                                                            key={rec.id}
                                                                                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-2 ${
                                                                                              isLeave
                                                                                                ? 'bg-orange-50 border-orange-200'
                                                                                                : 'bg-yellow-50 border-yellow-200'
                                                                                            }`}
                                                                                          >
                                                                                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-base ${
                                                                                              isLeave ? 'bg-orange-100' : 'bg-yellow-100'
                                                                                            }`}>
                                                                                              {isLeave ? '🏖️' : '⚠️'}
                                                                                            </div>
                                                                                            <div className="flex-1 min-w-0">
                                                                                              <div className={`text-xs font-bold ${isLeave ? 'text-orange-800' : 'text-yellow-800'}`}>
                                                                                                {isLeave ? 'On Leave' : 'Unavailable'}
                                                                                                {!isLeave && hasTime
                                                                                                  ? ` · ${to12h(rec.startTime)} – ${to12h(rec.endTime)}`
                                                                                                  : ' · Full Day'}
                                                                                              </div>
                                                                                              {rec.reason && (
                                                                                                <div className={`text-xs mt-0.5 truncate ${isLeave ? 'text-orange-600' : 'text-yellow-700'}`}>
                                                                                                  {rec.reason}
                                                                                                </div>
                                                                                              )}
                                                                                            </div>
                                                                                            <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                                                                                              isLeave ? 'bg-orange-200 text-orange-900' : 'bg-yellow-200 text-yellow-900'
                                                                                            }`}>
                                                                                              {isLeave ? 'Leave' : 'Unavailability'}
                                                                                            </span>
                                                                                          </div>
                                                                                        );
                                                                                      })}
                                                                                    </div>
                                                                                  );
                                                                                })()}
                                                                              </div>
                                                                              {/* Timeline Section */}
                                                                              <div className="px-5 py-4">
                                                                                {staff.roster?.dayType === 'off day' ? (
                                                                                  // Off Day Display
                                                                                  <div className="text-center py-8 text-gray-500">
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12 mx-auto mb-2 text-gray-400">
                                                                                      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clipRule="evenodd" />
                                                                                    </svg>
                                                                                    <div className="text-sm font-medium text-gray-600">Off Day</div>
                                                                                    <div className="text-xs mt-1 text-gray-500">Staff is not scheduled to work today</div>
                                                                                  </div>
                                                                                ) : (
                                                                                  <div className="space-y-4">
                                                                                    {/* Timeline Header with Legend */}
                                                                                    <div className="flex items-center justify-between text-xs border-b border-gray-200 pb-2">
                                                                                      <span className="text-gray-600 font-semibold">8:30 AM</span>
                                                                                      <div className="flex items-center gap-3">
                                                                                        <div className="flex items-center gap-1.5">
                                                                                          <div className="w-4 h-4 bg-gradient-to-r from-green-100 to-green-200 rounded border border-green-300"></div>
                                                                                          <span className="text-green-700 font-medium">Free</span>
                                                                                        </div>
                                                                                        <div className="flex items-center gap-1.5">
                                                                                          <div className="w-4 h-4 bg-gradient-to-r from-red-500 to-red-600 rounded border border-white"></div>
                                                                                          <span className="text-red-700 font-medium">Busy</span>
                                                                                        </div>
                                                                                        {/* Show lunch only for full-day staff */}
                                                                                        {(!staff.roster || (staff.roster.dayType !== 'morning half day' && staff.roster.dayType !== 'evening half day')) && (
                                                                                          <div className="flex items-center gap-1.5">
                                                                                            <div className="w-4 h-4 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded border-2 border-yellow-300"></div>
                                                                                            <span className="text-yellow-700 font-medium">🍽️ Lunch</span>
                                                                                          </div>
                                                                                        )}
                                                                                        {staff.roster && staff.roster.dayType !== 'off day' && (
                                                                                          <div className="flex items-center gap-1.5">
                                                                                            <div className={`w-4 h-4 rounded border-2 ${
                                                                                              staff.roster.dayType === 'morning half day'
                                                                                                ? 'bg-gradient-to-r from-purple-100 to-purple-200 border-purple-300'
                                                                                                : 'bg-gradient-to-r from-indigo-100 to-indigo-200 border-indigo-300'
                                                                                            }`}></div>
                                                                                            <span className={`font-medium ${
                                                                                              staff.roster.dayType === 'morning half day'
                                                                                                ? 'text-purple-700'
                                                                                                : 'text-indigo-700'
                                                                                            }`}>
                                                                                              {staff.roster.dayType === 'morning half day' ? 'Left Office' : 'Not Arrived'}
                                                                                            </span>
                                                                                          </div>
                                                                                        )}
                                                                                        {(staffUnavailabilityByStaffId.get(staff.staffId) || []).some(r => r.type === 'leave') && (
                                                                                          <div className="flex items-center gap-1.5">
                                                                                            <div className="w-4 h-4 rounded border-2 bg-orange-400 border-orange-500"></div>
                                                                                            <span className="text-orange-700 font-medium">🏖️ Leave</span>
                                                                                          </div>
                                                                                        )}
                                                                                        {(staffUnavailabilityByStaffId.get(staff.staffId) || []).some(r => r.type === 'unavailability') && (
                                                                                          <div className="flex items-center gap-1.5">
                                                                                            <div className="w-4 h-4 rounded border-2 bg-amber-300 border-amber-500"></div>
                                                                                            <span className="text-amber-700 font-medium">⚠️ Unavailable</span>
                                                                                          </div>
                                                                                        )}
                                                                                      </div>
                                                                                      <span className="text-gray-600 font-semibold">5:00 PM</span>
                                                                                    </div>
                                                                                    {/* Timeline Bar */}
                                                                                    <div className="relative h-16 bg-gradient-to-r from-green-100 to-green-200 rounded-lg overflow-hidden">
                                                                                      {/* Time markers */}
                                                                                      <div className="absolute inset-0 flex">
                                                                                        {Array.from({ length: 9 }).map((_, i) => (
                                                                                          <div key={i} className="flex-1 border-r border-gray-300 last:border-r-0" />
                                                                                        ))}
                                                                                      </div>
                                                                                      {/* Lunch time indicator (1:00 PM - 2:00 PM) - Only for full-day staff */}
                                                                                      {!staff.roster || staff.roster.dayType === 'off day' || (staff.roster.dayType !== 'morning half day' && staff.roster.dayType !== 'evening half day') ? (() => {
                                                                                        const lunchStart = 13 * 60; // 1:00 PM in minutes (780)
                                                                                        const lunchEnd = 14 * 60;   // 2:00 PM in minutes (840)
                                                                                        const left = ((lunchStart - startMinutes) / totalMinutes) * 100;
                                                                                        const width = ((lunchEnd - lunchStart) / totalMinutes) * 100;
                                                                                        return (
                                                                                          <div
                                                                                            className="absolute top-0 h-full bg-gradient-to-r from-yellow-100 to-yellow-200 border-2 border-yellow-300 flex items-center justify-center z-10"
                                                                                            style={{ left: `${left}%`, width: `${width}%` }}
                                                                                            title="Lunch Break: 1:00 PM - 2:00 PM"
                                                                                          >
                                                                                            <div className="text-yellow-800 text-[10px] font-bold px-1 text-center leading-tight">
                                                                                              <div>1:00 PM</div>
                                                                                              <div className="text-[9px] font-extrabold">🍽️ LUNCH</div>
                                                                                              <div>2:00 PM</div>
                                                                                            </div>
                                                                                          </div>
                                                                                        );
                                                                                      })() : null}
                                                                                      {/* Roster-based unavailability (Morning/Evening Half Day) */}
                                                                                      {staff.roster && staff.roster.dayType !== 'off day' && (() => {
                                                                                        let rosterUnavailStart;
                                                                                        let rosterUnavailEnd;
                                                                                        let rosterLabel;
                                                                                        let rosterColor;
                                                                                        
                                                                                        if (staff.roster.dayType === 'morning half day') {
                                                                                          // Morning half: works 12:30 PM - 5:00 PM, unavailable before 12:30 PM
                                                                                          rosterUnavailStart = startMinutes; // 8:30 AM
                                                                                          rosterUnavailEnd = 12 * 60 + 30;   // 12:30 PM
                                                                                          rosterLabel = 'Not Available (Not Yet Arrived)';
                                                                                          rosterColor = 'from-purple-100 to-purple-200 border-purple-300';
                                                                                        } else {
                                                                                          // Evening half: works 9:00 AM - 1:00 PM, unavailable after 1:00 PM
                                                                                          rosterUnavailStart = 13 * 60;  // 1:00 PM
                                                                                          rosterUnavailEnd = endMinutes; // 5:00 PM
                                                                                          rosterLabel = 'Not Available (Left Office)';
                                                                                          rosterColor = 'from-indigo-100 to-indigo-200 border-indigo-300';
                                                                                        }
                                                                                        
                                                                                        const left = ((rosterUnavailStart - startMinutes) / totalMinutes) * 100;
                                                                                        const width = ((rosterUnavailEnd - rosterUnavailStart) / totalMinutes) * 100;
                                                                                        
                                                                                        return (
                                                                                          <div
                                                                                            className={`absolute top-0 h-full bg-gradient-to-r ${rosterColor} border-2 flex items-center justify-center z-5`}
                                                                                            style={{ left: `${left}%`, width: `${width}%` }}
                                                                                            title={rosterLabel}
                                                                                          >
                                                                                            <div className={`text-[10px] font-bold px-1 text-center leading-tight ${
                                                                                              staff.roster.dayType === 'morning half day' ? 'text-purple-800' : 'text-indigo-800'
                                                                                            }`}>
                                                                                              <div>{to12h(`${Math.floor(rosterUnavailStart / 60)}:${(rosterUnavailStart % 60).toString().padStart(2, '0')}`)}</div>
                                                                                              <div className="text-[8px] font-extrabold">
                                                                                                {staff.roster.dayType === 'morning half day' ? 'NOT ARRIVED' : 'LEFT OFFICE'}
                                                                                              </div>
                                                                                              <div>{to12h(`${Math.floor(rosterUnavailEnd / 60)}:${(rosterUnavailEnd % 60).toString().padStart(2, '0')}`)}</div>
                                                                                            </div>
                                                                                          </div>
                                                                                        );
                                                                                      })()}
                                                                                      {/* Leave / Unavailability blocks from staff_unavailability */}
                                                                                      {(staffUnavailabilityByStaffId.get(staff.staffId) || []).map((rec, unavailIdx) => {
                                                                                        const DAY_S = 8 * 60 + 30;
                                                                                        const DAY_E = 17 * 60;
                                                                                        const isLeave = rec.type === 'leave';
                                                                                        let blockStart, blockEnd;
                                                                                        if (isLeave || (!rec.startTime && !rec.endTime)) {
                                                                                          blockStart = DAY_S;
                                                                                          blockEnd = DAY_E;
                                                                                        } else {
                                                                                          const sp = String(rec.startTime).split(':').map(Number);
                                                                                          const ep = String(rec.endTime).split(':').map(Number);
                                                                                          blockStart = (sp[0] || 0) * 60 + (sp[1] || 0);
                                                                                          blockEnd = (ep[0] || 0) * 60 + (ep[1] || 0);
                                                                                        }
                                                                                        blockStart = Math.max(blockStart, DAY_S);
                                                                                        blockEnd = Math.min(blockEnd, DAY_E);
                                                                                        if (blockEnd <= blockStart) return null;
                                                                                        const left = ((blockStart - startMinutes) / totalMinutes) * 100;
                                                                                        const width = ((blockEnd - blockStart) / totalMinutes) * 100;
                                                                                        const startLabel = `${String(Math.floor(blockStart / 60)).padStart(2, '0')}:${String(blockStart % 60).padStart(2, '0')}`;
                                                                                        const endLabel = `${String(Math.floor(blockEnd / 60)).padStart(2, '0')}:${String(blockEnd % 60).padStart(2, '0')}`;
                                                                                        return (
                                                                                          <div
                                                                                            key={`unavail-bar-${rec.id}`}
                                                                                            className={`absolute top-0 h-full flex items-center justify-center border-2 ${
                                                                                              isLeave
                                                                                                ? 'bg-orange-400/85 border-orange-600'
                                                                                                : 'bg-amber-300/85 border-amber-500'
                                                                                            }`}
                                                                                            style={{ left: `${left}%`, width: `${width}%`, zIndex: 25 + unavailIdx }}
                                                                                            title={`${isLeave ? '🏖️ On Leave' : '⚠️ Unavailable'}${rec.reason ? ': ' + rec.reason : ''}`}
                                                                                          >
                                                                                            {width > 6 && (
                                                                                              <div className={`text-[9px] font-bold px-1 text-center leading-tight select-none ${
                                                                                                isLeave ? 'text-orange-950' : 'text-amber-950'
                                                                                              }`}>
                                                                                                {!isLeave && rec.startTime && <div>{to12h(rec.startTime)}</div>}
                                                                                                <div className="text-[8px] font-extrabold">{isLeave ? '🏖️ LEAVE' : '⚠️ UNAVAIL'}</div>
                                                                                                {!isLeave && rec.endTime && <div>{to12h(rec.endTime)}</div>}
                                                                                              </div>
                                                                                            )}
                                                                                          </div>
                                                                                        );
                                                                                      })}
                                                                                      {/* Free time slots with labels */}
                                                                                      {(() => {
                                                                                        // Lunch time constants (only for full-day staff)
                                                                                        const lunchStart = 13 * 60; // 1:00 PM = 780 minutes
                                                                                        const lunchEnd = 14 * 60;   // 2:00 PM = 840 minutes
                                                                                        
                                                                                        // Check if staff has half-day roster (no lunch break for half-day staff)
                                                                                        const isHalfDay = staff.roster && (
                                                                                          staff.roster.dayType === 'morning half day' || 
                                                                                          staff.roster.dayType === 'evening half day'
                                                                                        );
                                                                                        
                                                                                        // Roster unavailability constants
                                                                                        let rosterUnavailStart = null;
                                                                                        let rosterUnavailEnd = null;
                                                                                        
                                                                                        if (staff.roster) {
                                                                                          if (staff.roster.dayType === 'morning half day') {
                                                                                            // Morning half: works 12:30 PM - 5:00 PM, unavailable before 12:30 PM
                                                                                            rosterUnavailStart = startMinutes; // 8:30 AM
                                                                                            rosterUnavailEnd = 12 * 60 + 30;   // 12:30 PM
                                                                                          } else if (staff.roster.dayType === 'evening half day') {
                                                                                            // Evening half: works 9:00 AM - 1:00 PM, unavailable after 1:00 PM
                                                                                            rosterUnavailStart = 13 * 60;  // 1:00 PM
                                                                                            rosterUnavailEnd = endMinutes; // 5:00 PM
                                                                                          } else if (staff.roster.dayType === 'off day') {
                                                                                            // Entire day is off
                                                                                            rosterUnavailStart = startMinutes;
                                                                                            rosterUnavailEnd = endMinutes;
                                                                                          }
                                                                                        }
                                                                                        
                                                                                        // Get all unavailable periods (lunch only for full-day staff + roster)
                                                                                        const blockedPeriods = [];
                                                                                        
                                                                                        // Add lunch only for full-day staff
                                                                                        if (!isHalfDay) {
                                                                                          blockedPeriods.push({ start: lunchStart, end: lunchEnd });
                                                                                        }
                                                                                        
                                                                                        // Add roster unavailability
                                                                                        if (rosterUnavailStart !== null && rosterUnavailEnd !== null) {
                                                                                          blockedPeriods.push({ start: rosterUnavailStart, end: rosterUnavailEnd });
                                                                                        }
                                                                                        
                                                                                        // Sort blocked periods
                                                                                        blockedPeriods.sort((a, b) => a.start - b.start);
                                                                                        
                                                                                        // Sort schedules by start time
                                                                                        const sortedSchedules = [...staff.schedules].sort((a, b) => 
                                                                                          timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
                                                                                        );
                                                                                        const freeSlots = [];
                                                                                        
                                                                                        // Helper to add a free slot, splitting around blocked periods
                                                                                        const addFreeSlot = (slotStart, slotEnd) => {
                                                                                          if (slotStart >= slotEnd) return;
                                                                                          
                                                                                          let currentStart = slotStart;
                                                                                          
                                                                                          for (const blocked of blockedPeriods) {
                                                                                            if (blocked.end <= currentStart || blocked.start >= slotEnd) {
                                                                                              // No overlap
                                                                                              continue;
                                                                                            }
                                                                                            
                                                                                            // Add free time before blocked period
                                                                                            if (currentStart < blocked.start && blocked.start < slotEnd) {
                                                                                              const endTime = Math.min(blocked.start, slotEnd);
                                                                                              freeSlots.push({
                                                                                                start: `${Math.floor(currentStart / 60).toString().padStart(2, '0')}:${(currentStart % 60).toString().padStart(2, '0')}`,
                                                                                                end: `${Math.floor(endTime / 60).toString().padStart(2, '0')}:${(endTime % 60).toString().padStart(2, '0')}`,
                                                                                                startMin: currentStart,
                                                                                                endMin: endTime,
                                                                                              });
                                                                                            }
                                                                                            
                                                                                            // Move past the blocked period
                                                                                            currentStart = Math.max(currentStart, blocked.end);
                                                                                          }
                                                                                          
                                                                                          // Add remaining free time after all blocked periods
                                                                                          if (currentStart < slotEnd) {
                                                                                            freeSlots.push({
                                                                                              start: `${Math.floor(currentStart / 60).toString().padStart(2, '0')}:${(currentStart % 60).toString().padStart(2, '0')}`,
                                                                                              end: `${Math.floor(slotEnd / 60).toString().padStart(2, '0')}:${(slotEnd % 60).toString().padStart(2, '0')}`,
                                                                                              startMin: currentStart,
                                                                                              endMin: slotEnd,
                                                                                            });
                                                                                          }
                                                                                        };
                                                                                        
                                                                                        // Calculate free slots
                                                                                        let currentTime = startMinutes; // 8:30 AM in minutes
                                                                                        
                                                                                        sortedSchedules.forEach((schedule) => {
                                                                                          const schedStart = timeToMinutes(schedule.startTime);
                                                                                          const schedEnd = timeToMinutes(schedule.endTime);
                                                                                          
                                                                                          // If there's a gap before this schedule
                                                                                          if (schedStart > currentTime) {
                                                                                            addFreeSlot(currentTime, schedStart);
                                                                                          }
                                                                                          currentTime = Math.max(currentTime, schedEnd);
                                                                                        });
                                                                                        
                                                                                        // Add final free slot if there's time left until 5:00 PM
                                                                                        if (currentTime < endMinutes) {
                                                                                          addFreeSlot(currentTime, endMinutes);
                                                                                        }
                                                                                        
                                                                                        // If no schedules at all, add free time for entire available day
                                                                                        if (sortedSchedules.length === 0) {
                                                                                          addFreeSlot(startMinutes, endMinutes);
                                                                                        }
                                                                                        
                                                                                        return freeSlots.map((slot, idx) => {
                                                                                          const left = ((slot.startMin - startMinutes) / totalMinutes) * 100;
                                                                                          const width = ((slot.endMin - slot.startMin) / totalMinutes) * 100;
                                                                                          return (
                                                                                            <div
                                                                                              key={`free-${idx}`}
                                                                                              className="absolute top-0 h-full flex items-center justify-center"
                                                                                              style={{ left: `${left}%`, width: `${width}%`, zIndex: 10 + idx }}
                                                                                            >
                                                                                              {width > 8 && (
                                                                                                <div className="text-green-700 text-[10px] font-bold px-1 text-center leading-tight">
                                                                                                  <div>{to12h(slot.start)}</div>
                                                                                                  <div className="text-[8px] opacity-75">Free</div>
                                                                                                  <div>{to12h(slot.end)}</div>
                                                                                                </div>
                                                                                              )}
                                                                                            </div>
                                                                                          );
                                                                                        });
                                                                                      })()}
                                                                                      {/* Scheduled blocks (red for busy time) */}
                                                                                      {staff.schedules.map((schedule, idx) => {
                                                                                        const startMin = timeToMinutes(schedule.startTime);
                                                                                        const endMin = timeToMinutes(schedule.endTime);
                                                                                        const left = ((startMin - startMinutes) / totalMinutes) * 100;
                                                                                        const width = ((endMin - startMin) / totalMinutes) * 100;
                                                                                        return (
                                                                                          <div
                                                                                            key={idx}
                                                                                            className="absolute top-0 h-full bg-gradient-to-r from-red-500 to-red-600 shadow-md border-2 border-white transition-all hover:scale-105 hover:z-10 cursor-pointer group flex items-center justify-center"
                                                                                            style={{ left: `${left}%`, width: `${width}%`, zIndex: 10 + idx }}
                                                                                            title={`${schedule.subjectName} (${to12h(schedule.startTime)} - ${to12h(schedule.endTime)})`}
                                                                                          >
                                                                                            {/* Time label on block - only show if wide enough */}
                                                                                            {width > 8 && (
                                                                                              <div className="text-white text-[10px] font-bold px-1 text-center leading-tight">
                                                                                                <div>{to12h(schedule.startTime)}</div>
                                                                                                <div className="text-[8px] opacity-75">-</div>
                                                                                                <div>{to12h(schedule.endTime)}</div>
                                                                                              </div>
                                                                                            )}
                                                                                            {/* Tooltip on hover */}
                                                                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg z-20">
                                                                                              <div className="font-semibold">{schedule.subjectName}</div>
                                                                                              <div className="text-gray-300">{to12h(schedule.startTime)} - {to12h(schedule.endTime)}</div>
                                                                                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                                                            </div>
                                                                                          </div>
                                                                                        );
                                                                                      })}
                                                                                    </div>
                                                                                    {/* Schedule List - Only show if there are schedules */}
                                                                                    {staff.schedules.length > 0 && (
                                                                                      <div className="space-y-2">
                                                                                        <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                                                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                            <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                                                                                          </svg>
                                                                                          Scheduled Classes
                                                                                        </div>
                                                                                        <div className="space-y-1.5">
                                                                                          {staff.schedules.map((schedule, idx) => (
                                                                                            <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-all border border-gray-200">
                                                                                              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-indigo-600"></div>
                                                                                              <div className="flex-1 min-w-0">
                                                                                                <div className="text-sm font-semibold text-gray-900">{schedule.subjectName}</div>
                                                                                                <div className="text-xs text-gray-600">{schedule.batchName}</div>
                                                                                              </div>
                                                                                              <div className="flex-shrink-0 text-right">
                                                                                                <div className="text-xs font-semibold text-indigo-600">{to12h(schedule.startTime)} - {to12h(schedule.endTime)}</div>
                                                                                                {schedule.classroom && (
                                                                                                  <div className="text-xs text-gray-500 mt-0.5">📍 {schedule.classroom}</div>
                                                                                                )}
                                                                                              </div>
                                                                                            </div>
                                                                                          ))}
                                                                                        </div>
                                                                                      </div>
                                                                                    )}
                                                                                  </div>
                                                                                )}
                                                                              </div>
                                                                            </div>
                                                                          );
                                                                        })}
                                                                      </div>
                                                                    </>
                                                                  );
                                                                })()}
                                                              </>
                                                            )}
                                                          </div>
                                                        </div>
                                                      )}
                                                      {summaryTab === 'available' && (
                                                        <div className="h-full flex flex-col">
                                                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4 space-y-4">
                                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                                                <path fillRule="evenodd" d="M10 2a.75.75 0 0 1 .75.75V4.5h.75A4.75 4.75 0 0 1 16.25 9.25v.943a3.751 3.751 0 1 1-1.5 0V9.25A3.25 3.25 0 0 0 11.5 6H10v8.25a.75.75 0 0 1-1.5 0V6H7.75A3.25 3.25 0 0 0 4.5 9.25v.943a3.751 3.751 0 1 1-1.5 0V9.25A4.75 4.75 0 0 1 7.25 4.5h.75V2.75A.75.75 0 0 1 10 2Zm6.5 11.5a2.25 2.25 0 1 0-4.5 0 2.25 2.25 0 0 0 4.5 0Z" clipRule="evenodd" />
                                                              </svg>
                                                              <span>
                                                                <b className="font-semibold text-gray-900">
                                                                  Available lecturers on {new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
                                                                    weekday: 'long',
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric',
                                                                  })}
                                                                </b>
                                                              </span>
                                                            </div>
                                                            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                                                              <div className="flex flex-col md:flex-row md:items-end gap-3 flex-1">
                                                                <div className="flex-1">
                                                                  <label className="block text-xs font-semibold text-gray-700 mb-1">Start time</label>
                                                                  <input
                                                                    type="time"
                                                                    value={availableStart}
                                                                    onChange={(e) => setAvailableStart(e.target.value)}
                                                                    className="block w-full rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                                                  />
                                                                </div>
                                                                <div className="flex-1">
                                                                  <label className="block text-xs font-semibold text-gray-700 mb-1">End time</label>
                                                                  <input
                                                                    type="time"
                                                                    value={availableEnd}
                                                                    onChange={(e) => setAvailableEnd(e.target.value)}
                                                                    className="block w-full rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                                                  />
                                                                </div>
                                                              </div>
                                                              <div className="flex flex-col items-stretch md:items-end gap-2">
                                                                <button
                                                                  type="button"
                                                                  onClick={handleFindAvailableLecturers}
                                                                  className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-1"
                                                                >
                                                                  <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                    className="w-4 h-4 mr-1.5"
                                                                  >
                                                                    <path
                                                                      fillRule="evenodd"
                                                                      d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
                                                                      clipRule="evenodd"
                                                                    />
                                                                  </svg>
                                                                  Find available lecturers and classrooms
                                                                </button>
                                                                <p className="text-xs text-gray-500 max-w-xs text-right">
                                                                  Uses roster, lunch break, and scheduled classes to find who is free for the full selected time range.
                                                                </p>
                                                              </div>
                                                            </div>
                                                            {availableError && (
                                                              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                                                {availableError}
                                                              </div>
                                                            )}
                                                          </div>
                                                          <div
                                                            className="flex-1 overflow-y-auto px-6 py-5 space-y-4 relative scroll-smooth"
                                                            style={{ maxHeight: 'calc(100vh - 300px)' }}
                                                          >
                                                            {availableResults === null && (
                                                              <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-gray-600">
                                                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-50 mb-3">
                                                                  <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                    className="w-7 h-7 text-indigo-500"
                                                                  >
                                                                    <path
                                                                      fillRule="evenodd"
                                                                      d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
                                                                      clipRule="evenodd"
                                                                    />
                                                                  </svg>
                                                                </div>
                                                                <div className="font-medium mb-1">Select a time range and click Find.</div>
                                                                <div className="text-xs text-gray-500">
                                                                  Recommended window is between 8:30 AM and 5:00 PM.
                                                                </div>
                                                              </div>
                                                            )}
                                                            {availableResults &&
                                                              availableResults.lecturers &&
                                                              availableResults.classrooms &&
                                                              availableResults.lecturers.length === 0 &&
                                                              availableResults.classrooms.length === 0 && (
                                                                <div className="flex flex-col items-center justify-center py-12 text-center text-sm text-gray-600">
                                                                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 mb-3">
                                                                    <svg
                                                                      xmlns="http://www.w3.org/2000/svg"
                                                                      viewBox="0 0 20 20"
                                                                      fill="currentColor"
                                                                      className="w-7 h-7 text-amber-500"
                                                                    >
                                                                      <path
                                                                        fillRule="evenodd"
                                                                        d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm0 4a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 6Zm0 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
                                                                        clipRule="evenodd"
                                                                      />
                                                                    </svg>
                                                                  </div>
                                                                  <div className="font-medium mb-1">No lecturers or classrooms free in this time range.</div>
                                                                  <div className="text-xs text-gray-500">
                                                                    Try adjusting the time window or checking another day.
                                                                  </div>
                                                                </div>
                                                              )}
                                                            {availableResults &&
                                                              availableResults.lecturers &&
                                                              availableResults.classrooms && (
                                                                <div className="flex flex-col gap-3">
                                                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filter by status</div>
                                                                    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm text-xs font-medium">
                                                                      <button
                                                                        type="button"
                                                                        onClick={() => setAvailableStatusFilter('all')}
                                                                        className={`px-3 py-1 rounded-md ${
                                                                          availableStatusFilter === 'all'
                                                                            ? 'bg-gray-900 text-white shadow-sm'
                                                                            : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                      >
                                                                        All
                                                                      </button>
                                                                      <button
                                                                        type="button"
                                                                        onClick={() => setAvailableStatusFilter('fully-free')}
                                                                        className={`px-3 py-1 rounded-md ${
                                                                          availableStatusFilter === 'fully-free'
                                                                            ? 'bg-green-600 text-white shadow-sm'
                                                                            : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                      >
                                                                        Free
                                                                      </button>
                                                                      <button
                                                                        type="button"
                                                                        onClick={() => setAvailableStatusFilter('partially-free')}
                                                                        className={`px-3 py-1 rounded-md ${
                                                                          availableStatusFilter === 'partially-free'
                                                                            ? 'bg-amber-500 text-white shadow-sm'
                                                                            : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                      >
                                                                        Partially free
                                                                      </button>
                                                                      <button
                                                                        type="button"
                                                                        onClick={() => setAvailableStatusFilter('busy')}
                                                                        className={`px-3 py-1 rounded-md ${
                                                                          availableStatusFilter === 'busy'
                                                                            ? 'bg-red-600 text-white shadow-sm'
                                                                            : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                      >
                                                                        Busy
                                                                      </button>
                                                                    </div>
                                                                  </div>

                                                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                                                                    {/* Classrooms column */}
                                                                    <div className="space-y-3">
                                                                      <div className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                                                          <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                            className="w-4 h-4 text-indigo-600"
                                                                          >
                                                                            <path
                                                                              fillRule="evenodd"
                                                                              d="M4 3.75A1.75 1.75 0 0 1 5.75 2h8.5A1.75 1.75 0 0 1 16 3.75V5h.25A1.75 1.75 0 0 1 18 6.75v9.5a.75.75 0 0 1-1.164.62L14 15.31l-2.836 1.56a.75.75 0 0 1-.728 0L7.6 15.31l-2.236 1.56A.75.75 0 0 1 4 16.25v-9.5A1.75 1.75 0 0 1 5.75 5H6V3.75Z"
                                                                              clipRule="evenodd"
                                                                            />
                                                                          </svg>
                                                                          <span>Classrooms availability</span>
                                                                        </div>
                                                                        <span className="text-xs text-gray-500">
                                                                          {availableResults.classrooms.filter(({ classroom, category }) => {
                                                                            const statusOk =
                                                                              availableStatusFilter === 'all' ||
                                                                              category === availableStatusFilter;
                                                                            const q = availableClassroomQuery.toLowerCase().trim();
                                                                            if (!q) return statusOk;
                                                                            const name = (classroom.name || '').toLowerCase();
                                                                            const floor = (classroom.floor || '').toLowerCase();
                                                                            return statusOk && (name.includes(q) || floor.includes(q));
                                                                          }).length}{' '}
                                                                          classroom
                                                                          {availableResults.classrooms.filter(({ classroom, category }) => {
                                                                            const statusOk =
                                                                              availableStatusFilter === 'all' ||
                                                                              category === availableStatusFilter;
                                                                            const q = availableClassroomQuery.toLowerCase().trim();
                                                                            if (!q) return statusOk;
                                                                            const name = (classroom.name || '').toLowerCase();
                                                                            const floor = (classroom.floor || '').toLowerCase();
                                                                            return statusOk && (name.includes(q) || floor.includes(q));
                                                                          }).length === 1
                                                                            ? ''
                                                                            : 's'}
                                                                        </span>
                                                                      </div>
                                                                      <div className="mt-1 relative">
                                                                        <input
                                                                          type="text"
                                                                          value={availableClassroomQuery}
                                                                          onChange={(e) => setAvailableClassroomQuery(e.target.value)}
                                                                          placeholder="Search classrooms by name or floor..."
                                                                          className="block w-full rounded-lg border border-gray-200 bg-white py-1.5 px-2.5 text-xs text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                                                                        />
                                                                      </div>
                                                                      {availableResults.classrooms.filter(({ classroom, category }) => {
                                                                        const statusOk =
                                                                          availableStatusFilter === 'all' ||
                                                                          category === availableStatusFilter;
                                                                        const q = availableClassroomQuery.toLowerCase().trim();
                                                                        if (!q) return statusOk;
                                                                        const name = (classroom.name || '').toLowerCase();
                                                                        const floor = (classroom.floor || '').toLowerCase();
                                                                        return statusOk && (name.includes(q) || floor.includes(q));
                                                                      }).length === 0 ? (
                                                                        <div className="text-xs text-gray-500">
                                                                          No classrooms in this time range.
                                                                        </div>
                                                                      ) : (
                                                                        <div className="space-y-3">
                                                                          {availableResults.classrooms
                                                                            .filter(({ classroom, category }) => {
                                                                              const statusOk =
                                                                                availableStatusFilter === 'all' ||
                                                                                category === availableStatusFilter;
                                                                              const q = availableClassroomQuery.toLowerCase().trim();
                                                                              if (!q) return statusOk;
                                                                              const name = (classroom.name || '').toLowerCase();
                                                                              const floor = (classroom.floor || '').toLowerCase();
                                                                              return statusOk && (name.includes(q) || floor.includes(q));
                                                                            })
                                                                            .map(({ classroom, freeSlots, category }) => {
                                                                              const busySlots = [];
                                                                              const busyReasons = [];
                                                                              if (availableRange && Array.isArray(classroom.schedules)) {
                                                                                const qs = availableRange.startMin;
                                                                                const qe = availableRange.endMin;
                                                                                const parseMinutes = (t) => {
                                                                                  const parts = String(t || '').split(':');
                                                                                  if (parts.length < 2) return null;
                                                                                  const h = parseInt(parts[0], 10);
                                                                                  const m = parseInt(parts[1], 10);
                                                                                  if (Number.isNaN(h) || Number.isNaN(m)) return null;
                                                                                  return h * 60 + m;
                                                                                };
                                                                                classroom.schedules.forEach((schedule) => {
                                                                                  const sStart = parseMinutes(schedule.startTime);
                                                                                  const sEnd = parseMinutes(schedule.endTime);
                                                                                  if (sStart === null || sEnd === null) return;
                                                                                  const bs = Math.max(sStart, qs);
                                                                                  const be = Math.min(sEnd, qe);
                                                                                  if (be > bs) {
                                                                                    busySlots.push({ startMin: bs, endMin: be });
                                                                                    busyReasons.push({
                                                                                      subject: schedule.subjectName || 'Class',
                                                                                      batch: schedule.batchName,
                                                                                      lecturer: schedule.lecturer,
                                                                                      timeLabel: `${to12h(schedule.startTime)} - ${to12h(schedule.endTime)}`,
                                                                                    });
                                                                                  }
                                                                                });
                                                                              }

                                                                              const minutesToHHMM = (val) => {
                                                                                const h = Math.floor(val / 60);
                                                                                const m = val % 60;
                                                                                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                                                              };

                                                                              return (
                                                                                <div
                                                                                  key={classroom.classroomId}
                                                                                  className="rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3 text-sm flex flex-col gap-2"
                                                                                >
                                                                                  <div className="flex items-center justify-between gap-3">
                                                                                    <div>
                                                                                      <div className="font-semibold text-gray-900">{classroom.name}</div>
                                                                                      {classroom.floor && (
                                                                                        <div className="text-[11px] text-gray-500">{classroom.floor}</div>
                                                                                      )}
                                                                                    </div>
                                                                                    <div className="flex flex-col items-end gap-1">
                                                                                      {classroom.capacity && (
                                                                                        <div className="text-[11px] text-gray-500 flex items-center gap-1">
                                                                                          <svg
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                            viewBox="0 0 20 20"
                                                                                            fill="currentColor"
                                                                                            className="w-3.5 h-3.5 text-gray-400"
                                                                                          >
                                                                                            <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.25.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
                                                                                          </svg>
                                                                                          <span>Capacity {classroom.capacity}</span>
                                                                                        </div>
                                                                                      )}
                                                                                      <span
                                                                                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                                                                                          category === 'fully-free'
                                                                                            ? 'bg-green-100 text-green-800 border-green-200'
                                                                                            : category === 'partially-free'
                                                                                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                                                            : 'bg-red-50 text-red-700 border-red-200'
                                                                                        }`}
                                                                                      >
                                                                                        {category === 'fully-free'
                                                                                          ? 'Fully free'
                                                                                          : category === 'partially-free'
                                                                                          ? 'Partially free'
                                                                                          : 'Busy'}
                                                                                      </span>
                                                                                    </div>
                                                                                  </div>
                                                                                  {freeSlots.length > 0 ? (
                                                                                    <>
                                                                                      <div className="text-[11px] text-gray-600">
                                                                                        Free during:
                                                                                      </div>
                                                                                      <div className="flex flex-wrap gap-1.5 mt-0.5">
                                                                                        {freeSlots.map((slot, idx) => (
                                                                                          <span
                                                                                            key={idx}
                                                                                            className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 border border-green-200"
                                                                                          >
                                                                                            {to12h(slot.start)} - {to12h(slot.end)}
                                                                                          </span>
                                                                                        ))}
                                                                                      </div>
                                                                                    </>
                                                                                  ) : (
                                                                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                                                                      No free time in this range.
                                                                                    </div>
                                                                                  )}
                                                                                  {busySlots.length > 0 && (
                                                                                    <>
                                                                                      <div className="text-[11px] text-gray-600 mt-1">
                                                                                        Busy during:
                                                                                      </div>
                                                                                      <div className="flex flex-wrap gap-1.5 mt-0.5">
                                                                                        {busySlots.map((seg, idx) => (
                                                                                          <span
                                                                                            key={idx}
                                                                                            className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 border border-red-200"
                                                                                          >
                                                                                            {to12h(minutesToHHMM(seg.startMin))} - {to12h(minutesToHHMM(seg.endMin))}
                                                                                          </span>
                                                                                        ))}
                                                                                      </div>
                                                                                      {busyReasons.length > 0 && (
                                                                                        <div className="mt-1.5 space-y-0.5 text-[11px] text-gray-600">
                                                                                          {busyReasons.map((r, idx) => (
                                                                                            <div key={idx} className="flex flex-wrap items-center gap-1">
                                                                                              <span className="font-medium text-gray-800">{r.subject}</span>
                                                                                              {r.batch && <span className="text-gray-500">• {r.batch}</span>}
                                                                                              {r.lecturer && <span className="text-gray-500">• {r.lecturer}</span>}
                                                                                              <span className="text-gray-500">• {r.timeLabel}</span>
                                                                                            </div>
                                                                                          ))}
                                                                                        </div>
                                                                                      )}
                                                                                    </>
                                                                                  )}
                                                                                </div>
                                                                              );
                                                                            })}
                                                                        </div>
                                                                      )}
                                                                    </div>

                                                                    {/* Lecturers column */}
                                                                    <div className="space-y-3">
                                                                      <div className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                                                          <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                            className="w-4 h-4 text-indigo-600"
                                                                          >
                                                                            <path
                                                                              fillRule="evenodd"
                                                                              d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z"
                                                                              clipRule="evenodd"
                                                                            />
                                                                          </svg>
                                                                          <span>Lecturers availability</span>
                                                                        </div>
                                                                        <span className="text-xs text-gray-500">
                                                                          {availableResults.lecturers.filter(({ staff, category }) => {
                                                                            const statusOk =
                                                                              availableStatusFilter === 'all' ||
                                                                              category === availableStatusFilter;
                                                                            const q = availableLecturerQuery.toLowerCase().trim();
                                                                            if (!q) return statusOk;
                                                                            const name = (staff.name || '').toLowerCase();
                                                                            const position = (staff.positionName || '').toLowerCase();
                                                                            return statusOk && (name.includes(q) || position.includes(q));
                                                                          }).length}{' '}
                                                                          lecturer
                                                                          {availableResults.lecturers.filter(({ staff, category }) => {
                                                                            const statusOk =
                                                                              availableStatusFilter === 'all' ||
                                                                              category === availableStatusFilter;
                                                                            const q = availableLecturerQuery.toLowerCase().trim();
                                                                            if (!q) return statusOk;
                                                                            const name = (staff.name || '').toLowerCase();
                                                                            const position = (staff.positionName || '').toLowerCase();
                                                                            return statusOk && (name.includes(q) || position.includes(q));
                                                                          }).length === 1
                                                                            ? ''
                                                                            : 's'}
                                                                        </span>
                                                                      </div>
                                                                      <div className="mt-1 relative">
                                                                        <input
                                                                          type="text"
                                                                          value={availableLecturerQuery}
                                                                          onChange={(e) => setAvailableLecturerQuery(e.target.value)}
                                                                          placeholder="Search lecturers by name or position..."
                                                                          className="block w-full rounded-lg border border-gray-200 bg-white py-1.5 px-2.5 text-xs text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                                                                        />
                                                                      </div>
                                                                      {availableResults.lecturers.filter(({ staff, category }) => {
                                                                        const statusOk =
                                                                          availableStatusFilter === 'all' ||
                                                                          category === availableStatusFilter;
                                                                        const q = availableLecturerQuery.toLowerCase().trim();
                                                                        if (!q) return statusOk;
                                                                        const name = (staff.name || '').toLowerCase();
                                                                        const position = (staff.positionName || '').toLowerCase();
                                                                        return statusOk && (name.includes(q) || position.includes(q));
                                                                      }).length === 0 ? (
                                                                        <div className="text-xs text-gray-500">
                                                                          No lecturers in this time range.
                                                                        </div>
                                                                      ) : (
                                                                        <div className="space-y-3">
                                                                          {availableResults.lecturers
                                                                            .filter(({ staff, category }) => {
                                                                              const statusOk =
                                                                                availableStatusFilter === 'all' ||
                                                                                category === availableStatusFilter;
                                                                              const q = availableLecturerQuery.toLowerCase().trim();
                                                                              if (!q) return statusOk;
                                                                              const name = (staff.name || '').toLowerCase();
                                                                              const position = (staff.positionName || '').toLowerCase();
                                                                              return statusOk && (name.includes(q) || position.includes(q));
                                                                            })
                                                                            .map(({ staff, freeSlots, category }) => {
                                                                              const uniqueBatchCount = new Set(
                                                                                (staff.schedules || [])
                                                                                  .map((s) => (s.batchName || '').toString().trim())
                                                                                  .filter(Boolean)
                                                                              ).size;

                                                                              const busySlots = [];
                                                                              const busyReasons = [];
                                                                              if (availableRange && Array.isArray(staff.schedules)) {
                                                                                const qs = availableRange.startMin;
                                                                                const qe = availableRange.endMin;
                                                                                const parseMinutes = (t) => {
                                                                                  const parts = String(t || '').split(':');
                                                                                  if (parts.length < 2) return null;
                                                                                  const h = parseInt(parts[0], 10);
                                                                                  const m = parseInt(parts[1], 10);
                                                                                  if (Number.isNaN(h) || Number.isNaN(m)) return null;
                                                                                  return h * 60 + m;
                                                                                };
                                                                                (staff.schedules || []).forEach((schedule) => {
                                                                                  const sStart = parseMinutes(schedule.startTime);
                                                                                  const sEnd = parseMinutes(schedule.endTime);
                                                                                  if (sStart === null || sEnd === null) return;
                                                                                  const bs = Math.max(sStart, qs);
                                                                                  const be = Math.min(sEnd, qe);
                                                                                  if (be > bs) {
                                                                                    busySlots.push({ startMin: bs, endMin: be });
                                                                                    busyReasons.push({
                                                                                      subject: schedule.subjectName || 'Class',
                                                                                      batch: schedule.batchName,
                                                                                      timeLabel: `${to12h(schedule.startTime)} - ${to12h(schedule.endTime)}`,
                                                                                    });
                                                                                  }
                                                                                });
                                                                              }

                                                                              const minutesToHHMM = (val) => {
                                                                                const h = Math.floor(val / 60);
                                                                                const m = val % 60;
                                                                                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                                                              };

                                                                              return (
                                                                                <div
                                                                                  key={staff.staffId || staff.staffCode || staff.name}
                                                                                  className="rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3 text-sm flex flex-col gap-2"
                                                                                >
                                                                                  <div className="flex items-center justify-between gap-3">
                                                                                    <div className="flex-1 min-w-0">
                                                                                      <div className="flex items-center gap-2">
                                                                                        <h3 className="text-sm font-bold text-gray-900 truncate">
                                                                                          {staff.title ? `${staff.title}. ` : ''}
                                                                                          {staff.name}
                                                                                        </h3>
                                                                                        {staff.positionName && (
                                                                                          <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-[10px] font-semibold text-indigo-800">
                                                                                            {staff.positionName}
                                                                                          </span>
                                                                                        )}
                                                                                      </div>
                                                                                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
                                                                                        <span
                                                                                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                                                                                            category === 'fully-free'
                                                                                              ? 'bg-green-100 text-green-800 border-green-200'
                                                                                              : category === 'partially-free'
                                                                                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                                                              : 'bg-red-50 text-red-700 border-red-200'
                                                                                          }`}
                                                                                        >
                                                                                          {category === 'fully-free'
                                                                                            ? 'Fully free'
                                                                                            : category === 'partially-free'
                                                                                            ? 'Partially free'
                                                                                            : 'Busy'}
                                                                                        </span>
                                                                                        {staff.roster && (
                                                                                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5">
                                                                                            <span className="text-[10px] font-semibold text-gray-700">Roster:</span>
                                                                                            <span className="capitalize text-gray-800">{staff.roster.dayType}</span>
                                                                                          </span>
                                                                                        )}
                                                                                        {(staffUnavailabilityByStaffId.get(staff.staffId) || []).map((rec) => (
                                                                                          <span
                                                                                            key={rec.id}
                                                                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                                                                              rec.type === 'leave'
                                                                                                ? 'bg-orange-100 text-orange-800'
                                                                                                : 'bg-yellow-100 text-yellow-800'
                                                                                            }`}
                                                                                          >
                                                                                            {rec.type === 'leave'
                                                                                              ? '🏖️ On Leave'
                                                                                              : `⚠️ Unavailable${rec.startTime && rec.endTime ? ` ${to12h(rec.startTime)}–${to12h(rec.endTime)}` : ''}`}
                                                                                          </span>
                                                                                        ))}
                                                                                      </div>
                                                                                    </div>
                                                                                    <div className="flex-shrink-0 text-right">
                                                                                      <div className="inline-flex flex-col items-center justify-center bg-white rounded-lg px-3 py-1.5 shadow-sm border border-gray-200">
                                                                                        <div className="text-base font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                                                                          {uniqueBatchCount}
                                                                                        </div>
                                                                                        <div className="text-[10px] text-gray-600 font-medium">
                                                                                          {uniqueBatchCount === 1 ? 'Batch today' : 'Batches today'}
                                                                                        </div>
                                                                                      </div>
                                                                                    </div>
                                                                                  </div>
                                                                                  {freeSlots.length > 0 ? (
                                                                                    <>
                                                                                      <div className="text-[11px] text-gray-600">
                                                                                        Free during:
                                                                                      </div>
                                                                                      <div className="flex flex-wrap gap-1.5 mt-0.5">
                                                                                        {freeSlots.map((slot, idx) => (
                                                                                          <span
                                                                                            key={idx}
                                                                                            className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 border border-green-200"
                                                                                          >
                                                                                            {to12h(slot.start)} - {to12h(slot.end)}
                                                                                          </span>
                                                                                        ))}
                                                                                      </div>
                                                                                    </>
                                                                                  ) : (
                                                                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                                                                      No free time in this range.
                                                                                    </div>
                                                                                  )}
                                                                                  {busySlots.length > 0 && (
                                                                                    <>
                                                                                      <div className="text-[11px] text-gray-600 mt-1">
                                                                                        Busy during:
                                                                                      </div>
                                                                                      <div className="flex flex-wrap gap-1.5 mt-0.5">
                                                                                        {busySlots.map((seg, idx) => (
                                                                                          <span
                                                                                            key={idx}
                                                                                            className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 border border-red-200"
                                                                                          >
                                                                                            {to12h(minutesToHHMM(seg.startMin))} - {to12h(minutesToHHMM(seg.endMin))}
                                                                                          </span>
                                                                                        ))}
                                                                                      </div>
                                                                                      {busyReasons.length > 0 && (
                                                                                        <div className="mt-1.5 space-y-0.5 text-[11px] text-gray-600">
                                                                                          {busyReasons.map((r, idx) => (
                                                                                            <div key={idx} className="flex flex-wrap items-center gap-1">
                                                                                              <span className="font-medium text-gray-800">{r.subject}</span>
                                                                                              {r.batch && <span className="text-gray-500">• {r.batch}</span>}
                                                                                              <span className="text-gray-500">• {r.timeLabel}</span>
                                                                                            </div>
                                                                                          ))}
                                                                                        </div>
                                                                                      )}
                                                                                    </>
                                                                                  )}
                                                                                  {busySlots.length === 0 && category === 'busy' && (
                                                                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                                                                      Busy due to roster or lunch break in this time range.
                                                                                    </div>
                                                                                  )}
                                                                                  {staff.schedules && staff.schedules.length > 0 && (
                                                                                    <div className="mt-2 border-t border-gray-100 pt-2">
                                                                                      <div className="text-[11px] font-semibold text-gray-700 mb-1">Today&apos;s classes</div>
                                                                                      <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                                                                                        {staff.schedules.map((schedule, idx) => (
                                                                                          <div
                                                                                            key={idx}
                                                                                            className="flex items-center gap-2 text-[11px] bg-white rounded-lg px-2.5 py-1.5 border border-gray-200"
                                                                                          >
                                                                                            <span className="font-semibold text-indigo-700">
                                                                                              {to12h(schedule.startTime)} - {to12h(schedule.endTime)}
                                                                                            </span>
                                                                                            <span className="text-gray-400">•</span>
                                                                                            <span className="font-medium text-gray-900 truncate">
                                                                                              {schedule.subjectName}
                                                                                            </span>
                                                                                            {schedule.batchName && (
                                                                                              <>
                                                                                                <span className="text-gray-400">•</span>
                                                                                                <span className="text-gray-600 truncate">{schedule.batchName}</span>
                                                                                              </>
                                                                                            )}
                                                                                          </div>
                                                                                        ))}
                                                                                      </div>
                                                                                    </div>
                                                                                  )}
                                                                                </div>
                                                                              );
                                                                            })}
                                                                        </div>
                                                                      )}
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              )}
                                                          </div>
                                                        </div>
                                                      )}
                                                      {summaryTab === 'classrooms' && (
                                                        <div className="h-full flex flex-col">
                                                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4 space-y-3">
                                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                                                <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                                                              </svg>
                                                              <span><b className="font-semibold text-gray-900">{new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</b></span>
                                                            </div>
                                                            {/* Floor filter + Search bar */}
                                                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                                              {/* Floor filter (segmented) */}
                                                              <div className="flex-1">
                                                                <div className="inline-flex flex-wrap gap-2 items-center">
                                                                  <span className="text-xs text-gray-600 mr-1">Floor:</span>
                                                                  <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                                                                    {summaryFloorOptions.map((f) => (
                                                                      <button
                                                                        key={f}
                                                                        onClick={() => setSummaryFloorFilter(f)}
                                                                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                                                          summaryFloorFilter === f
                                                                            ? 'bg-indigo-600 text-white shadow-sm'
                                                                            : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                      >
                                                                        {f}
                                                                      </button>
                                                                    ))}
                                                                  </div>
                                                                </div>
                                                              </div>
                                                              {/* Search */}
                                                              <div className="relative md:w-1/2">
                                                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-400">
                                                                    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                                                                  </svg>
                                                                </div>
                                                                <input
                                                                  type="text"
                                                                  value={summarySearchQuery}
                                                                  onChange={(e) => setSummarySearchQuery(e.target.value)}
                                                                  placeholder="Search by classroom name, subject, or batch..."
                                                                  className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                                                                />
                                                                {summarySearchQuery && (
                                                                  <button
                                                                    onClick={() => setSummarySearchQuery('')}
                                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                                                    title="Clear search"
                                                                  >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                                      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                                                    </svg>
                                                                  </button>
                                                                )}
                                                              </div>
                                                            </div>
                                                          </div>
                                                          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 relative scroll-smooth" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                                                            {(() => {
                                                                  // Apply floor filter first
                                                                  const floorFiltered = derivedClassroomSchedules.filter((c) => {
                                                                    if (summaryFloorFilter === 'All') return true;
                                                                    const f = (c.floor && String(c.floor).trim()) || 'Unknown';
                                                                    // normalize compare (case-insensitive)
                                                                    return f.toLowerCase() === summaryFloorFilter.toLowerCase();
                                                                  });
                                                                  // Then filter by search
                                                                  const filteredClassrooms = floorFiltered.filter((classroom) => {
                                                                    const q = summarySearchQuery.toLowerCase().trim();
                                                                    if (!q) return true;
                                                                    const nameMatch = (classroom.name || '').toLowerCase().includes(q);
                                                                    const floorMatch = (classroom.floor || '').toLowerCase().includes(q);
                                                                    const scheduleMatch = Array.isArray(classroom.schedules) && classroom.schedules.some((s) =>
                                                                      (s.subjectName || '').toLowerCase().includes(q) ||
                                                                      (s.batchName || '').toLowerCase().includes(q) ||
                                                                      (s.lecturer || '').toLowerCase().includes(q)
                                                                    );
                                                                    return nameMatch || floorMatch || scheduleMatch;
                                                                  });

                                                                  const to12h = (time24) => {
                                                                    if (!time24) return '';
                                                                    const [h, m] = time24.split(':');
                                                                    let hours = parseInt(h, 10);
                                                                    const modifier = hours >= 12 ? 'PM' : 'AM';
                                                                    hours = hours % 12 || 12;
                                                                    return `${hours}:${m} ${modifier}`;
                                                                  };

                                                                  const timeToMinutes = (time24) => {
                                                                    const [h, m] = time24.split(':').map((n) => parseInt(n, 10));
                                                                    return (h || 0) * 60 + (m || 0);
                                                                  };

                                                                  // Time slots from 8:30 AM to 5:00 PM
                                                                  const startMinutes = 8 * 60 + 30; // 8:30 AM
                                                                  const endMinutes = 17 * 60; // 5:00 PM
                                                                  const totalMinutes = endMinutes - startMinutes;

                                                                  // Sort order helper: Ground Floor -> 0, numeric floors ascending, Unknown/empty last
                                                                  const floorRank = (val) => {
                                                                    const f = (val || '').toString().trim().toLowerCase();
                                                                    if (!f || f === 'unknown') return Number.POSITIVE_INFINITY;
                                                                    if (f === 'ground' || f === 'ground floor' || f === 'g') return 0;
                                                                    const m = f.match(/\d+/);
                                                                    if (m) return parseInt(m[0], 10);
                                                                    return Number.POSITIVE_INFINITY;
                                                                  };

                                                                  // Apply ordering when All floors selected
                                                                  const orderedClassrooms = summaryFloorFilter === 'All'
                                                                    ? [...filteredClassrooms].sort((a, b) => {
                                                                        const fa = floorRank(a.floor);
                                                                        const fb = floorRank(b.floor);
                                                                        if (fa !== fb) return fa - fb;
                                                                        return (a.name || '').localeCompare(b.name || '');
                                                                      })
                                                                    : filteredClassrooms;

                                                                  return (
                                                                    <>
                                                                      {orderedClassrooms.length === 0 && !classroomSchedulesLoading && (
                                                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                                                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-3">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-amber-500">
                                                                              <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clipRule="evenodd" />
                                                                            </svg>
                                                                          </div>
                                                                          <div className="text-sm font-medium text-gray-700">
                                                                            {summarySearchQuery 
                                                                              ? `No results found for "${summarySearchQuery}"` 
                                                                              : 'No classrooms found'}
                                                                          </div>
                                                                          {summarySearchQuery && (
                                                                            <button
                                                                              onClick={() => setSummarySearchQuery('')}
                                                                              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
                                                                            >
                                                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                                                              </svg>
                                                                              Clear search
                                                                            </button>
                                                                          )}
                                                                        </div>
                                                                      )}
                                                                      <div className="grid grid-cols-1 gap-4">
                                                                        {orderedClassrooms.map((classroom) => (
                                                                          <div key={classroom.classroomId} className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                                                                            {/* Classroom Header */}
                                                                            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-4 border-b border-gray-200">
                                                                              <div className="flex items-center gap-4">
                                                                                {/* Classroom Icon */}
                                                                                <div className="flex-shrink-0">
                                                                                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-md">
                                                                                    🏫
                                                                                  </div>
                                                                                </div>
                                                                                {/* Classroom Details */}
                                                                                <div className="flex-1 min-w-0">
                                                                                  <div className="flex items-center gap-2">
                                                                                    <h3 className="text-lg font-bold text-gray-900">{classroom.name}</h3>
                                                                                    {classroom.floor && (
                                                                                      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800">
                                                                                        {classroom.floor}
                                                                                      </span>
                                                                                    )}
                                                                                  </div>
                                                                                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600">
                                                                                    {classroom.capacity && (
                                                                                      <div className="flex items-center gap-1">
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                          <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
                                                                                        </svg>
                                                                                        Capacity: {classroom.capacity}
                                                                                      </div>
                                                                                    )}
                                                                                  </div>
                                                                                </div>
                                                                                {/* Schedule Count Badge */}
                                                                                <div className="flex-shrink-0">
                                                                                  <div className="inline-flex flex-col items-center justify-center bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
                                                                                    <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">{classroom.schedules.length}</div>
                                                                                    <div className="text-xs text-gray-600 font-medium">{classroom.schedules.length === 1 ? 'Batch' : 'Batches'}</div>
                                                                                  </div>
                                                                                </div>
                                                                              </div>
                                                                            </div>
                                                                            {/* Timeline Section */}
                                                                            <div className="px-5 py-4">
                                                                              <div className="space-y-4">
                                                                                {/* Timeline Header with Legend */}
                                                                                <div className="flex items-center justify-between text-xs border-b border-gray-200 pb-2">
                                                                                  <span className="text-gray-600 font-semibold">8:30 AM</span>
                                                                                  <div className="flex items-center gap-3">
                                                                                    <div className="flex items-center gap-1.5">
                                                                                      <div className="w-4 h-4 bg-gradient-to-r from-green-100 to-green-200 rounded border border-green-300"></div>
                                                                                      <span className="text-green-700 font-medium">Free</span>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-1.5">
                                                                                      <div className="w-4 h-4 bg-gradient-to-r from-red-500 to-red-600 rounded border border-white"></div>
                                                                                      <span className="text-red-700 font-medium">Busy</span>
                                                                                    </div>
                                                                                  </div>
                                                                                  <span className="text-gray-600 font-semibold">5:00 PM</span>
                                                                                </div>
                                                                                {/* Timeline Bar */}
                                                                                <div className="relative h-16 bg-gradient-to-r from-green-100 to-green-200 rounded-lg overflow-hidden">
                                                                                  {/* Time markers */}
                                                                                  <div className="absolute inset-0 flex">
                                                                                    {Array.from({ length: 9 }).map((_, i) => (
                                                                                      <div key={i} className="flex-1 border-r border-gray-300 last:border-r-0" />
                                                                                    ))}
                                                                                  </div>
                                                                                  {/* Free slots with time labels */}
                                                                                  {(() => {
                                                                                    // Build sorted list of schedules
                                                                                    const sorted = [...classroom.schedules].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
                                                                                    const freeSlots = [];
                                                                                    let current = startMinutes;
                                                                                    for (const s of sorted) {
                                                                                      const sStart = timeToMinutes(s.startTime);
                                                                                      const sEnd = timeToMinutes(s.endTime);
                                                                                      if (sStart > current) {
                                                                                        const fsStart = current;
                                                                                        const fsEnd = Math.min(sStart, endMinutes);
                                                                                        if (fsEnd > fsStart) {
                                                                                          freeSlots.push({
                                                                                            startMin: fsStart,
                                                                                            endMin: fsEnd,
                                                                                            start: `${String(Math.floor(fsStart / 60)).padStart(2, '0')}:${String(fsStart % 60).padStart(2, '0')}`,
                                                                                            end: `${String(Math.floor(fsEnd / 60)).padStart(2, '0')}:${String(fsEnd % 60).padStart(2, '0')}`,
                                                                                          });
                                                                                        }
                                                                                      }
                                                                                      current = Math.max(current, sEnd);
                                                                                    }
                                                                                    if (current < endMinutes) {
                                                                                      freeSlots.push({
                                                                                        startMin: current,
                                                                                        endMin: endMinutes,
                                                                                        start: `${String(Math.floor(current / 60)).padStart(2, '0')}:${String(current % 60).padStart(2, '0')}`,
                                                                                        end: `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`,
                                                                                      });
                                                                                    }
                                                                                    return freeSlots.map((slot, i) => {
                                                                                      const left = ((slot.startMin - startMinutes) / totalMinutes) * 100;
                                                                                      const width = ((slot.endMin - slot.startMin) / totalMinutes) * 100;
                                                                                      return (
                                                                                        <div key={`free-${i}`} className="absolute top-0 h-full flex items-center justify-center" style={{ left: `${left}%`, width: `${width}%` }}>
                                                                                          {width > 8 && (
                                                                                            <div className="text-green-700 text-[10px] font-bold px-1 text-center leading-tight">
                                                                                              <div>{to12h(slot.start)}</div>
                                                                                              <div className="text-[8px] opacity-75">Free</div>
                                                                                              <div>{to12h(slot.end)}</div>
                                                                                            </div>
                                                                                          )}
                                                                                        </div>
                                                                                      );
                                                                                    });
                                                                                  })()}
                                                                                  {/* Scheduled blocks with time labels */}
                                                                                  {classroom.schedules.map((schedule, idx) => {
                                                                                    const schedStart = timeToMinutes(schedule.startTime);
                                                                                    const schedEnd = timeToMinutes(schedule.endTime);
                                                                                    const left = ((schedStart - startMinutes) / totalMinutes) * 100;
                                                                                    const width = ((schedEnd - schedStart) / totalMinutes) * 100;
                                                                                    return (
                                                                                      <div
                                                                                        key={idx}
                                                                                        className="absolute top-0 h-full bg-gradient-to-r from-red-500 to-red-600 shadow-md border-2 border-white flex items-center justify-center z-20 group"
                                                                                        style={{ left: `${left}%`, width: `${width}%`, zIndex: 10 + idx }}
                                                                                        title={`${schedule.subjectName} - ${schedule.batchName} (${to12h(schedule.startTime)} - ${to12h(schedule.endTime)})`}
                                                                                      >
                                                                                        {width > 8 && (
                                                                                          <div className="text-white text-[10px] font-bold px-1 text-center leading-tight">
                                                                                            <div>{to12h(schedule.startTime)}</div>
                                                                                            <div className="text-[8px] opacity-75">-</div>
                                                                                            <div>{to12h(schedule.endTime)}</div>
                                                                                          </div>
                                                                                        )}
                                                                                      </div>
                                                                                    );
                                                                                  })}
                                                                                </div>
                                                                                {/* Schedule List */}
                                                                                {classroom.schedules.length > 0 && (
                                                                                  <div className="space-y-2">
                                                                                    <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                                                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                        <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                                                                                      </svg>
                                                                                      Scheduled Classes
                                                                                    </div>
                                                                                    <div className="space-y-1.5">
                                                                                      {classroom.schedules.map((schedule, idx) => (
                                                                                        <div key={idx} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                                                                                          <span className="font-semibold text-indigo-700">{to12h(schedule.startTime)} - {to12h(schedule.endTime)}</span>
                                                                                          <span className="text-gray-400">•</span>
                                                                                          <span className="font-medium text-gray-900">{schedule.subjectName}</span>
                                                                                          <span className="text-gray-400">•</span>
                                                                                          <span className="text-gray-600">{schedule.batchName}</span>
                                                                                          {schedule.lecturer && schedule.lecturer !== 'TBD' && (
                                                                                            <>
                                                                                              <span className="text-gray-400">•</span>
                                                                                              <span className="text-gray-600 italic">{schedule.lecturer}</span>
                                                                                            </>
                                                                                          )}
                                                                                        </div>
                                                                                      ))}
                                                                                    </div>
                                                                                  </div>
                                                                                )}
                                                                              </div>
                                                                            </div>
                                                                          </div>
                                                                        ))}
                                                                      </div>
                                                                    </>
                                                                  );
                                                                })()}
                                                          </div>
                                                        </div>
                                                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}




